import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Search, Star, ArrowRight, Zap, Shield, Truck, ChevronLeft, Facebook, Twitter, Instagram, Mail, Phone, MapPin, Award, Users, Quote, X, Loader,Globe, ExternalLink, FileText, Menu, LogOut, MessageSquare, Eye, CheckCircle, ShieldCheck, Briefcase } from 'lucide-react';

// ADD THIS LINE to tell ESLint that firebase is a global variable
/* global firebase */

// Typography constants for consistency
const TYPOGRAPHY = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  weights: {
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800'
  },
  sizes: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    xxl: '24px',
    xxxl: '28px',
    display: 'clamp(36px, 5vw, 48px)',
    hero: 'clamp(60px, 10vw, 120px)'
  },
  lineHeights: {
    tight: '1.2',
    normal: '1.5',
    relaxed: '1.6',
    loose: '1.8'
  },
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em'
  }
};

const TestimonialsSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Firebase database URL
  const FIREBASE_DATABASE_URL = "https://tescaadmin-default-rtdb.firebaseio.com";

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch testimonials from Firebase
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${FIREBASE_DATABASE_URL}/testimonials.json`);
        const data = await response.json();
        
        if (data) {
          const testimonialsArray = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          })).sort((a, b) => (a.order || 0) - (b.order || 0));
          setTestimonials(testimonialsArray);
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  // Auto-rotate slides (moves through pages of 3)
  const itemsPerPage = isMobile ? 1 : 3;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);

  useEffect(() => {
    if (totalPages > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalPages);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [totalPages]);

  // Get current page testimonials
  const getCurrentTestimonials = () => {
    const start = currentSlide * itemsPerPage;
    return testimonials.slice(start, start + itemsPerPage);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '24 Aug 2023';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  // Don't render section if no testimonials
  if (!loading && testimonials.length === 0) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <section style={{
        padding: isMobile ? '40px 0' : '80px 0',
        position: 'relative',
        overflow: 'hidden',
        minHeight: isMobile ? '50vh' : 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          color: 'rgba(255,255,255,0.5)'
        }}>
          Loading testimonials...
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Technical Consultation Hero Section */}
      <section style={{
        padding: isMobile ? '40px 0' : '80px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: isMobile ? '0 16px' : '0 40px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 30 : 60,
            alignItems: 'center'
          }}>
            {/* Left - Image */}
            <div style={{
              borderRadius: 12,
              overflow: 'hidden'
            }}>
              <img 
                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=600&h=400&fit=crop"
                alt="Technical Consultation"
                style={{
                  width: '100%',
                  height: isMobile ? '250px' : '350px',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </div>

            {/* Right - Content */}
            <div style={{
              textAlign: isMobile ? 'center' : 'left'
            }}>
              <span style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.18)',
                borderRadius: 6,
                fontSize: '13px',
                color: '#1a1a1a',
                marginBottom: 20,
                fontFamily: TYPOGRAPHY.fontFamily
              }}>
                Technical Consultation
              </span>
              <h2 style={{
                fontSize: isMobile ? '32px' : '48px',
                fontWeight: TYPOGRAPHY.weights.bold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#2d3748',
                lineHeight: 1.1,
                marginBottom: 20
              }}>
                Innovate. Automate. Elevate.
              </h2>
              <p style={{
                fontSize: isMobile ? '14px' : '16px',
                color: 'rgba(0,0,0,0.7)',
                fontFamily: TYPOGRAPHY.fontFamily,
                lineHeight: 1.6
              }}>
                Get a Free Software & Hardware Consultation Today!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section style={{
        padding: isMobile ? '40px 0' : '80px 0',
        position: 'relative',
        overflow: 'hidden',
        minHeight: isMobile ? '50vh' : 'auto'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: isMobile ? '0 16px' : '0 40px'
        }}>
          {/* Header - Left aligned like screenshot */}
          <div style={{
            textAlign: 'left',
            marginBottom: isMobile ? 30 : 50
          }}>
            <h2 style={{
              fontSize: isMobile ? '28px' : '42px',
              fontWeight: TYPOGRAPHY.weights.bold,
              fontFamily: TYPOGRAPHY.fontFamily,
              letterSpacing: TYPOGRAPHY.letterSpacing.tight,
              lineHeight: 1.2,
              marginBottom: 16,
              color: '#1a1a1a'
            }}>
              What Our Clients Say – Trusted by<br />
              Innovators
            </h2>
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: TYPOGRAPHY.weights.regular,
              fontFamily: TYPOGRAPHY.fontFamily,
              lineHeight: TYPOGRAPHY.lineHeights.relaxed,
              color: 'rgba(0,0,0,0.7)'
            }}>
              Real experiences, real impact. See how Dynavation's cutting-edge solutions have transformed businesses like yours!
            </p>
          </div>

          {/* Testimonials Grid - 3 cards like screenshot */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? 20 : 24
          }}>
            {getCurrentTestimonials().map((testimonial) => (
              <div
                key={testimonial.id}
                style={{
                  background: '#ffffff',
                  borderRadius: 16,
                  padding: isMobile ? '24px' : '28px',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '260px'
                }}
              >
                {/* Quote Icon - Top Right */}
                <Quote size={28} style={{
                  position: 'absolute',
                  top: 24,
                  right: 24,
                  color: '#1a1a1a',
                  opacity: 0.5
                }} />

                {/* Header with Logo/Image and Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  marginBottom: 18
                }}>
                  {/* Profile Image or Logo Placeholder */}
                  {testimonial.image ? (
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        objectFit: 'cover',
                        flexShrink: 0
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  {/* Fallback Logo Placeholder - Blue play button style */}
                  <div style={{
                    width: 48,
                    height: 48,
                    background: '#1a1a1a',
                    borderRadius: 8,
                    display: testimonial.image ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <div style={{
                      width: 0,
                      height: 0,
                      borderTop: '7px solid transparent',
                      borderBottom: '7px solid transparent',
                      borderLeft: '12px solid white',
                      marginLeft: 3
                    }} />
                  </div>
                  
                  <div>
                    <h4 style={{
                      fontSize: '15px',
                      fontWeight: TYPOGRAPHY.weights.semibold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      color: '#1a1a1a',
                      marginBottom: 2,
                      lineHeight: 1.3
                    }}>
                      {testimonial.name}
                    </h4>
                    <p style={{
                      color: 'rgba(0,0,0,0.6)',
                      fontSize: '12px',
                      fontWeight: TYPOGRAPHY.weights.regular,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      lineHeight: 1.4
                    }}>
                      {testimonial.role}{testimonial.company ? `, ${testimonial.company}` : ''}
                    </p>
                  </div>
                </div>

                {/* Testimonial Content */}
                <p style={{
                  fontSize: '14px',
                  fontWeight: TYPOGRAPHY.weights.regular,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  lineHeight: 1.6,
                  color: 'rgba(0, 0, 0, 0.7)',
                  marginBottom: 'auto',
                  flex: 1
                }}>
                  {testimonial.content || testimonial.text}
                </p>

                {/* Footer with Date and Stars */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 20
                }}>
                  <span style={{
                    color: 'rgba(0,0,0,0.5)',
                    fontSize: '13px',
                    fontFamily: TYPOGRAPHY.fontFamily
                  }}>
                    {formatDate(testimonial.date)}
                  </span>
                  <div style={{ display: 'flex', gap: 2 }}>
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={i} size={16} style={{ color: '#eab308', fill: '#eab308' }} />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Dots */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            marginTop: 40
          }}>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                style={{
                  width: currentSlide === index ? 24 : 10,
                  height: 10,
                  borderRadius: 5,
                  border: 'none',
                  background: currentSlide === index ? '#1a1a1a' : 'rgba(0, 0, 0, 0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  padding: 0
                }}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

const CategoriesCarousel = ({ categories, onSelect }) => {
  const containerRef = useRef(null);
  const cardRefs = useRef([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const inputBlockRef = useRef(false);
  const hoverTimerRef = useRef(null);
  const hoverRectRef = useRef(null);
  const rafRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const [touchOffset, setTouchOffset] = useState(0);
  const [isTouching, setIsTouching] = useState(false);

  const allCategories = categories && categories.length > 0 ? [...categories] : [];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const curr = cardRefs.current;
    if (curr.length !== allCategories.length) {
      const next = new Array(allCategories.length);
      for (let i = 0; i < next.length; i++) next[i] = curr[i] || null;
      cardRefs.current = next;
    }
  }, [allCategories.length]);

  const [currentIndex, setCurrentIndex] = useState(Math.floor(allCategories.length / 2));
  const autoScrollRef = useRef(null);

  const handleTouchStart = (e) => {
    if (!isMobile) return;
    touchStartX.current = e.touches[0].clientX;
    setIsTouching(true);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !touchStartX.current) return;
    touchEndX.current = e.touches[0].clientX;
    const diff = touchEndX.current - touchStartX.current;
    setTouchOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isMobile || !touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (Math.abs(distance) > threshold) {
      if (distance > 0) {
        navigateNext();
      } else {
        navigatePrev();
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
    setTouchOffset(0);
    setIsTouching(false);
  };

  useEffect(() => {
    const startAutoScroll = () => {
      autoScrollRef.current = setInterval(() => {
        if (!isAnimating && !inputBlockRef.current && hoveredCard === null) {
          setIsAnimating(true);
          setCurrentIndex(prev => (prev + 1) % allCategories.length);

          setTimeout(() => {
            setIsAnimating(false);
          }, 900);
        }
      }, isMobile ? 3500 : 4500);
    };

    startAutoScroll();

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [allCategories.length, hoveredCard, isMobile]);

  const getCardTransform = (index) => {
    const distance = index - currentIndex;
    const absDistance = Math.abs(distance);
    const isHovered = hoveredCard === index;

    const spacing = isMobile ? 300 : 380;
    const baseX = distance * spacing;

    if (absDistance === 0) {
      const baseScale = isMobile ? 1.0 : 1.16;
      const baseY = isMobile ? -10 : -18;
      const scale = isHovered && !isMobile ? 1.18 : baseScale;
      const y = baseY;

      return {
        transform: `translateX(${baseX}px) scale(${scale}) translateY(${y}px)`,
        filter: `brightness(${isHovered && !isMobile ? 1.25 : 1.2}) saturate(${isHovered && !isMobile ? 1.15 : 1.08}) blur(0px)`,
        zIndex: isHovered ? 30 : 20,
        opacity: 1,
        boxShadow: isHovered && !isMobile
          ? '0 40px 100px -25px rgba(0, 0, 0, 0.85), 0 0 0 3px rgba(0, 0, 0, 0.3), 0 0 60px rgba(0, 0, 0, 0.18)'
          : isMobile
            ? '0 25px 60px -15px rgba(0, 0, 0, 0.4), 0 0 0 2px rgba(255,255,255,0.2)'
            : '0 35px 80px -20px rgba(0, 0, 0, 0.5), 0 0 0 2px rgba(255,255,255,0.25), 0 0 40px rgba(0, 0, 0, 0.12)',
        transition: isMobile ? 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
      };
    } else if (absDistance === 1) {
      const tiltY = isMobile ? 0 : (distance > 0 ? 18 : -18);
      return {
        transform: `translateX(${baseX}px) scale(${isMobile ? 0.7 : 0.88}) translateY(${isMobile ? 10 : 15}px) rotateY(${tiltY}deg) rotateX(${isMobile ? 0 : 6}deg)`,
        filter: `brightness(0.75) saturate(0.85) blur(${isMobile ? 0.5 : 0.8}px)`,
        zIndex: 15,
        opacity: isMobile ? 0.4 : 0.65,
        boxShadow: '0 30px 60px -15px rgba(0,0,0,0.7)',
        transition: isMobile ? 'all 0.65s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'all 0.85s cubic-bezier(0.34, 1.56, 0.64, 1)'
      };
    } else {
      const tiltY = isMobile ? 0 : (distance > 0 ? 28 : -28);
      const scale = Math.max(isMobile ? 0.55 : 0.68, 0.88 - (absDistance - 1) * 0.12);
      const brightness = Math.max(0.55, 0.75 - (absDistance - 1) * 0.12);

      return {
        transform: `translateX(${baseX}px) scale(${scale}) translateY(${isMobile ? 15 : 25}px) rotateY(${tiltY}deg) rotateX(${isMobile ? 0 : 10}deg)`,
        filter: `brightness(${brightness}) saturate(0.75) blur(${isMobile ? 1 : 1.5}px)`,
        zIndex: Math.max(1, 15 - absDistance),
        opacity: Math.max(isMobile ? 0.2 : 0.35, 0.65 - (absDistance - 1) * 0.18),
        boxShadow: '0 25px 50px -15px rgba(0,0,0,0.5)',
        transition: isMobile ? 'all 0.65s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'all 0.85s cubic-bezier(0.34, 1.56, 0.64, 1)'
      };
    }
  };

  const getTiltTransform = (cardIndex) => {
    if (isMobile) return '';
    const isCenter = Math.abs(cardIndex - currentIndex) === 0;
    const isHovered = hoveredCard === cardIndex;

    if (isCenter && isHovered) {
      return 'rotateY(-8deg)';
    }
    return '';
  };

  const navigateNext = () => {
    if (inputBlockRef.current || isAnimating) return;

    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }

    setHoveredCard(null);
    inputBlockRef.current = true;
    setIsAnimating(true);

    setCurrentIndex(prev => (prev + 1) % allCategories.length);

    setTimeout(() => {
      setIsAnimating(false);
      inputBlockRef.current = false;

      autoScrollRef.current = setInterval(() => {
        if (!isAnimating && !inputBlockRef.current && hoveredCard === null) {
          setIsAnimating(true);
          setCurrentIndex(prev => (prev + 1) % allCategories.length);
          setTimeout(() => setIsAnimating(false), 900);
        }
      }, isMobile ? 3500 : 4500);
    }, 900);
  };

  const navigatePrev = () => {
    if (inputBlockRef.current || isAnimating) return;

    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }

    setHoveredCard(null);
    inputBlockRef.current = true;
    setIsAnimating(true);

    setCurrentIndex(prev => (prev - 1 + allCategories.length) % allCategories.length);

    setTimeout(() => {
      setIsAnimating(false);
      inputBlockRef.current = false;

      autoScrollRef.current = setInterval(() => {
        if (!isAnimating && !inputBlockRef.current && hoveredCard === null) {
          setIsAnimating(true);
          setCurrentIndex(prev => (prev + 1) % allCategories.length);
          setTimeout(() => setIsAnimating(false), 900);
        }
      }, isMobile ? 3500 : 4500);
    }, 900);
  };

  const handleMouseEnter = (index, event) => {
    if (isMobile) return;
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    if (Math.abs(index - currentIndex) === 0 && !isAnimating) {
      const el = cardRefs.current[index];
      if (el) {
        const r = el.getBoundingClientRect();
        hoverRectRef.current = { left: r.left, top: r.top, width: r.width, height: r.height };
      }
      setHoveredCard(index);
      setMousePos({ x: event.clientX, y: event.clientY });
    }
  };

  const handleMouseLeave = (index) => {
    if (isMobile) return;
    if (hoveredCard === index) {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      hoverRectRef.current = null;
      setHoveredCard(null);
    }
  };

  const handleMouseMove = (event) => {
    if (isMobile) return;
    if (hoveredCard !== null && Math.abs(hoveredCard - currentIndex) === 0) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      const { clientX, clientY } = event;
      rafRef.current = requestAnimationFrame(() => {
        setMousePos({ x: clientX, y: clientY });
      });
    }
  };

  const contentStyle = (index) => {
    const isCenter = Math.abs(index - currentIndex) === 0;

    if (!isCenter) return { opacity: 0, pointerEvents: 'none' };

    return {
      opacity: 1,
      pointerEvents: 'auto',
      transition: 'opacity 0.6s ease-out 0.3s'
    };
  };

  // Add early return check here - BEFORE the main return
  if (!categories || categories.length === 0) {
    return null;
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{
        textAlign: 'center',
        marginBottom: isMobile ? 30 : 50,
        maxWidth: 1200,
        marginInline: 'auto',
        paddingInline: isMobile ? 16 : 24
      }}>
        <h2 style={{
          fontSize: isMobile ? 'clamp(32px, 8vw, 44px)' : 'clamp(36px, 6vw, 56px)',
          fontWeight: TYPOGRAPHY.weights.bold,
          fontFamily: TYPOGRAPHY.fontFamily,
          letterSpacing: TYPOGRAPHY.letterSpacing.tight,
          lineHeight: TYPOGRAPHY.lineHeights.tight,
          marginBottom: isMobile ? 12 : 16,
          background: '#1a1a1a',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Explore Categories
        </h2>
        <p style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.xl,
          fontWeight: TYPOGRAPHY.weights.regular,
          fontFamily: TYPOGRAPHY.fontFamily,
          lineHeight: TYPOGRAPHY.lineHeights.relaxed,
          color: 'rgba(0,0,0,0.6)'
        }}>
          Discover our curated collection of premium products
        </p>
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: isMobile ? '520px' : '700px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          perspective: '2000px'
        }}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchMove={isMobile ? handleTouchMove : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        {!isMobile && (
          <>
            <button
              onClick={navigatePrev}
              disabled={inputBlockRef.current}
              style={{
                position: 'absolute',
                left: isMobile ? '3%' : '5%',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 25,
                width: isMobile ? 45 : 60,
                height: isMobile ? 45 : 60,
                borderRadius: '50%',
                border: '2px solid rgba(0, 0, 0, 0.18)',
                background: 'rgba(0,0,0,0.7)',
                color: '#1a1a1a',
                cursor: inputBlockRef.current ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s ease',
                opacity: inputBlockRef.current ? 0.5 : 1,
                pointerEvents: inputBlockRef.current ? 'none' : 'auto'
              }}
              onMouseEnter={(e) => {
                if (!inputBlockRef.current && !isMobile) {
                  e.target.style.background = 'rgba(0, 0, 0, 0.6)';
                  e.target.style.borderColor = 'rgba(0, 0, 0, 0.4)';
                  e.target.style.color = 'white';
                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.7)';
                e.target.style.borderColor = 'rgba(0, 0, 0, 0.18)';
                e.target.style.color = '#1a1a1a';
                e.target.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <ChevronLeft size={isMobile ? 22 : 28} />
            </button>

            <button
              onClick={navigateNext}
              disabled={inputBlockRef.current}
              style={{
                position: 'absolute',
                right: isMobile ? '3%' : '5%',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 25,
                width: isMobile ? 45 : 60,
                height: isMobile ? 45 : 60,
                borderRadius: '50%',
                border: '2px solid rgba(0, 0, 0, 0.18)',
                background: 'rgba(0,0,0,0.7)',
                color: '#1a1a1a',
                cursor: inputBlockRef.current ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s ease',
                opacity: inputBlockRef.current ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!inputBlockRef.current && !isMobile) {
                  e.target.style.background = 'rgba(0, 0, 0, 0.6)';
                  e.target.style.borderColor = 'rgba(0, 0, 0, 0.4)';
                  e.target.style.color = 'white';
                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.7)';
                e.target.style.borderColor = 'rgba(0, 0, 0, 0.18)';
                e.target.style.color = '#1a1a1a';
                e.target.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <ChevronRight size={isMobile ? 22 : 28} />
            </button>
          </>
        )}
        <div style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 50,
          background: isMobile
            ? 'radial-gradient(ellipse 700px 500px at 50% 50%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.9) 100%)'
            : 'radial-gradient(ellipse 1000px 700px at 50% 50%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.8) 100%)'
        }} />

        <div style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 5,
          background: isMobile
            ? 'radial-gradient(ellipse 900px 600px at 50% 60%, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.02) 50%, transparent 100%)'
            : 'radial-gradient(ellipse 1400px 900px at 50% 60%, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0.04) 50%, transparent 100%)'
        }} />

        <div
          ref={containerRef}
          style={{
            position: 'relative',
            width: isMobile ? '280px' : '400px',
            height: isMobile ? '420px' : '600px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transformStyle: 'preserve-3d'
          }}
        >
          {allCategories.length > 0 && allCategories.map((category, index) => {
            const cardTransform = getCardTransform(index);
            const tiltTransform = getTiltTransform(index);
            const isCenter = Math.abs(index - currentIndex) === 0;

            const combinedTransform = (tiltTransform && hoveredCard === index && isCenter)
              ? `${cardTransform.transform} ${tiltTransform}`
              : cardTransform.transform;

            return (
              <div
                key={category.id}
                ref={el => { cardRefs.current[index] = el; }}
                onClick={() => onSelect(category)}
                onMouseEnter={(e) => handleMouseEnter(index, e)}
                onMouseLeave={() => handleMouseLeave(index)}
                onMouseMove={handleMouseMove}
                style={{
                  position: 'absolute',
                  width: isMobile ? 260 : 350,
                  height: isMobile ? 380 : 500,
                  borderRadius: isMobile ? 18 : 24,
                  transformOrigin: '50% 50%',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  willChange: 'transform',
                  transformStyle: 'preserve-3d',
                  transform: combinedTransform,
                  filter: cardTransform.filter,
                  zIndex: cardTransform.zIndex,
                  opacity: cardTransform.opacity,
                  boxShadow: cardTransform.boxShadow,
                  transition: cardTransform.transition,
                }}
              >
                <div style={{ position: 'absolute', inset: 0 }}>
                  <img
                    src={category.image}
                    alt={category.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      pointerEvents: 'none',
                      transform: (hoveredCard === index && isCenter && !isMobile)
                        ? 'scale(1.05) translateY(-10px)'
                        : 'scale(1)',
                      transition: 'transform 0.5s ease-out'
                    }}
                  />

                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: isCenter
                      ? `linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.8) 100%)`
                      : 'rgba(0,0,0,0.3)',
                    pointerEvents: 'none',
                    transition: 'background 0.6s ease'
                  }} />

                  {hoveredCard === index && isCenter && !isMobile && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      border: '2px solid rgba(0, 0, 0, 0.25)',
                      borderRadius: 20,
                      boxShadow: 'inset 0 0 30px rgba(0, 0, 0, 0.12)',
                      pointerEvents: 'none',
                      animation: 'pulse 2s ease-in-out infinite'
                    }} />
                  )}

                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: isMobile ? '25px 20px' : '40px 30px',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
                    pointerEvents: 'auto',
                    cursor: 'inherit',
                    ...contentStyle(index),
                  }}>
                    <div style={{
                      fontSize: isMobile ? '10px' : TYPOGRAPHY.sizes.xs,
                      fontWeight: TYPOGRAPHY.weights.medium,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      color: 'rgba(255,255,255,0.8)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      marginBottom: isMobile ? 6 : 10,
                      transform: isAnimating ? 'translateY(15px)' : 'translateY(0)',
                      transition: 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.05s'
                    }}>
                      #{(category.name || 'category').replace(/\s+/g, '').toLowerCase()}
                    </div>

                    <h2 style={{
                      fontSize: isMobile ? 'clamp(22px, 6vw, 28px)' : TYPOGRAPHY.sizes.display,
                      fontWeight: TYPOGRAPHY.weights.extrabold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      letterSpacing: TYPOGRAPHY.letterSpacing.tight,
                      lineHeight: TYPOGRAPHY.lineHeights.tight,
                      color: '#ffffff',
                      marginBottom: isMobile ? 8 : 15,
                      textShadow: '0 4px 12px rgba(0,0,0,0.8)',
                      transform: isAnimating ? 'translateY(25px)' : 'translateY(0)',
                      transition: 'transform 0.65s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s'
                    }}>
                      {(category.name || 'CATEGORY').toUpperCase()}
                    </h2>

                    <p style={{
                      fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.base,
                      fontWeight: TYPOGRAPHY.weights.regular,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      lineHeight: TYPOGRAPHY.lineHeights.normal,
                      color: 'rgba(255,255,255,0.9)',
                      marginBottom: isMobile ? 15 : 25,
                      textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                      transform: isAnimating ? 'translateY(25px)' : 'translateY(0)',
                      transition: 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s'
                    }}>
                      {category.description}
                    </p>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transform: isAnimating ? 'translateY(25px)' : 'translateY(0)',
                      transition: 'transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s',
                      gap: isMobile ? '8px' : '0'
                    }}>
                      <div style={{
                        fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
                        fontWeight: TYPOGRAPHY.weights.medium,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        color: '#ffffff',
                        background: 'rgba(255,255,255,0.15)',
                        padding: isMobile ? '6px 10px' : '8px 16px',
                        borderRadius: 20,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)'
                      }}>
                        {category.products?.length || 0} Products
                      </div>

                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#ffffff',
                        background: 'rgba(0, 0, 0, 0.15)',
                        padding: isMobile ? '8px 14px' : '12px 20px',
                        borderRadius: 25,
                        backdropFilter: 'blur(10px)',
                        fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
                        fontWeight: TYPOGRAPHY.weights.semibold,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        border: '1px solid rgba(0, 0, 0, 0.18)',
                        transition: 'all 0.3s ease'
                      }}>
                        Explore
                        <ChevronRight size={isMobile ? 12 : 16} style={{ marginLeft: 4 }} />
                      </div>
                    </div>
                  </div>

                  {!isCenter && (
                    <div style={{
                      position: 'absolute',
                      top: isMobile ? 12 : 20,
                      right: isMobile ? 12 : 20,
                      width: isMobile ? 32 : 40,
                      height: isMobile ? 32 : 40,
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
                      fontWeight: TYPOGRAPHY.weights.semibold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      {index + 1}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          position: 'absolute',
          bottom: isMobile ? '3%' : '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: isMobile ? 8 : 12,
          zIndex: 60
        }}>
          {allCategories.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isAnimating && !inputBlockRef.current) {
                  if (autoScrollRef.current) {
                    clearInterval(autoScrollRef.current);
                  }
                  setIsAnimating(true);
                  setCurrentIndex(index);
                  setTimeout(() => setIsAnimating(false), 900);

                  autoScrollRef.current = setInterval(() => {
                    if (!isAnimating && !inputBlockRef.current && hoveredCard === null) {
                      setIsAnimating(true);
                      setCurrentIndex(prev => (prev + 1) % allCategories.length);
                      setTimeout(() => setIsAnimating(false), 900);
                    }
                  }, isMobile ? 3500 : 4500);
                }
              }}
              style={{
                width: index === currentIndex ? (isMobile ? 24 : 32) : (isMobile ? 8 : 10),
                height: isMobile ? 8 : 10,
                borderRadius: isMobile ? 4 : 5,
                border: 'none',
                background: index === currentIndex
                  ? 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)'
                  : 'rgba(0, 0, 0, 0.2)',
                cursor: 'pointer',
                transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                backdropFilter: 'blur(10px)',
                boxShadow: index === currentIndex
                  ? '0 4px 15px rgba(0, 0, 0, 0.25)'
                  : 'none'
              }}
              onMouseEnter={(e) => {
                if (index !== currentIndex && !isMobile) {
                  e.target.style.background = 'rgba(0, 0, 0, 0.3)';
                  e.target.style.transform = 'scale(1.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (index !== currentIndex) {
                  e.target.style.background = 'rgba(0, 0, 0, 0.2)';
                  e.target.style.transform = 'scale(1)';
                }
              }}
            />
          ))}
        </div>

        {!isMobile && (
          <div style={{
            position: 'absolute',
            bottom: '15%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '800px',
            height: '60px',
            background: 'radial-gradient(ellipse, rgba(0,0,0,0.3) 0%, transparent 70%)',
            borderRadius: '50%',
            zIndex: 1
          }} />
        )}
      </div>
    </div>
  );
};

const ProductsScrollView = ({ category, onProductClick }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const filteredProducts = category.products?.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.shortDescription.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  if (!category.products || category.products.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: isMobile ? '60px 20px' : '80px 20px',
        background: '#f8f9fa',
        borderRadius: '20px',
        marginBottom: isMobile ? '60px' : '80px'
      }}>
        <h3 style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.xl : TYPOGRAPHY.sizes.xxl,
          fontWeight: TYPOGRAPHY.weights.semibold,
          fontFamily: TYPOGRAPHY.fontFamily,
          color: 'rgba(0, 0, 0, 0.7)',
          marginBottom: '10px'
        }}>
          Coming Soon
        </h3>
        <p style={{
          fontSize: TYPOGRAPHY.sizes.base,
          fontWeight: TYPOGRAPHY.weights.regular,
          fontFamily: TYPOGRAPHY.fontFamily,
          color: 'rgba(0, 0, 0, 0.5)'
        }}>
          Products in this category will be available soon
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: isMobile ? '60px' : '80px' }}>
      <div style={{
        textAlign: 'center',
        marginBottom: isMobile ? '40px' : '60px'
      }}>
        <h2 style={{
          fontSize: isMobile ? 'clamp(28px, 7vw, 36px)' : TYPOGRAPHY.sizes.display,
          fontWeight: TYPOGRAPHY.weights.bold,
          fontFamily: TYPOGRAPHY.fontFamily,
          letterSpacing: TYPOGRAPHY.letterSpacing.tight,
          marginBottom: '15px',
          background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          {category.name}
        </h2>
        <p style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
          fontWeight: TYPOGRAPHY.weights.regular,
          fontFamily: TYPOGRAPHY.fontFamily,
          color: 'rgba(0, 0, 0, 0.7)',
          marginBottom: isMobile ? '20px' : '30px'
        }}>
          {category.products.length} premium products available
        </p>

        <div style={{
          maxWidth: isMobile ? '100%' : '600px',
          margin: '0 auto',
          position: 'relative',
          padding: isMobile ? '0 16px' : '0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: '#f9fafb',
            backdropFilter: 'blur(20px)',
            borderRadius: '50px',
            padding: isMobile ? '12px 20px' : '16px 28px',
            border: '2px solid rgba(0, 0, 0, 0.18)',
            transition: 'all 0.3s ease'
          }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.4)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.12)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.18)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
            <Search size={isMobile ? 18 : 22} style={{ color: '#1a1a1a', marginRight: isMobile ? '10px' : '15px' }} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                fontWeight: TYPOGRAPHY.weights.medium,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#2d3748'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  background: 'rgba(0, 0, 0, 0.12)',
                  border: 'none',
                  borderRadius: '50%',
                  width: isMobile ? '24px' : '28px',
                  height: isMobile ? '24px' : '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#1a1a1a',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.12)';
                }}
              >
                <X size={isMobile ? 14 : 16} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : window.innerWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gap: isMobile ? '20px' : '30px',
        padding: isMobile ? '0 16px' : '0 20px'
      }}>
        {filteredProducts.map((product) => (
          <div
            key={product.id}
            onClick={() => onProductClick(product)}
            style={{
              background: '#f8fafc',
              backdropFilter: 'blur(16px)',
              borderRadius: isMobile ? '16px' : '20px',
              overflow: 'hidden',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              transition: 'all 0.4s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(-10px) scale(1.03)';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.4)';
                e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.18)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{
              position: 'relative',
              height: isMobile ? '200px' : '280px',
              overflow: 'hidden'
            }}>
              <img
                src={product.image}
                alt={product.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease'
                }}
              />
              <div style={{
                position: 'absolute',
                top: isMobile ? 10 : 15,
                right: isMobile ? 10 : 15,
                background: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(10px)',
                padding: isMobile ? '6px 12px' : '8px 16px',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <Star size={isMobile ? 12 : 14} style={{ color: '#ffffff', fill: '#ffffff' }} />
                <span style={{
                  color: '#ffffff',
                  fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
                  fontWeight: TYPOGRAPHY.weights.semibold,
                  fontFamily: TYPOGRAPHY.fontFamily
                }}>
                  {product.rating}
                </span>
              </div>
            </div>

            <div style={{
              padding: isMobile ? '18px' : '25px'
            }}>
              <h3 style={{
                fontSize: isMobile ? TYPOGRAPHY.sizes.lg : TYPOGRAPHY.sizes.xl,
                fontWeight: TYPOGRAPHY.weights.bold,
                fontFamily: TYPOGRAPHY.fontFamily,
                letterSpacing: TYPOGRAPHY.letterSpacing.tight,
                marginBottom: isMobile ? '8px' : '12px',
                color: '#2d3748'
              }}>
                {product.name}
              </h3>

              <p style={{
                fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.regular,
                fontFamily: TYPOGRAPHY.fontFamily,
                lineHeight: TYPOGRAPHY.lineHeights.relaxed,
                color: 'rgba(0, 0, 0, 0.7)',
                marginBottom: isMobile ? '15px' : '20px'
              }}>
                {product.shortDescription}
              </p>

              <button style={{
                width: '100%',
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                padding: isMobile ? '12px 0' : '15px 0',
                borderRadius: isMobile ? '10px' : '12px',
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer',
                fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
                onClick={(e) => {
                  e.stopPropagation();
                  onProductClick(product);
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #111111 0%, #111111 100%)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.25)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                View Details
                <ArrowRight size={isMobile ? 14 : 16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && searchQuery && (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '40px 20px' : '60px 20px',
          background: '#f8f9fa',
          borderRadius: '20px',
          marginTop: isMobile ? '30px' : '40px'
        }}>
          <Search size={isMobile ? 36 : 48} style={{ color: 'rgba(0, 0, 0, 0.3)', marginBottom: isMobile ? '15px' : '20px' }} />
          <h3 style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.xl : TYPOGRAPHY.sizes.xxl,
            fontWeight: TYPOGRAPHY.weights.semibold,
            fontFamily: TYPOGRAPHY.fontFamily,
            color: 'rgba(0, 0, 0, 0.7)',
            marginBottom: '10px'
          }}>
            No products found
          </h3>
          <p style={{
            fontSize: TYPOGRAPHY.sizes.base,
            fontWeight: TYPOGRAPHY.weights.regular,
            fontFamily: TYPOGRAPHY.fontFamily,
            color: 'rgba(0, 0, 0, 0.5)',
            marginBottom: '20px'
          }}>
            Try adjusting your search terms
          </p>
          <button
            onClick={() => setSearchQuery('')}
            style={{
              padding: isMobile ? '10px 20px' : '12px 24px',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
              border: 'none',
              borderRadius: '25px',
              color: '#ffffff',
              fontSize: TYPOGRAPHY.sizes.base,
              fontWeight: TYPOGRAPHY.weights.semibold,
              fontFamily: TYPOGRAPHY.fontFamily,
              cursor: 'pointer',
              transition: 'transform 0.3s ease'
            }}
            onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Clear Search
          </button>
        </div>
      )}
    </div>
  );
};

const AboutPage = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState({});
  const observerRefs = useRef({});

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observers = {};

    Object.keys(observerRefs.current).forEach(key => {
      if (observerRefs.current[key]) {
        observers[key] = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setIsVisible(prev => ({ ...prev, [key]: true }));
            }
          },
          { threshold: 0.2 }
        );
        observers[key].observe(observerRefs.current[key]);
      }
    });

    return () => {
      Object.values(observers).forEach(observer => observer.disconnect());
    };
  }, []);

  const stats = [
    { number: '469K+', label: 'Social Followers', icon: Users },
    { number: '25K+', label: 'Happy Clients', icon: Award },
    { number: '756+', label: 'Projects Done', icon: MapPin },
    { number: '100+', label: 'Global Branch', icon: Globe },
    { number: '240+', label: 'Conference', icon: Star }
  ];

  const values = [
    {
      title: 'Decades of Expertise',
      desc: 'What truly distinguishes us is the extensive expertise inherited from our parent company, Acolag and Digital Devices. With over 20 years in the industry, this experience forms the foundation upon which we build and deliver high-quality products and solutions.',
      icon: Award,
      color: '#1a1a1a'
    },
    {
      title: 'Educational Excellence',
      desc: 'Education is at the heart of our mission. Leveraging our parent company\'s 20 years of educational expertise, we craft educational trainer kits that inspire and empower the engineers and technicians of tomorrow.',
      icon: Users,
      color: '#111111'
    },
    {
      title: 'Customization for Your Needs',
      desc: 'We understand that every project and every professional has unique requirements. That\'s why we offer extensive customization options. Whether you need specialized tools, a tailored workbench, or educational equipment, our solutions are designed to match your exact needs.',
      icon: Shield,
      color: '#111111'
    },
    {
      title: 'Technical Prowess',
      desc: 'Our team of experts brings together a wealth of knowledge in electronics and technology. With their combined experience and passion, we provide more than just products; we provide technical solutions to complex challenges.',
      icon: Zap,
      color: '#1a1a1a'
    },
    {
      title: 'Outstanding Customer Support',
      desc: 'Your satisfaction is paramount. Our dedicated customer support team is always ready to assist you with inquiries, technical issues, and product-related concerns. We\'re here to ensure your experience with our products is nothing short of exceptional.',
      icon: Users,
      color: '#1a1a1a'
    }
  ];

  const milestones = [
    { year: '2000s', title: 'The Foundation', desc: 'Parent company Acolag and Digital Devices established, building decades of expertise in electronics and educational solutions' },
    { year: '2020', title: 'New Vision', desc: 'Oakmond Engineers founded to deliver cutting-edge hardware and software solutions with a focus on innovation' },
    { year: '2021', title: 'Educational Impact', desc: 'Launched comprehensive IoT and educational lab setups, empowering institutions across the region' },
    { year: '2023', title: 'Industry Leadership', desc: 'Expanded to serve businesses and industries with advanced technological systems and solutions' },
    { year: 'Present', title: 'Innovation at Scale', desc: 'Delivering smart, scalable, and future-ready technologies to clients worldwide' },
    { year: 'Future', title: 'Shaping Tomorrow', desc: 'Continuing to drive innovation, efficiency, and growth through precision engineering and expertise' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: isMobile ? '100px' : '120px',
      paddingBottom: isMobile ? '80px' : '100px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated background */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '5%',
        width: isMobile ? '200px' : '400px',
        height: isMobile ? '200px' : '400px',
        background: 'radial-gradient(circle, rgba(0, 0, 0, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 10s ease-in-out infinite'
      }} />

      <div style={{
        position: 'absolute',
        bottom: '20%',
        left: '10%',
        width: isMobile ? '250px' : '450px',
        height: isMobile ? '250px' : '450px',
        background: 'radial-gradient(circle, rgba(2, 132, 199, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(70px)',
        animation: 'float 12s ease-in-out infinite reverse'
      }} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px', position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <div
          ref={el => observerRefs.current['hero'] = el}
          style={{
            textAlign: 'center',
            marginBottom: isMobile ? '60px' : '80px',
            opacity: isVisible['hero'] ? 1 : 0,
            transform: isVisible['hero'] ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        >
          <div style={{
            display: 'inline-block',
            background: 'rgba(0, 0, 0, 0.08)',
            padding: '10px 24px',
            borderRadius: '25px',
            border: '1px solid rgba(0, 0, 0, 0.18)',
            marginBottom: '25px'
          }}>
            <span style={{
              fontSize: TYPOGRAPHY.sizes.sm,
              fontWeight: TYPOGRAPHY.weights.semibold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#1a1a1a',
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}>
              Who We Are
            </span>
          </div>

          <h1 style={{
            fontSize: isMobile ? 'clamp(36px, 10vw, 60px)' : TYPOGRAPHY.sizes.hero,
            fontWeight: TYPOGRAPHY.weights.bold,
            fontFamily: TYPOGRAPHY.fontFamily,
            letterSpacing: TYPOGRAPHY.letterSpacing.tight,
            marginBottom: isMobile ? '25px' : '35px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #374151 50%, #6b7280 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center',
            lineHeight: TYPOGRAPHY.lineHeights.tight
          }}>
            Welcome to Oakmond Engineers<br />Where Innovation Meets Precision
          </h1>

          <p style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.lg : 'clamp(20px, 2vw, 24px)',
            lineHeight: TYPOGRAPHY.lineHeights.loose,
            color: 'rgba(0, 0, 0, 0.7)',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            At Oakmond Engineers Pvt. Ltd., we are dedicated to delivering cutting-edge hardware and software 
            solutions designed to drive innovation, efficiency, and growth. With expertise in IoT, educational 
            lab setups, and advanced technological systems, we empower businesses, institutions, and industries 
            to achieve their goals through smart, scalable, and future-ready technologies.
          </p>
        </div>

        {/* Stats Grid */}
        <div
          ref={el => observerRefs.current['stats'] = el}
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
            gap: isMobile ? '20px' : '25px',
            marginBottom: isMobile ? '70px' : '100px'
          }}
        >
          {stats.map((stat, idx) => (
            <div
              key={idx}
              style={{
                background: '#f9fafb',
                backdropFilter: 'blur(20px)',
                padding: isMobile ? '25px 20px' : '30px 20px',
                borderRadius: '20px',
                border: '2px solid rgba(0, 0, 0, 0.15)',
                textAlign: 'center',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                opacity: isVisible['stats'] ? 1 : 0,
                transform: isVisible['stats'] ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
                transitionDelay: `${idx * 0.1}s`
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.05)';
                  e.currentTarget.style.borderColor = '#1a1a1a';
                  e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.18)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.15)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <stat.icon size={isMobile ? 28 : 36} style={{ color: '#1a1a1a', marginBottom: '12px' }} />
              <div style={{
                fontSize: isMobile ? 'clamp(24px, 6vw, 32px)' : 'clamp(32px, 3.5vw, 42px)',
                fontWeight: TYPOGRAPHY.weights.extrabold,
                fontFamily: TYPOGRAPHY.fontFamily,
                background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '8px'
              }}>
                {stat.number}
              </div>
              <div style={{
                fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.medium,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: 'rgba(0, 0, 0, 0.8)'
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Mission Statement */}
        <div
          ref={el => observerRefs.current['mission'] = el}
          style={{
            background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
            backdropFilter: 'blur(30px)',
            borderRadius: isMobile ? '24px' : '32px',
            padding: isMobile ? '40px 25px' : '60px 50px',
            border: '2px solid rgba(0, 0, 0, 0.18)',
            marginBottom: isMobile ? '60px' : '80px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            opacity: isVisible['mission'] ? 1 : 0,
            transform: isVisible['mission'] ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        >
          <div style={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: '120px',
            height: '120px',
            background: 'radial-gradient(circle, rgba(0, 0, 0, 0.18) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(30px)'
          }} />

          <Quote size={isMobile ? 40 : 50} style={{ color: 'rgba(0, 0, 0, 0.18)', marginBottom: '20px' }} />

          <p style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.xl : 'clamp(22px, 2.5vw, 28px)',
            fontWeight: TYPOGRAPHY.weights.medium,
            fontFamily: TYPOGRAPHY.fontFamily,
            lineHeight: TYPOGRAPHY.lineHeights.loose,
            color: 'rgba(0, 0, 0, 0.8)',
            fontStyle: 'italic',
            maxWidth: '900px',
            margin: '0 auto 30px'
          }}>
            "At Oakmond Engineers, we don't just deliver products—we deliver solutions that transform challenges 
            into opportunities. Built on a foundation of over 20 years of industry expertise from our parent company, 
            we combine precision engineering with cutting-edge innovation to create technologies that empower our 
            clients to lead in their respective fields."
          </p>

          <div style={{
            width: '60px',
            height: '4px',
            background: 'linear-gradient(90deg, #1a1a1a, #111111)',
            margin: '0 auto',
            borderRadius: '2px'
          }} />
        </div>

        {/* Values Grid - "What Sets Us Apart" */}
        <div
          ref={el => observerRefs.current['values'] = el}
          style={{ marginBottom: isMobile ? '60px' : '80px' }}
        >
          <h2 style={{
            fontSize: isMobile ? 'clamp(28px, 7vw, 40px)' : TYPOGRAPHY.sizes.display,
            fontWeight: TYPOGRAPHY.weights.bold,
            fontFamily: TYPOGRAPHY.fontFamily,
            textAlign: 'center',
            marginBottom: isMobile ? '12px' : '16px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #374151 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            opacity: isVisible['values'] ? 1 : 0,
            transform: isVisible['values'] ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease-out'
          }}>
            What Sets Us Apart
          </h2>
          <p style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
            textAlign: 'center',
            color: 'rgba(0, 0, 0, 0.7)',
            marginBottom: isMobile ? '40px' : '60px',
            fontFamily: TYPOGRAPHY.fontFamily,
            opacity: isVisible['values'] ? 1 : 0,
            transform: isVisible['values'] ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease-out 0.2s'
          }}>
            At Oakmond Engineers, our dedication to excellence, innovation, and commitment to our customers sets us apart in the world of electronics and technology.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '25px' : '35px'
          }}>
            {values.map((value, idx) => (
              <div
                key={idx}
                style={{
                  background: '#f9fafb',
                  backdropFilter: 'blur(20px)',
                  padding: isMobile ? '30px' : '40px',
                  borderRadius: '24px',
                  border: '2px solid rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: isVisible['values'] ? 1 : 0,
                  transform: isVisible['values'] ? 'translateX(0) scale(1)' : `translateX(${idx % 2 === 0 ? '-30px' : '30px'}) scale(0.95)`,
                  transitionDelay: `${0.4 + idx * 0.15}s`
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-10px)';
                    e.currentTarget.style.borderColor = value.color;
                    e.currentTarget.style.boxShadow = `0 25px 50px ${value.color}30`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: '100px',
                  height: '100px',
                  background: `radial-gradient(circle, ${value.color}20 0%, transparent 70%)`,
                  borderRadius: '50%',
                  filter: 'blur(25px)'
                }} />

                <div style={{
                  width: isMobile ? '60px' : '70px',
                  height: isMobile ? '60px' : '70px',
                  background: `${value.color}20`,
                  borderRadius: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <value.icon size={isMobile ? 30 : 35} style={{ color: value.color }} />
                </div>

                <h3 style={{
                  fontSize: isMobile ? TYPOGRAPHY.sizes.xl : TYPOGRAPHY.sizes.xxl,
                  fontWeight: TYPOGRAPHY.weights.bold,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  color: '#2d3748',
                  marginBottom: '15px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {value.title}
                </h3>

                <p style={{
                  fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                  fontWeight: TYPOGRAPHY.weights.regular,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  lineHeight: TYPOGRAPHY.lineHeights.relaxed,
                  color: 'rgba(0, 0, 0, 0.8)',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Journey Timeline */}
        <div
          ref={el => observerRefs.current['journey'] = el}
          style={{
            background: '#f9fafb',
            backdropFilter: 'blur(20px)',
            borderRadius: isMobile ? '24px' : '32px',
            padding: isMobile ? '40px 25px' : '60px 50px',
            border: '2px solid rgba(0, 0, 0, 0.15)',
            opacity: isVisible['journey'] ? 1 : 0,
            transform: isVisible['journey'] ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        >
          <h2 style={{
            fontSize: isMobile ? 'clamp(28px, 7vw, 40px)' : TYPOGRAPHY.sizes.display,
            fontWeight: TYPOGRAPHY.weights.bold,
            fontFamily: TYPOGRAPHY.fontFamily,
            textAlign: 'center',
            marginBottom: isMobile ? '40px' : '60px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #374151 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Our Journey
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            position: 'relative',
            paddingLeft: isMobile ? '30px' : '40px'
          }}>
            {/* Animated Timeline line */}
            <div style={{
              position: 'absolute',
              left: isMobile ? '11px' : '15px',
              top: '20px',
              bottom: '20px',
              width: '3px',
              background: 'rgba(0, 0, 0, 0.12)',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: isVisible['journey'] ? '100%' : '0%',
                background: 'linear-gradient(180deg, #1a1a1a, #111111)',
                transition: 'height 2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transitionDelay: '0.3s'
              }} />
            </div>

            {milestones.map((milestone, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: isMobile ? '15px' : '25px',
                  position: 'relative',
                  marginBottom: idx === milestones.length - 1 ? 0 : (isMobile ? '40px' : '50px'),
                  opacity: isVisible['journey'] ? 1 : 0,
                  transform: isVisible['journey'] ? 'translateX(0)' : 'translateX(-30px)',
                  transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transitionDelay: `${0.5 + idx * 0.2}s`
                }}
              >
                {/* Animated dot with pulse effect */}
                <div style={{
                  position: 'absolute',
                  left: isMobile ? '-24px' : '-32px',
                  top: '5px',
                  width: isMobile ? '20px' : '24px',
                  height: isMobile ? '20px' : '24px',
                  background: 'linear-gradient(135deg, #1a1a1a, #111111)',
                  borderRadius: '50%',
                  border: '3px solid #0a0a0a',
                  boxShadow: isVisible['journey']
                    ? '0 0 0 4px rgba(0, 0, 0, 0.12), 0 0 20px rgba(0, 0, 0, 0.25)'
                    : '0 0 0 4px rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.6s ease-out',
                  transitionDelay: `${0.5 + idx * 0.2}s`,
                  animation: isVisible['journey'] ? `pulse 2s ease-in-out ${idx * 0.3}s infinite` : 'none'
                }} />

                <div style={{ flex: 1 }}>
                  {/* Year Badge */}
                  <div style={{
                    display: 'inline-block',
                    background: 'rgba(0, 0, 0, 0.12)',
                    padding: '6px 16px',
                    borderRadius: '15px',
                    marginBottom: '12px',
                    transform: isVisible['journey'] ? 'scale(1)' : 'scale(0.8)',
                    transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transitionDelay: `${0.6 + idx * 0.2}s`
                  }}>
                    <span style={{
                      fontSize: TYPOGRAPHY.sizes.sm,
                      fontWeight: TYPOGRAPHY.weights.bold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      color: '#1a1a1a'
                    }}>
                      {milestone.year}
                    </span>
                  </div>

                  {/* Milestone Title */}
                  <h4 style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.lg : TYPOGRAPHY.sizes.xl,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    color: '#2d3748',
                    marginBottom: '10px',
                    transform: isVisible['journey'] ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'transform 0.5s ease-out',
                    transitionDelay: `${0.7 + idx * 0.2}s`
                  }}>
                    {milestone.title}
                  </h4>

                  {/* Milestone Description */}
                  <p style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                    fontWeight: TYPOGRAPHY.weights.regular,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    lineHeight: TYPOGRAPHY.lineHeights.relaxed,
                    color: 'rgba(0, 0, 0, 0.7)',
                    transform: isVisible['journey'] ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'transform 0.5s ease-out',
                    transitionDelay: `${0.8 + idx * 0.2}s`
                  }}>
                    {milestone.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Closing Statement */}
        <div style={{
          textAlign: 'center',
          marginTop: isMobile ? '60px' : '80px',
          padding: isMobile ? '40px 25px' : '50px',
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0.03) 100%)',
          borderRadius: isMobile ? '24px' : '32px',
          border: '1px solid rgba(0, 0, 0, 0.12)'
        }}>
          <p style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.xl : 'clamp(22px, 2.5vw, 28px)',
            fontWeight: TYPOGRAPHY.weights.medium,
            fontFamily: TYPOGRAPHY.fontFamily,
            lineHeight: TYPOGRAPHY.lineHeights.loose,
            color: 'rgba(0, 0, 0, 0.9)',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            Driven by precision engineering and guided by innovation, Oakmond Engineers continues to create 
            opportunities for technological advancement, empowering businesses and institutions to achieve 
            excellence through smart, scalable, and future-ready solutions.
          </p>
        </div>
      </div>
    </div>
  );
};



// Add this at the very top of your file, before the TescaLandingPage component
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyB95RDaMwgZOvP5T1dbtHRTZUs9HL6cK5I",
  authDomain: "tescaadmin.firebaseapp.com",
  databaseURL: "https://tescaadmin-default-rtdb.firebaseio.com",
  projectId: "tescaadmin",
  storageBucket: "tescaadmin.firebasestorage.app",
  messagingSenderId: "929456494025",
  appId: "1:929456494025:web:fed963db245393ce094418"
};

// ADD THIS INITIALIZATION CODE HERE
let firebaseApp;
let auth;
let database;

try {
  firebaseApp = firebase.initializeApp(FIREBASE_CONFIG);
  auth = firebase.auth();
  database = firebase.database();
} catch (error) {
  // Firebase already initialized
  firebaseApp = firebase.app();
  auth = firebase.auth();
  database = firebase.database();
}



// Enhanced Authentication Modal Component with Google Login
const AuthModal = ({ onClose, onAuthSuccess }) => {
  const [authMode, setAuthMode] = useState('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setAuthError('');

    try {
      // Create Google Auth Provider
      const provider = new firebase.auth.GoogleAuthProvider();

      // Add scopes if needed
      provider.addScope('profile');
      provider.addScope('email');

      // Sign in with popup
      const result = await auth.signInWithPopup(provider);

      // Get user info
      const user = result.user;
      const userName = user.displayName || user.email.split('@')[0];

      // Store user data in database
      await database.ref(`users/${user.uid}`).set({
        name: userName,
        email: user.email,
        photoUrl: user.photoURL || '',
        provider: 'google',
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      });

      // Get ID token
      const idToken = await user.getIdToken();

      const userObj = {
        email: user.email,
        uid: user.uid,
        token: idToken,
        name: userName,
        photoUrl: user.photoURL || ''
      };

      localStorage.setItem('userToken', idToken);
      localStorage.setItem('userId', user.uid);
      localStorage.setItem('userName', userName);

      onAuthSuccess(userObj);

    } catch (err) {
      console.error('Google auth error:', err);

      // Handle specific error cases
      if (err.code === 'auth/popup-closed-by-user') {
        setAuthError('Sign-in cancelled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setAuthError('Pop-up blocked. Please allow pop-ups for this site.');
      } else if (err.code === 'auth/cancelled-popup-request') {
        setAuthError('Sign-in cancelled. Please try again.');
      } else if (err.code === 'auth/unauthorized-domain') {
        setAuthError('This domain is not authorized. Please contact support.');
      } else {
        setAuthError('Google Sign-In failed. Please try again or use email login.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');

    // Validation
    if (authMode === 'signup') {
      if (authForm.password !== authForm.confirmPassword) {
        setAuthError('Passwords do not match');
        return;
      }
      if (authForm.password.length < 6) {
        setAuthError('Password must be at least 6 characters');
        return;
      }
      if (!authForm.name.trim()) {
        setAuthError('Please enter your name');
        return;
      }
    }

    setAuthLoading(true);

    try {
      let user;

      if (authMode === 'signup') {
        // Create new user
        const userCredential = await auth.createUserWithEmailAndPassword(
          authForm.email,
          authForm.password
        );
        user = userCredential.user;

        // Update profile with name
        await user.updateProfile({
          displayName: authForm.name.trim()
        });

        // Store user data in database
        await database.ref(`users/${user.uid}`).set({
          name: authForm.name.trim(),
          email: authForm.email,
          provider: 'email',
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString()
        });

      } else {
        // Sign in existing user
        const userCredential = await auth.signInWithEmailAndPassword(
          authForm.email,
          authForm.password
        );
        user = userCredential.user;

        // Update last login
        await database.ref(`users/${user.uid}/lastLogin`).set(new Date().toISOString());
      }

      // Get user name from database or profile
      let userName = authForm.name.trim();
      if (authMode === 'login') {
        const snapshot = await database.ref(`users/${user.uid}`).once('value');
        const userData = snapshot.val();
        userName = userData?.name || user.displayName || user.email.split('@')[0];
      }

      // Get ID token
      const idToken = await user.getIdToken();

      const userObj = {
        email: user.email,
        uid: user.uid,
        token: idToken,
        name: userName
      };

      localStorage.setItem('userToken', idToken);
      localStorage.setItem('userId', user.uid);
      localStorage.setItem('userName', userName);

      onAuthSuccess(userObj);

    } catch (err) {
      console.error('Auth error:', err);

      let errorMessage = 'Authentication failed';

      if (err.code) {
        switch (err.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'Email already registered. Please login instead.';
            break;
          case 'auth/user-not-found':
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            errorMessage = 'Invalid email or password';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password should be at least 6 characters';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many attempts. Please try again later';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your connection.';
            break;
          default:
            errorMessage = err.message || 'An error occurred. Please try again.';
        }
      }

      setAuthError(errorMessage);
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(255, 255, 255, 0.92)',
      backdropFilter: 'blur(12px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      padding: isMobile ? '16px' : '20px',
      animation: 'fadeIn 0.4s ease'
    }}
      onClick={onClose}>
      <div
        className="auth-modal"
        style={{
          background: 'linear-gradient(135deg, rgba(15, 15, 15, 0.98) 0%, rgba(5, 5, 5, 0.98) 100%)',
          border: '2px solid rgba(0, 0, 0, 0.18)',
          borderRadius: isMobile ? '24px' : '32px',
          padding: isMobile ? '35px 25px' : '50px 45px',
          maxWidth: '520px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.9), 0 0 100px rgba(0, 0, 0, 0.08)',
          animation: 'slideUpFade 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          msOverflowStyle: 'none',  /* Hide scrollbar for IE and Edge */
          scrollbarWidth: 'none'     /* Hide scrollbar for Firefox */
        }}
        onClick={(e) => e.stopPropagation()}>
        {/* Animated Background Elements */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(0, 0, 0, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)',
          animation: 'float 6s ease-in-out infinite',
          pointerEvents: 'none'
        }} />

        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(0, 0, 0, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)',
          animation: 'float 8s ease-in-out infinite reverse',
          pointerEvents: 'none'
        }} />

        {/* Close Button */}
        <button
          onClick={onClose}
          type="button"
          style={{
            position: 'absolute',
            top: isMobile ? '15px' : '20px',
            right: isMobile ? '15px' : '20px',
            padding: '10px',
            background: '#f8f9fa',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            color: '#2d3748',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
            e.currentTarget.style.borderColor = '#ef4444';
            e.currentTarget.style.transform = 'rotate(90deg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f8f9fa';
            e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.transform = 'rotate(0deg)';
          }}
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: isMobile ? '35px' : '45px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            width: isMobile ? '70px' : '85px',
            height: isMobile ? '70px' : '85px',
            margin: '0 auto 25px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeInUp 0.6s ease-out, pulse 3s ease-in-out infinite',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.25), inset 0 0 20px rgba(255, 255, 255, 0.1)'
          }}>
            <Users size={isMobile ? 35 : 42} color="white" />
          </div>

          <h2 style={{
            fontSize: isMobile ? 'clamp(26px, 6vw, 32px)' : 'clamp(32px, 4vw, 40px)',
            fontWeight: TYPOGRAPHY.weights.extrabold,
            fontFamily: TYPOGRAPHY.fontFamily,
            background: 'linear-gradient(135deg, #ffffff 0%, #1a1a1a 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '12px',
            letterSpacing: '-0.02em'
          }}>
            {authMode === 'login' ? 'Welcome Back!' : 'Join Dynavation'}
          </h2>

          <p style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
            color: 'rgba(255, 255, 255, 0.7)',
            fontFamily: TYPOGRAPHY.fontFamily,
            lineHeight: '1.6'
          }}>
            {authMode === 'login'
              ? 'Sign in to unlock exclusive features and personalized experiences'
              : 'Create your account and start exploring premium products'}
          </p>
        </div>

        {/* Error Message */}
        {authError && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            borderRadius: '14px',
            padding: isMobile ? '14px 16px' : '16px 20px',
            marginBottom: isMobile ? '25px' : '30px',
            color: '#ef4444',
            fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.sm,
            fontFamily: TYPOGRAPHY.fontFamily,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'shake 0.5s ease'
          }}>
            <span style={{ fontSize: '20px' }}>⚠</span>
            <span>{authError}</span>
          </div>
        )}

        {/* Google Sign In Button */}
        <button
          onClick={handleGoogleLogin}
          disabled={googleLoading || authLoading}
          type="button"
          style={{
            width: '100%',
            padding: isMobile ? '14px' : '16px',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '2px solid rgba(0, 0, 0, 0.15)',
            borderRadius: '14px',
            color: '#1a1a1a',
            fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
            fontWeight: TYPOGRAPHY.weights.semibold,
            fontFamily: TYPOGRAPHY.fontFamily,
            cursor: googleLoading || authLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: isMobile ? '25px' : '30px',
            opacity: googleLoading || authLoading ? 0.6 : 1,
            boxShadow: '0 4px 20px rgba(255, 255, 255, 0.1)'
          }}
          onMouseEnter={(e) => {
            if (!googleLoading && !authLoading && !isMobile) {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
          }}
        >
          {googleLoading ? (
            <>
              <Loader size={22} style={{ animation: 'spin 1s linear infinite' }} />
              Connecting to Google...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </>
          )}
        </button>

        {/* Divider */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          margin: isMobile ? '25px 0' : '30px 0'
        }}>
          <div style={{
            flex: 1,
            height: '1px',
            background: 'linear-gradient(to right, transparent, rgba(0, 0, 0, 0.18), transparent)'
          }} />
          <span style={{
            padding: '0 15px',
            fontSize: TYPOGRAPHY.sizes.sm,
            color: 'rgba(255, 255, 255, 0.5)',
            fontFamily: TYPOGRAPHY.fontFamily,
            fontWeight: TYPOGRAPHY.weights.medium
          }}>OR</span>
          <div style={{
            flex: 1,
            height: '1px',
            background: 'linear-gradient(to right, rgba(0, 0, 0, 0.18), transparent)'
          }} />
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleAuth}>
          {authMode === 'signup' && (
            <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                marginBottom: '10px',
                transition: 'all 0.3s ease'
              }}>Full Name *</label>
              <input
                type="text"
                value={authForm.name}
                onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                onFocus={() => setFocusedField('name')}
                onBlur={() => setFocusedField(null)}
                placeholder="John Doe"
                required={authMode === 'signup'}
                disabled={authLoading || googleLoading}
                style={{
                  width: isMobile ? '92%' : '95%',
                  padding: isMobile ? '14px 16px' : '16px 18px',
                  background: focusedField === 'name'
                    ? 'rgba(0, 0, 0, 0.06)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: `2px solid ${focusedField === 'name' ? '#1a1a1a' : 'rgba(0, 0, 0, 0.12)'}`,
                  borderRadius: '14px',
                  color: '#2d3748',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  outline: 'none',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transform: focusedField === 'name' ? 'translateY(-2px)' : 'translateY(0)',
                  boxShadow: focusedField === 'name'
                    ? '0 8px 25px rgba(0, 0, 0, 0.1)'
                    : 'none'
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.sizes.sm,
              fontWeight: TYPOGRAPHY.weights.semibold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#1a1a1a',
              marginBottom: '10px'
            }}>Email Address *</label>
            <input
              type="email"
              value={authForm.email}
              onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              placeholder="john@example.com"
              required
              disabled={authLoading || googleLoading}
              style={{
                width: isMobile ? '92%' : '95%',
                padding: isMobile ? '14px 16px' : '16px 18px',
                background: focusedField === 'email'
                  ? 'rgba(0, 0, 0, 0.06)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: `2px solid ${focusedField === 'email' ? '#1a1a1a' : 'rgba(0, 0, 0, 0.12)'}`,
                borderRadius: '14px',
                color: '#2d3748',
                fontSize: TYPOGRAPHY.sizes.base,
                fontFamily: TYPOGRAPHY.fontFamily,
                outline: 'none',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transform: focusedField === 'email' ? 'translateY(-2px)' : 'translateY(0)',
                boxShadow: focusedField === 'email'
                  ? '0 8px 25px rgba(0, 0, 0, 0.1)'
                  : 'none'
              }}
            />
          </div>

          <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.sizes.sm,
              fontWeight: TYPOGRAPHY.weights.semibold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#1a1a1a',
              marginBottom: '10px'
            }}>Password *</label>
            <input
              type="password"
              value={authForm.password}
              onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              placeholder="••••••••"
              required
              disabled={authLoading || googleLoading}
              style={{
                width: isMobile ? '92%' : '95%',
                padding: isMobile ? '14px 16px' : '16px 18px',
                background: focusedField === 'password'
                  ? 'rgba(0, 0, 0, 0.06)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: `2px solid ${focusedField === 'password' ? '#1a1a1a' : 'rgba(0, 0, 0, 0.12)'}`,
                borderRadius: '14px',
                color: '#2d3748',
                fontSize: TYPOGRAPHY.sizes.base,
                fontFamily: TYPOGRAPHY.fontFamily,
                outline: 'none',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transform: focusedField === 'password' ? 'translateY(-2px)' : 'translateY(0)',
                boxShadow: focusedField === 'password'
                  ? '0 8px 25px rgba(0, 0, 0, 0.1)'
                  : 'none'
              }}
            />
          </div>

          {authMode === 'signup' && (
            <div style={{ marginBottom: isMobile ? '28px' : '32px' }}>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                marginBottom: '10px'
              }}>Confirm Password *</label>
              <input
                type="password"
                value={authForm.confirmPassword}
                onChange={(e) => setAuthForm({ ...authForm, confirmPassword: e.target.value })}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                placeholder="••••••••"
                required={authMode === 'signup'}
                disabled={authLoading || googleLoading}
                style={{
                  width: isMobile ? '92%' : '95%',
                  padding: isMobile ? '14px 16px' : '16px 18px',
                  background: focusedField === 'confirmPassword'
                    ? 'rgba(0, 0, 0, 0.06)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: `2px solid ${focusedField === 'confirmPassword' ? '#1a1a1a' : 'rgba(0, 0, 0, 0.12)'}`,
                  borderRadius: '14px',
                  color: '#2d3748',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  outline: 'none',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transform: focusedField === 'confirmPassword' ? 'translateY(-2px)' : 'translateY(0)',
                  boxShadow: focusedField === 'confirmPassword'
                    ? '0 8px 25px rgba(0, 0, 0, 0.1)'
                    : 'none'
                }}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={authLoading || googleLoading}
            style={{
              width: '100%',
              padding: isMobile ? '16px' : '18px',
              background: authLoading || googleLoading
                ? 'rgba(0, 0, 0, 0.3)'
                : 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
              border: 'none',
              borderRadius: '14px',
              color: '#ffffff',
              fontSize: isMobile ? TYPOGRAPHY.sizes.lg : TYPOGRAPHY.sizes.xl,
              fontWeight: TYPOGRAPHY.weights.bold,
              fontFamily: TYPOGRAPHY.fontFamily,
              cursor: authLoading || googleLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              boxShadow: authLoading || googleLoading
                ? 'none'
                : '0 10px 35px rgba(0, 0, 0, 0.25)',
              marginBottom: isMobile ? '25px' : '30px',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!authLoading && !googleLoading && !isMobile) {
                e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 15px 45px rgba(0, 0, 0, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 10px 35px rgba(0, 0, 0, 0.25)';
            }}
          >
            {/* Shimmer effect */}
            {!authLoading && !googleLoading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: 'shimmer 3s infinite'
              }} />
            )}

            {authLoading ? (
              <>
                <Loader size={22} style={{ animation: 'spin 1s linear infinite' }} />
                {authMode === 'login' ? 'Signing in...' : 'Creating account...'}
              </>
            ) : (
              <>
                {authMode === 'login' ? 'Sign In' : 'Create Account'}
                <ArrowRight size={20} />
              </>
            )}
          </button>

          <div style={{
            textAlign: 'center'
          }}>
            <button
              type="button"
              onClick={() => {
                setAuthMode(authMode === 'login' ? 'signup' : 'login');
                setAuthError('');
                setAuthForm({ email: '', password: '', name: '', confirmPassword: '' });
              }}
              disabled={authLoading || googleLoading}
              style={{
                background: 'none',
                border: 'none',
                color: '#1a1a1a',
                fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                cursor: authLoading || googleLoading ? 'not-allowed' : 'pointer',
                textDecoration: 'none',
                opacity: authLoading || googleLoading ? 0.5 : 1,
                transition: 'all 0.3s ease',
                position: 'relative',
                padding: '4px 0'
              }}
              onMouseEnter={(e) => {
                if (!authLoading && !googleLoading) {
                  e.currentTarget.style.color = '#111111';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#1a1a1a';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {authMode === 'login'
                ? "Don't have an account? Sign up"
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </form>

        {/* Security Badge */}
        <div style={{
          marginTop: isMobile ? '25px' : '30px',
          padding: isMobile ? '14px 16px' : '16px 20px',
          background: 'rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Shield size={isMobile ? 18 : 20} style={{ color: '#1a1a1a', flexShrink: 0 }} />
          <p style={{
            fontSize: TYPOGRAPHY.sizes.xs,
            color: 'rgba(255, 255, 255, 0.6)',
            fontFamily: TYPOGRAPHY.fontFamily,
            margin: 0,
            lineHeight: '1.6'
          }}>
            <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Secure & Protected</strong> - Your data is encrypted and we respect your privacy. We'll never share your information.
          </p>
        </div>
      </div>
    </div>
  );
};


// Our Story Page Component
// Our Story Page Component
const OurStoryPage = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div style={{
      padding: isMobile ? '60px 20px' : '100px 40px',
      maxWidth: '1200px',
      margin: '0 auto',
      minHeight: '80vh'
    }}>
      <h1 style={{
        fontSize: isMobile ? TYPOGRAPHY.sizes.xxxl : TYPOGRAPHY.sizes.display,
        fontWeight: TYPOGRAPHY.weights.bold,
        fontFamily: TYPOGRAPHY.fontFamily,
        background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 50%, #111111 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '40px',
        textAlign: 'center'
      }}>
        Our Story
      </h1>
      <div style={{
        fontSize: TYPOGRAPHY.sizes.base,
        lineHeight: TYPOGRAPHY.lineHeights.relaxed,
        color: 'rgba(0, 0, 0, 0.8)',
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        <p style={{ marginBottom: '25px' }}>
          Dynavation was founded by two passionate college innovators with a simple yet powerful mission — 
          to make quality education accessible and exciting for everyone. What started as a small dream has 
          grown into a dynamic company dedicated to empowering students, educators, and industries with 
          cutting-edge tools and solutions.
        </p>
        
        <h2 style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.xxl : TYPOGRAPHY.sizes.xxxl,
          fontWeight: TYPOGRAPHY.weights.bold,
          fontFamily: TYPOGRAPHY.fontFamily,
          color: '#1a1a1a',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          The Beginning
        </h2>
        <p style={{ marginBottom: '25px' }}>
          It all began in a college dorm room where two engineering students shared a common frustration — 
          the gap between theoretical knowledge and practical application. They saw their peers struggling 
          to understand complex concepts because traditional teaching methods lacked hands-on, engaging experiences. 
          This observation sparked an idea: What if learning could be more interactive, creative, and fun?
        </p>
        <p style={{ marginBottom: '25px' }}>
          With limited resources but boundless enthusiasm, they started creating DIY electronic kits in their 
          spare time. These kits were designed to help students visualize and understand STEM concepts through 
          practical experimentation. Word spread quickly, and soon, schools and educational institutions began 
          showing interest.
        </p>

        <h2 style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.xxl : TYPOGRAPHY.sizes.xxxl,
          fontWeight: TYPOGRAPHY.weights.bold,
          fontFamily: TYPOGRAPHY.fontFamily,
          color: '#1a1a1a',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          Growth and Evolution
        </h2>
        <p style={{ marginBottom: '25px' }}>
          What began as a passion project quickly evolved into Dynavation Electronics Pvt. Ltd. The company's 
          first major breakthrough came with the launch of comprehensive STEM education kits for kids. These 
          kits made learning electronics, robotics, and programming accessible and enjoyable for young minds, 
          sparking curiosity and creativity.
        </p>
        <p style={{ marginBottom: '25px' }}>
          As the impact grew, so did the vision. Recognizing the needs of engineering students and institutions, 
          Dynavation developed Skill Labs — advanced learning modules and equipment designed to bridge the 
          gap between academic knowledge and industry requirements. These labs have since transformed how 
          engineering education is delivered across numerous colleges.
        </p>
        <p style={{ marginBottom: '25px' }}>
          The journey didn't stop there. Understanding the technological needs of modern industries, Dynavation 
          expanded into industrial IoT and R&D solutions. Today, organizations rely on our innovative tools 
          to optimize operations, conduct research, and drive technological advancement.
        </p>

        <h2 style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.xxl : TYPOGRAPHY.sizes.xxxl,
          fontWeight: TYPOGRAPHY.weights.bold,
          fontFamily: TYPOGRAPHY.fontFamily,
          color: '#1a1a1a',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          Our Philosophy
        </h2>
        <p style={{ marginBottom: '25px' }}>
          At Dynavation, we believe learning should be hands-on, creative, and future-ready. We understand 
          that true learning happens when theory meets practice, when curiosity is ignited, and when innovation 
          is encouraged. This philosophy drives every product we create and every solution we deliver.
        </p>
        <p style={{ marginBottom: '25px' }}>
          From STEM education kits for kids to Skill Labs for engineering students, and industrial IoT and 
          R&D solutions for organizations — we bridge the gap between knowledge and real-world application. 
          Our products are designed not just to teach, but to inspire discovery, encourage experimentation, 
          and foster innovation.
        </p>

        <h2 style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.xxl : TYPOGRAPHY.sizes.xxxl,
          fontWeight: TYPOGRAPHY.weights.bold,
          fontFamily: TYPOGRAPHY.fontFamily,
          color: '#1a1a1a',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          Impact and Reach
        </h2>
        <p style={{ marginBottom: '25px' }}>
          Over the years, Dynavation has touched the lives of thousands of students, educators, and professionals. 
          Our STEM kits have introduced countless children to the world of technology, sparking interest that 
          could shape their future careers. Our Skill Labs have helped engineering students gain practical 
          skills that make them industry-ready. Our industrial solutions have enabled organizations to innovate 
          and stay competitive in a rapidly evolving technological landscape.
        </p>
        <p style={{ marginBottom: '25px' }}>
          But numbers tell only part of the story. The real measure of our success lies in the "aha moments" 
          when a student finally understands a complex concept, the confidence gained when theory transforms 
          into practical skill, and the innovations born when the right tools meet creative minds.
        </p>

        <h2 style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.xxl : TYPOGRAPHY.sizes.xxxl,
          fontWeight: TYPOGRAPHY.weights.bold,
          fontFamily: TYPOGRAPHY.fontFamily,
          color: '#1a1a1a',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
        Looking Ahead
        </h2>
        <p style={{ marginBottom: '25px' }}>
          Driven by innovation and guided by purpose, Dynavation continues to create opportunities for learning, 
          discovery, and growth. We remain committed to our founding mission — making quality education accessible 
          and exciting for everyone.
        </p>
        <p style={{ marginBottom: '25px' }}>
          As technology evolves, so do we. We're constantly researching, developing, and refining our products 
          to meet the changing needs of education and industry. Our goal is to stay at the forefront of 
          educational technology while remaining true to our core values of accessibility, quality, and innovation.
        </p>
        <p>
          The journey that began in a college dorm room continues with the same passion and purpose — 
          shaping a smarter and more connected future for all. Whether you're a curious child taking your 
          first steps into STEM, an engineering student preparing for industry, or an organization driving 
          innovation, Dynavation is here to empower your journey with cutting-edge tools and unwavering support.
        </p>

       
      </div>
    </div>
  );
};

// Press Page Component
const PressPage = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const pressReleases = [
    {
      date: 'October 15, 2024',
      title: 'Dynavation Launches Revolutionary AI-Powered Product Line',
      excerpt: 'Today marks a milestone as we unveil our most advanced product lineup yet, featuring integrated AI capabilities that adapt to user needs in real-time.'
    },
    {
      date: 'September 8, 2024',
      title: 'Dynavation Reaches 1 Million Global Users',
      excerpt: 'We are thrilled to announce that our community has grown to over 1 million users worldwide, a testament to our commitment to innovation and quality.'
    },
    {
      date: 'July 22, 2024',
      title: 'Award-Winning Excellence: Tech Innovation Award 2024',
      excerpt: 'Dynavation has been honored with the prestigious Tech Innovation Award for our groundbreaking contributions to consumer technology.'
    },
    {
      date: 'May 30, 2024',
      title: 'Strategic Partnership with Leading Technology Providers',
      excerpt: 'New partnerships expand our capabilities and bring enhanced features to our customers across all product categories.'
    }
  ];

  return (
    <div style={{
      padding: isMobile ? '60px 20px' : '100px 40px',
      maxWidth: '1200px',
      margin: '0 auto',
      minHeight: '80vh'
    }}>
      <h1 style={{
        fontSize: isMobile ? TYPOGRAPHY.sizes.xxxl : TYPOGRAPHY.sizes.display,
        fontWeight: TYPOGRAPHY.weights.bold,
        fontFamily: TYPOGRAPHY.fontFamily,
        background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 50%, #111111 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        Press & Media
      </h1>
      <p style={{
        fontSize: TYPOGRAPHY.sizes.lg,
        color: 'rgba(0, 0, 0, 0.7)',
        textAlign: 'center',
        marginBottom: '60px'
      }}>
        Latest news and announcements from Dynavation
      </p>

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {pressReleases.map((release, index) => (
          <div key={index} style={{
            background: 'rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: '16px',
            padding: isMobile ? '25px' : '35px',
            marginBottom: '30px',
            transition: 'transform 0.3s ease, border-color 0.3s ease',
            cursor: 'pointer'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
            }}>
            <div style={{
              fontSize: TYPOGRAPHY.sizes.sm,
              color: '#1a1a1a',
              marginBottom: '10px',
              fontWeight: TYPOGRAPHY.weights.semibold
            }}>
              {release.date}
            </div>
            <h3 style={{
              fontSize: isMobile ? TYPOGRAPHY.sizes.lg : TYPOGRAPHY.sizes.xl,
              fontWeight: TYPOGRAPHY.weights.bold,
              color: '#2d3748',
              marginBottom: '15px',
              lineHeight: TYPOGRAPHY.lineHeights.tight
            }}>
              {release.title}
            </h3>
            <p style={{
              fontSize: TYPOGRAPHY.sizes.base,
              color: 'rgba(0, 0, 0, 0.7)',
              lineHeight: TYPOGRAPHY.lineHeights.relaxed
            }}>
              {release.excerpt}
            </p>
          </div>
        ))}

        <div style={{
          textAlign: 'center',
          marginTop: '50px',
          padding: '40px',
          background: 'rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '16px'
        }}>
          <h3 style={{
            fontSize: TYPOGRAPHY.sizes.xl,
            fontWeight: TYPOGRAPHY.weights.bold,
            color: '#2d3748',
            marginBottom: '15px'
          }}>
            Media Inquiries
          </h3>
          <p style={{
            fontSize: TYPOGRAPHY.sizes.base,
            color: 'rgba(0, 0, 0, 0.7)',
            marginBottom: '10px'
          }}>
            For press inquiries and media kits, please contact:
          </p>
          <a href="mailto:press@dynavation.com" style={{
            fontSize: TYPOGRAPHY.sizes.lg,
            color: '#1a1a1a',
            fontWeight: TYPOGRAPHY.weights.semibold,
            textDecoration: 'none'
          }}>
            press@dynavation.com
          </a>
        </div>
      </div>
    </div>
  );
};



const GalleryPage = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch gallery from Firebase
  const fetchGallery = async () => {
    try {
      const response = await fetch(`${FIREBASE_CONFIG.databaseURL}/gallery.json`);
      const data = await response.json();
      
      if (data) {
        const galleryArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        })).sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setGallery(galleryArray);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(galleryArray.map(item => item.category))];
        setCategories(uniqueCategories);
      } else {
        setGallery([]);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
    
    // Auto-refresh every 5 seconds
    const interval = setInterval(fetchGallery, 5000);
    return () => clearInterval(interval);
  }, []);

  const filteredGallery = filterCategory === 'all' 
    ? gallery 
    : gallery.filter(item => item.category === filterCategory);

  const featuredItems = gallery.filter(item => item.featured);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff'
      }}>
        <div style={{ textAlign: 'center', color: '#2d3748' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(0, 0, 0, 0.18)',
            borderTop: '3px solid #1a1a1a',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p>Loading Gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '80px 0 60px' : '120px 0 100px',
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)'
    }}>
      {/* Hero Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: isMobile ? '40px' : '80px',
        padding: isMobile ? '0 20px' : '0 40px',
        position: 'relative'
      }}>
        {/* Background Glow */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '300px' : '600px',
          height: isMobile ? '300px' : '600px',
          background: 'radial-gradient(circle, rgba(0, 0, 0, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          pointerEvents: 'none'
        }} />

        <div style={{
          display: 'inline-block',
          marginBottom: '20px',
          padding: isMobile ? '8px 20px' : '10px 28px',
          background: 'rgba(0, 0, 0, 0.08)',
          borderRadius: '30px',
          border: '1px solid rgba(0, 0, 0, 0.18)',
          backdropFilter: 'blur(10px)'
        }}>
          <span style={{
            fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
            fontWeight: TYPOGRAPHY.weights.bold,
            color: '#1a1a1a',
            textTransform: 'uppercase',
            letterSpacing: '0.15em'
          }}>
            ✨ Product Showcase
          </span>
        </div>
        
        <h1 style={{
          fontSize: isMobile ? 'clamp(36px, 10vw, 48px)' : 'clamp(56px, 8vw, 90px)',
          fontWeight: TYPOGRAPHY.weights.extrabold,
          fontFamily: TYPOGRAPHY.fontFamily,
          background: 'linear-gradient(135deg, #ffffff 0%, #1a1a1a 50%, #6b7280 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '20px',
          lineHeight: '1.1',
          letterSpacing: '-0.03em',
          position: 'relative'
        }}>
          Our Gallery
        </h1>
        
        <p style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.xl,
          color: 'rgba(0, 0, 0, 0.7)',
          maxWidth: '700px',
          margin: '0 auto',
          lineHeight: '1.7',
          position: 'relative'
        }}>
          Explore our collection of innovative products, cutting-edge projects, and state-of-the-art facilities
        </p>

        {/* Stats */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: isMobile ? '30px' : '60px',
          marginTop: isMobile ? '30px' : '50px',
          position: 'relative'
        }}>
          <div>
            <div style={{
              fontSize: isMobile ? '32px' : '48px',
              fontWeight: TYPOGRAPHY.weights.bold,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #6b7280 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {gallery.length}
            </div>
            <div style={{
              fontSize: isMobile ? '12px' : TYPOGRAPHY.sizes.sm,
              color: 'rgba(0, 0, 0, 0.6)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>
              Total Items
            </div>
          </div>
          <div>
            <div style={{
              fontSize: isMobile ? '32px' : '48px',
              fontWeight: TYPOGRAPHY.weights.bold,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #6b7280 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {categories.length}
            </div>
            <div style={{
              fontSize: isMobile ? '12px' : TYPOGRAPHY.sizes.sm,
              color: 'rgba(0, 0, 0, 0.6)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>
              Categories
            </div>
          </div>
          <div>
            <div style={{
              fontSize: isMobile ? '32px' : '48px',
              fontWeight: TYPOGRAPHY.weights.bold,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #6b7280 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {featuredItems.length}
            </div>
            <div style={{
              fontSize: isMobile ? '12px' : TYPOGRAPHY.sizes.sm,
              color: 'rgba(0, 0, 0, 0.6)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>
              Featured
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0 40px',
        marginBottom: isMobile ? '30px' : '50px'
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          justifyContent: 'center'
        }}>
          <button
            onClick={() => setFilterCategory('all')}
            style={{
              padding: isMobile ? '10px 20px' : '14px 32px',
              background: filterCategory === 'all' 
                ? 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)' 
                : 'rgba(255, 255, 255, 0.05)',
              border: `2px solid ${filterCategory === 'all' ? '#1a1a1a' : 'rgba(0, 0, 0, 0.18)'}`,
              borderRadius: '30px',
              color: filterCategory === 'all' ? '#ffffff' : '#2d3748',
              fontSize: isMobile ? '12px' : TYPOGRAPHY.sizes.sm,
              fontWeight: TYPOGRAPHY.weights.semibold,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)'
            }}
          >
            All ({gallery.length})
          </button>
          
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              style={{
                padding: isMobile ? '10px 20px' : '14px 32px',
                background: filterCategory === cat 
                  ? 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)' 
                  : 'rgba(255, 255, 255, 0.05)',
                border: `2px solid ${filterCategory === cat ? '#1a1a1a' : 'rgba(0, 0, 0, 0.18)'}`,
                borderRadius: '30px',
                color: '#2d3748',
                fontSize: isMobile ? '12px' : TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.semibold,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
            >
              {cat} ({gallery.filter(item => item.category === cat).length})
            </button>
          ))}
        </div>
      </div>

      {/* Featured Section (only show if filter is 'all' and has featured items) */}
      {filterCategory === 'all' && featuredItems.length > 0 && (
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          padding: isMobile ? '0 16px' : '0 40px',
          marginBottom: isMobile ? '40px' : '60px'
        }}>
          <h2 style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.xl : TYPOGRAPHY.sizes.xxl,
            fontWeight: TYPOGRAPHY.weights.bold,
            color: '#2d3748',
            marginBottom: '25px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ color: '#1a1a1a' }}>⭐</span> Featured
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : featuredItems.length === 1 ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px'
          }}>
            {featuredItems.slice(0, 2).map((item, index) => (
              <div
                key={item.id}
                onClick={() => setSelectedImage(item)}
                style={{
                  position: 'relative',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: '2px solid rgba(0, 0, 0, 0.25)',
                  background: '#f9fafb',
                  transition: 'all 0.4s ease',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s forwards`,
                  opacity: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 30px 60px rgba(0, 0, 0, 0.18)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  position: 'relative',
                  paddingTop: isMobile ? '60%' : '50%',
                  overflow: 'hidden'
                }}>
                  <img
                    src={item.image}
                    alt={item.title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.6s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  />
                  
                  {/* Gradient Overlay */}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 60%)',
                    pointerEvents: 'none'
                  }} />

                  {/* Featured Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)',
                    borderRadius: '25px',
                    fontSize: TYPOGRAPHY.sizes.sm,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    color: '#ffffff',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                  }}>
                    ⭐ Featured
                  </div>

                  {/* Content Overlay */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: isMobile ? '20px' : '30px'
                  }}>
                    <div style={{
                      fontSize: TYPOGRAPHY.sizes.xs,
                      color: '#1a1a1a',
                      fontWeight: TYPOGRAPHY.weights.semibold,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      marginBottom: '8px'
                    }}>
                      {item.category}
                    </div>
                    <h3 style={{
                      fontSize: isMobile ? TYPOGRAPHY.sizes.xl : TYPOGRAPHY.sizes.xxl,
                      fontWeight: TYPOGRAPHY.weights.bold,
                      color: '#2d3748',
                      marginBottom: '10px',
                      lineHeight: '1.3'
                    }}>
                      {item.title}
                    </h3>
                    {item.description && (
                      <p style={{
                        fontSize: TYPOGRAPHY.sizes.base,
                        color: 'rgba(0, 0, 0, 0.8)',
                        lineHeight: '1.6',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gallery Grid */}
      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0 40px'
      }}>
        {filterCategory === 'all' && featuredItems.length > 0 && (
          <h2 style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.xl : TYPOGRAPHY.sizes.xxl,
            fontWeight: TYPOGRAPHY.weights.bold,
            color: '#2d3748',
            marginBottom: '25px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ color: '#1a1a1a' }}>🖼️</span> All Items
          </h2>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile 
            ? 'repeat(2, 1fr)' 
            : 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: isMobile ? '16px' : '25px'
        }}>
          {filteredGallery.map((item, index) => (
            <div
              key={item.id}
              onClick={() => setSelectedImage(item)}
              style={{
                background: '#f8fafc',
                backdropFilter: 'blur(20px)',
                borderRadius: isMobile ? '16px' : '20px',
                overflow: 'hidden',
                border: item.featured ? '2px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                opacity: 0,
                animation: `fadeInUp 0.5s ease-out ${index * 0.05}s forwards`
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = 'translateY(-10px) scale(1.02)';
                  e.currentTarget.style.borderColor = '#1a1a1a';
                  e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.18)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.borderColor = item.featured ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Image */}
              <div style={{
                position: 'relative',
                paddingTop: '75%',
                overflow: 'hidden',
                background: '#0a0a0a'
              }}>
                <img
                  src={item.image}
                  alt={item.title}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease'
                  }}
                  onMouseEnter={(e) => { if (!isMobile) e.currentTarget.style.transform = 'scale(1.1)'; }}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                />
                
                {/* Gradient Overlay */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.9) 100%)',
                  pointerEvents: 'none'
                }} />

                {/* Category Badge */}
                <div style={{
                  position: 'absolute',
                  top: isMobile ? '10px' : '15px',
                  left: isMobile ? '10px' : '15px',
                  padding: isMobile ? '5px 10px' : '8px 16px',
                  background: 'rgba(0, 0, 0, 0.75)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  fontSize: isMobile ? '10px' : TYPOGRAPHY.sizes.xs,
                  fontWeight: TYPOGRAPHY.weights.bold,
                  color: '#ffffff',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {item.category}
                </div>

                {/* Featured Badge */}
                {item.featured && (
                  <div style={{
                    position: 'absolute',
                    top: isMobile ? '10px' : '15px',
                    right: isMobile ? '10px' : '15px',
                    padding: isMobile ? '5px 10px' : '8px 14px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '20px',
                    fontSize: isMobile ? '10px' : TYPOGRAPHY.sizes.xs,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    color: '#ffffff'
                  }}>
                    ⭐
                  </div>
                )}

                {/* View Icon */}
                <div style={{
                  position: 'absolute',
                  bottom: isMobile ? '10px' : '15px',
                  right: isMobile ? '10px' : '15px',
                  width: isMobile ? '36px' : '44px',
                  height: isMobile ? '36px' : '44px',
                  borderRadius: '50%',
                  background: 'rgba(0, 0, 0, 0.75)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.25)'
                }}>
                  <Eye size={isMobile ? 16 : 20} />
                </div>
              </div>

              {/* Content */}
              <div style={{
                padding: isMobile ? '14px' : '20px'
              }}>
                <h3 style={{
                  fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.lg,
                  fontWeight: TYPOGRAPHY.weights.bold,
                  color: '#2d3748',
                  marginBottom: isMobile ? '6px' : '10px',
                  lineHeight: '1.3',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {item.title}
                </h3>
                
                {item.description && (
                  <p style={{
                    fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
                    color: 'rgba(0, 0, 0, 0.6)',
                    lineHeight: '1.5',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {item.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredGallery.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: isMobile ? '60px 20px' : '100px 40px',
            background: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '24px',
            border: '2px solid rgba(0, 0, 0, 0.12)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🖼️</div>
            <h3 style={{
              fontSize: isMobile ? TYPOGRAPHY.sizes.xl : TYPOGRAPHY.sizes.xxl,
              fontWeight: TYPOGRAPHY.weights.bold,
              color: '#2d3748',
              marginBottom: '12px'
            }}>
              No Items Found
            </h3>
            <p style={{
              fontSize: TYPOGRAPHY.sizes.lg,
              color: 'rgba(0, 0, 0, 0.7)',
              marginBottom: '30px'
            }}>
              {filterCategory === 'all' 
                ? 'Gallery items will appear here once added' 
                : `No items found in ${filterCategory}`}
            </p>
            {filterCategory !== 'all' && (
              <button
                onClick={() => setFilterCategory('all')}
                style={{
                  padding: '14px 32px',
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontWeight: TYPOGRAPHY.weights.bold,
                  cursor: 'pointer'
                }}
              >
                View All Items
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          zIndex: 3000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          animation: 'fadeIn 0.3s ease'
        }}
        onClick={() => setSelectedImage(null)}>
          {/* Close Button */}
          <button
            onClick={() => setSelectedImage(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: '#2d3748',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.8)';
              e.currentTarget.style.borderColor = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <X size={24} />
          </button>

          <div style={{
            maxWidth: '1000px',
            width: '100%',
            background: '#f9fafb',
            borderRadius: '24px',
            overflow: 'hidden',
            border: '2px solid rgba(0, 0, 0, 0.18)',
            animation: 'slideUpFade 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
          onClick={(e) => e.stopPropagation()}>
            
            {/* Image */}
            <div style={{
              position: 'relative',
              background: '#ffffff'
            }}>
              <img
                src={selectedImage.image}
                alt={selectedImage.title}
                style={{
                  width: '100%',
                  maxHeight: isMobile ? '300px' : '500px',
                  objectFit: 'contain'
                }}
              />
              
              {selectedImage.featured && (
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)',
                  borderRadius: '25px',
                  fontSize: TYPOGRAPHY.sizes.sm,
                  fontWeight: TYPOGRAPHY.weights.bold,
                  color: '#ffffff',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                }}>
                  ⭐ Featured
                </div>
              )}
            </div>

            {/* Content */}
            <div style={{
              padding: isMobile ? '25px 20px' : '35px 40px'
            }}>
              <div style={{
                display: 'inline-block',
                padding: '6px 14px',
                background: 'rgba(0, 0, 0, 0.12)',
                borderRadius: '20px',
                marginBottom: '15px'
              }}>
                <span style={{
                  fontSize: TYPOGRAPHY.sizes.sm,
                  color: '#1a1a1a',
                  fontWeight: TYPOGRAPHY.weights.semibold,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {selectedImage.category}
                </span>
              </div>

              <h2 style={{
                fontSize: isMobile ? TYPOGRAPHY.sizes.xxl : 'clamp(28px, 4vw, 40px)',
                fontWeight: TYPOGRAPHY.weights.bold,
                color: '#2d3748',
                marginBottom: '15px',
                lineHeight: '1.2'
              }}>
                {selectedImage.title}
              </h2>

              {selectedImage.description && (
                <p style={{
                  fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
                  color: 'rgba(0, 0, 0, 0.8)',
                  lineHeight: '1.8'
                }}>
                  {selectedImage.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Keyframe Animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUpFade {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `
      }} />
    </div>
  );
};


const NewsPage = ({ news }) => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const filteredNews = filter === 'all' ? news : news.filter(article => {
    const articleDate = new Date(article.date);
    const now = new Date();
    const diffDays = Math.floor((now - articleDate) / (1000 * 60 * 60 * 24));
    
    if (filter === 'week') return diffDays <= 7;
    if (filter === 'month') return diffDays <= 30;
    return true;
  });

  return (
    <div style={{
      padding: isMobile ? '80px 0 60px' : '120px 0 100px',
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Subtle Background */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '0',
        width: isMobile ? '250px' : '400px',
        height: isMobile ? '250px' : '400px',
        background: 'radial-gradient(circle, rgba(0, 0, 0, 0.04) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0 40px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{
          marginBottom: isMobile ? '40px' : '70px'
        }}>
          <div style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.base,
            fontWeight: TYPOGRAPHY.weights.semibold,
            color: '#1a1a1a',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}>
            News & Updates
          </div>
          
          <h1 style={{
            fontSize: isMobile ? 'clamp(32px, 10vw, 40px)' : 'clamp(48px, 6vw, 72px)',
            fontWeight: TYPOGRAPHY.weights.bold,
            fontFamily: TYPOGRAPHY.fontFamily,
            color: '#2d3748',
            marginBottom: '16px',
            lineHeight: '1.1',
            letterSpacing: '-0.02em'
          }}>
            Latest Insights
          </h1>
          
          <p style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.xl,
            color: 'rgba(0, 0, 0, 0.7)',
            maxWidth: '700px',
            lineHeight: '1.6',
            marginBottom: isMobile ? '24px' : '40px'
          }}>
            Stay informed with the latest news and stories from Dynavation
          </p>

          {/* Filter Tabs */}
          <div style={{
            display: 'inline-flex',
            gap: isMobile ? '6px' : '8px',
            background: 'rgba(255, 255, 255, 0.02)',
            padding: isMobile ? '4px' : '6px',
            borderRadius: '10px',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            width: isMobile ? '100%' : 'auto',
            overflowX: isMobile ? 'auto' : 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
          className="hide-scrollbar">
            {[
              { value: 'all', label: 'All' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' }
            ].map((btn) => (
              <button
                key={btn.value}
                onClick={() => setFilter(btn.value)}
                style={{
                  padding: isMobile ? '10px 16px' : '12px 28px',
                  background: filter === btn.value ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: filter === btn.value ? '#1a1a1a' : 'rgba(0, 0, 0, 0.5)',
                  fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.base,
                  fontWeight: TYPOGRAPHY.weights.semibold,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontFamily: TYPOGRAPHY.fontFamily,
                  whiteSpace: 'nowrap',
                  flex: isMobile ? '1' : 'initial'
                }}
                onMouseEnter={(e) => {
                  if (filter !== btn.value && !isMobile) {
                    e.currentTarget.style.color = '#2d3748';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (filter !== btn.value) {
                    e.currentTarget.style.color = 'rgba(0, 0, 0, 0.5)';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        {filteredNews.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
            gap: isMobile ? '16px' : '32px'
          }}>
            {filteredNews.map((article, index) => (
              <article
                key={article.id}
                onClick={() => setSelectedArticle(article)}
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: isMobile ? '12px' : '16px',
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  transition: 'all 0.4s ease',
                  opacity: 0,
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s forwards`
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.18)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {article.image && (
                  <div style={{
                    height: isMobile ? '140px' : '240px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img
                      src={article.image}
                      alt={article.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease'
                      }}
                      onMouseEnter={(e) => { if (!isMobile) e.currentTarget.style.transform = 'scale(1.05)'; }}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                    
                    {/* Date Badge */}
                    <div style={{
                      position: 'absolute',
                      top: isMobile ? '8px' : '12px',
                      left: isMobile ? '8px' : '12px',
                      padding: isMobile ? '4px 8px' : '8px 16px',
                      background: 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '6px',
                      fontSize: isMobile ? '9px' : TYPOGRAPHY.sizes.xs,
                      fontWeight: TYPOGRAPHY.weights.semibold,
                      color: '#2d3748'
                    }}>
                      {new Date(article.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                )}
                
                <div style={{
                  padding: isMobile ? '14px' : '28px'
                }}>
                  <h3 style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.xl,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    color: '#2d3748',
                    marginBottom: isMobile ? '8px' : '12px',
                    lineHeight: '1.3',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {article.title}
                  </h3>
                  
                  <p style={{
                    fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.base,
                    color: 'rgba(0, 0, 0, 0.6)',
                    lineHeight: '1.5',
                    display: '-webkit-box',
                    WebkitLineClamp: isMobile ? 2 : 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    marginBottom: isMobile ? '12px' : '20px'
                  }}>
                    {article.excerpt || article.content}
                  </p>
                  
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: '#1a1a1a',
                    fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
                    fontWeight: TYPOGRAPHY.weights.semibold,
                    gap: '4px',
                    transition: 'gap 0.3s ease'
                  }}
                  onMouseEnter={(e) => { if (!isMobile) e.currentTarget.style.gap = '8px'; }}
                  onMouseLeave={(e) => e.currentTarget.style.gap = '4px'}>
                    Read <ArrowRight size={isMobile ? 12 : 16} />
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: isMobile ? '60px 20px' : '100px 40px',
            background: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '16px',
            border: '1px solid rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{
              width: isMobile ? '56px' : '72px',
              height: isMobile ? '56px' : '72px',
              margin: '0 auto 24px',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Mail size={isMobile ? 28 : 36} style={{ color: '#1a1a1a' }} />
            </div>
            
            <h3 style={{
              fontSize: isMobile ? TYPOGRAPHY.sizes.xl : TYPOGRAPHY.sizes.xxl,
              fontWeight: TYPOGRAPHY.weights.semibold,
              color: '#2d3748',
              marginBottom: '12px'
            }}>
              No Articles Found
            </h3>
            
            <p style={{
              fontSize: TYPOGRAPHY.sizes.base,
              color: 'rgba(0, 0, 0, 0.5)',
              marginBottom: '24px'
            }}>
              {filter === 'all' 
                ? 'News articles will appear here once published' 
                : `No news found for the selected time period`}
            </p>
            
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                style={{
                  padding: '12px 28px',
                  background: 'rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(0, 0, 0, 0.18)',
                  borderRadius: '8px',
                  color: '#1a1a1a',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontWeight: TYPOGRAPHY.weights.semibold,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                View All News
              </button>
            )}
          </div>
        )}

{/* Article Modal with Always Visible Close Button */}
{selectedArticle && (
  <div style={{
    position: 'fixed',
    inset: 0,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    zIndex: 3000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: isMobile ? '16px' : '20px',
    overflow: 'auto',
    animation: 'fadeIn 0.3s ease'
  }}
  onClick={() => setSelectedArticle(null)}>
    <div style={{
      maxWidth: '900px',
      width: '100%',
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
      borderRadius: isMobile ? '16px' : '24px',
      overflow: 'hidden',
      border: '1px solid rgba(0, 0, 0, 0.1)',
      maxHeight: '90vh',
      overflow: 'auto',
      animation: 'slideUpFade 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
      boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5)',
      position: 'relative'
    }}
    onClick={(e) => e.stopPropagation()}>
      {/* Always Visible Close Button */}
      <div style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        pointerEvents: 'none',
        padding: isMobile ? '16px' : '20px',
        display: 'flex',
        justifyContent: 'flex-end'
      }}>
        <button
          onClick={() => setSelectedArticle(null)}
          style={{
            width: isMobile ? '44px' : '52px',
            height: isMobile ? '44px' : '52px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            color: '#2d3748',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            pointerEvents: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1a1a1a';
            e.currentTarget.style.transform = 'rotate(90deg) scale(1.1)';
            e.currentTarget.style.borderColor = '#1a1a1a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'rotate(0deg) scale(1)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
          }}
        >
          <X size={isMobile ? 24 : 28} strokeWidth={2.5} />
        </button>
      </div>

      {selectedArticle.image && (
        <div style={{
          height: isMobile ? '220px' : '400px',
          overflow: 'hidden',
          position: 'relative',
          marginTop: isMobile ? '-60px' : '-72px' // Offset for the close button
        }}>
          <img
            src={selectedArticle.image}
            alt={selectedArticle.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 30%, transparent 50%, rgba(0,0,0,0.6) 100%)'
          }} />
        </div>
      )}

      <div style={{
        padding: isMobile ? '28px 20px' : '48px 56px'
      }}>
        <div style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.sm,
          color: 'rgba(0, 0, 0, 0.5)',
          marginBottom: isMobile ? '12px' : '16px',
          fontWeight: TYPOGRAPHY.weights.medium,
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          {new Date(selectedArticle.date).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>

        <h1 style={{
          fontSize: isMobile ? 'clamp(24px, 7vw, 32px)' : 'clamp(36px, 5vw, 48px)',
          fontWeight: TYPOGRAPHY.weights.bold,
          color: '#2d3748',
          marginBottom: isMobile ? '24px' : '32px',
          lineHeight: '1.2',
          letterSpacing: '-0.02em'
        }}>
          {selectedArticle.title}
        </h1>

        <div style={{
          fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.lg,
          color: 'rgba(0, 0, 0, 0.8)',
          lineHeight: '1.8',
          whiteSpace: 'pre-wrap',
          fontFamily: TYPOGRAPHY.fontFamily
        }}>
          {selectedArticle.content}
        </div>
      </div>
    </div>
  </div>
)}
      </div>
    </div>
  );
};



// Blog Page Component
const BlogPage = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const blogPosts = [
    {
      category: 'Technology',
      title: 'The Future of AI in Consumer Electronics',
      date: 'October 20, 2024',
      excerpt: 'Exploring how artificial intelligence is reshaping the way we interact with everyday devices and what it means for the future of technology.',
      readTime: '5 min read'
    },
    {
      category: 'Innovation',
      title: '5 Ways Smart Technology Improves Daily Life',
      date: 'October 12, 2024',
      excerpt: 'Discover how smart technology solutions are making our daily routines more efficient, convenient, and enjoyable.',
      readTime: '4 min read'
    },
    {
      category: 'Insights',
      title: 'Sustainability in Tech: Our Commitment to the Environment',
      date: 'October 5, 2024',
      excerpt: 'Learn about our initiatives to reduce environmental impact and create more sustainable technology products for a better tomorrow.',
      readTime: '6 min read'
    },
    {
      category: 'Product Updates',
      title: 'Behind the Design: Creating User-Centered Products',
      date: 'September 28, 2024',
      excerpt: 'A deep dive into our design philosophy and how we put user experience at the heart of every product we create.',
      readTime: '7 min read'
    },
    {
      category: 'Tech Tips',
      title: 'Maximizing Your Device Performance: Expert Tips',
      date: 'September 20, 2024',
      excerpt: 'Professional tips and tricks to get the most out of your technology devices and maintain peak performance.',
      readTime: '5 min read'
    },
    {
      category: 'Industry News',
      title: 'Tech Trends to Watch in 2025',
      date: 'September 15, 2024',
      excerpt: 'Our predictions for the most impactful technology trends that will shape the industry in the coming year.',
      readTime: '8 min read'
    }
  ];

  return (
    <div style={{
      padding: isMobile ? '60px 20px' : '100px 40px',
      maxWidth: '1200px',
      margin: '0 auto',
      minHeight: '80vh'
    }}>
      <h1 style={{
        fontSize: isMobile ? TYPOGRAPHY.sizes.xxxl : TYPOGRAPHY.sizes.display,
        fontWeight: TYPOGRAPHY.weights.bold,
        fontFamily: TYPOGRAPHY.fontFamily,
        background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 50%, #111111 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        Blog
      </h1>
      <p style={{
        fontSize: TYPOGRAPHY.sizes.lg,
        color: 'rgba(0, 0, 0, 0.7)',
        textAlign: 'center',
        marginBottom: '60px'
      }}>
        Insights, updates, and stories from the world of technology
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '30px',
        maxWidth: '1100px',
        margin: '0 auto'
      }}>
        {blogPosts.map((post, index) => (
          <div key={index} style={{
            background: 'rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: '16px',
            padding: isMobile ? '25px' : '30px',
            transition: 'transform 0.3s ease, border-color 0.3s ease',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
            }}>
            <div style={{
              display: 'inline-block',
              padding: '6px 12px',
              background: 'rgba(0, 0, 0, 0.12)',
              borderRadius: '20px',
              fontSize: TYPOGRAPHY.sizes.xs,
              color: '#1a1a1a',
              fontWeight: TYPOGRAPHY.weights.semibold,
              marginBottom: '15px',
              alignSelf: 'flex-start'
            }}>
              {post.category}
            </div>
            <h3 style={{
              fontSize: TYPOGRAPHY.sizes.xl,
              fontWeight: TYPOGRAPHY.weights.bold,
              color: '#2d3748',
              marginBottom: '12px',
              lineHeight: TYPOGRAPHY.lineHeights.tight
            }}>
              {post.title}
            </h3>
            <p style={{
              fontSize: TYPOGRAPHY.sizes.base,
              color: 'rgba(0, 0, 0, 0.7)',
              lineHeight: TYPOGRAPHY.lineHeights.relaxed,
              marginBottom: '20px',
              flex: 1
            }}>
              {post.excerpt}
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '15px',
              borderTop: '1px solid rgba(0, 0, 0, 0.08)'
            }}>
              <span style={{
                fontSize: TYPOGRAPHY.sizes.sm,
                color: 'rgba(0, 0, 0, 0.5)'
              }}>
                {post.date}
              </span>
              <span style={{
                fontSize: TYPOGRAPHY.sizes.sm,
                color: '#1a1a1a',
                fontWeight: TYPOGRAPHY.weights.medium
              }}>
                {post.readTime}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const WhyChooseUsSection = ({ isMobile }) => {
  const [whyChooseFeatures, setWhyChooseFeatures] = useState([]);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [counters, setCounters] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const fetchWhyChoose = async () => {
      try {
        const response = await fetch(`${FIREBASE_CONFIG.databaseURL}/whyChoose.json`);
        const data = await response.json();
        
        if (data) {
          const featuresArray = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          })).sort((a, b) => (a.order || 0) - (b.order || 0));
          setWhyChooseFeatures(featuresArray);
        } else {
          setWhyChooseFeatures([]);
        }
      } catch (error) {
        console.error('Error fetching why choose features:', error);
      }
    };
    
    // Initial fetch
    fetchWhyChoose();
    
    // Set up polling for real-time updates (every 3 seconds)
    const interval = setInterval(fetchWhyChoose, 3000);
    
    // Cleanup on unmount
    return () => clearInterval(interval);
  }, []);

  // Counter animation function
  const animateCounter = (id, targetValue) => {
    const match = targetValue.match(/^(\d+)(.*)$/);
    if (!match) {
      setCounters(prev => ({ ...prev, [id]: targetValue }));
      return;
    }

    const targetNumber = parseInt(match[1]);
    const suffix = match[2] || '';
    const duration = 2000;
    const steps = 60;
    const increment = targetNumber / steps;
    let step = 0;

    setCounters(prev => ({ ...prev, [id]: `0${suffix}` }));

    const timer = setInterval(() => {
      step++;
      const current = Math.min(Math.round(increment * step), targetNumber);
      setCounters(prev => ({ ...prev, [id]: `${current}${suffix}` }));

      if (step >= steps) {
        clearInterval(timer);
        setCounters(prev => ({ ...prev, [id]: targetValue }));
      }
    }, duration / steps);
  };

  // Start animation when section becomes visible
  const handleScroll = () => {
    const section = document.getElementById('why-choose-section');
    if (section && !hasAnimated) {
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Check if section is in viewport
      if (rect.top < windowHeight * 0.75 && rect.bottom > 0) {
        setIsVisible(true);
        setHasAnimated(true);
        
        // Start counter animations
        whyChooseFeatures.forEach((feature) => {
          if (feature.counterValue) {
            animateCounter(feature.id, feature.counterValue);
          }
        });
      }
    }
  };

  // Set up scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    // Check on mount in case already in view
    setTimeout(handleScroll, 500);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [whyChooseFeatures, hasAnimated]);

  if (whyChooseFeatures.length === 0) return null;

  // Calculate grid columns based on number of features (DESKTOP ONLY)
  const getGridColumns = () => {
    const count = whyChooseFeatures.length;
    
    if (count === 1) {
      return '1fr';
    } else if (count === 2) {
      return 'repeat(2, 1fr)';
    } else if (count === 3) {
      return 'repeat(3, 1fr)';
    } else {
      return 'repeat(4, 1fr)';
    }
  };

  // Calculate max width for centering when features are less than 4 (DESKTOP ONLY)
  const getContainerMaxWidth = () => {
    const count = whyChooseFeatures.length;
    
    if (count === 1) return '400px';
    if (count === 2) return '850px';
    if (count === 3) return '1200px';
    return '100%';
  };

  return (
    <section 
      id="why-choose-section"
      style={{
        padding: isMobile ? '50px 0' : '120px 0',
        position: 'relative',
        overflow: 'hidden',
        background: '#ffffff',
        width: '100%'
      }}
    >
      {/* Background Elements */}
      <div style={{
        position: 'absolute',
        top: '20%',
        right: '-10%',
        width: isMobile ? '300px' : '600px',
        height: isMobile ? '300px' : '600px',
        background: 'radial-gradient(circle, rgba(0, 0, 0, 0.06) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: isMobile ? '100%' : '1400px',
        margin: '0 auto',
        padding: isMobile ? '0' : '0 40px',
        position: 'relative',
        zIndex: 1,
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: isMobile ? '25px' : '80px',
          animation: 'fadeInUp 0.8s ease-out',
          padding: isMobile ? '0 20px' : '0'
        }}>
          <div style={{
            display: 'inline-block',
            marginBottom: isMobile ? '10px' : '24px'
          }}>
            <div style={{
              padding: isMobile ? '5px 14px' : '8px 24px',
              background: 'rgba(0, 0, 0, 0.08)',
              borderRadius: '25px',
              border: '1px solid rgba(0, 0, 0, 0.18)'
            }}>
              <span style={{
                fontSize: isMobile ? '9px' : TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.bold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                textTransform: 'uppercase',
                letterSpacing: '0.15em'
              }}>
                Why Choose Us
              </span>
            </div>
          </div>

          <h2 style={{
            fontSize: isMobile ? 'clamp(22px, 6vw, 28px)' : 'clamp(48px, 6vw, 72px)',
            fontWeight: TYPOGRAPHY.weights.extrabold,
            fontFamily: TYPOGRAPHY.fontFamily,
            letterSpacing: '-0.02em',
            lineHeight: '1.1',
            marginBottom: isMobile ? '10px' : '24px',
            background: 'linear-gradient(135deg, #ffffff 0%, #1a1a1a 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Why Choose OAKMOND
          </h2>

          <p style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.xl,
            color: 'rgba(0, 0, 0, 0.7)',
            fontFamily: TYPOGRAPHY.fontFamily,
            maxWidth: isMobile ? '100%' : '700px',
            margin: '0 auto',
            lineHeight: '1.5'
          }}>
            Experience excellence through innovation and reliability
          </p>
        </div>

        {/* MOBILE: Horizontal Scrolling */}
        {isMobile ? (
          <div style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            paddingBottom: '5px'
          }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              paddingLeft: '16px',
              paddingRight: '16px'
            }}>
              {whyChooseFeatures.map((feature, index) => (
                <div
                  key={feature.id}
                  style={{
                    minWidth: '200px',
                    maxWidth: '200px',
                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0.4) 100%)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    padding: '20px 16px',
                    textAlign: 'center',
                    flexShrink: 0,
                    animation: `fadeInUp 0.8s ease-out ${index * 0.1}s both`,
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    fontSize: '36px',
                    marginBottom: '12px',
                    lineHeight: '1'
                  }}>
                    {feature.icon}
                  </div>

                  {/* Counter (Mobile) */}
                  {feature.counterValue && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{
                        fontSize: '28px',
                        fontWeight: TYPOGRAPHY.weights.bold,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #6b7280 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        lineHeight: '1.2'
                      }}>
                        {counters[feature.id] !== undefined ? counters[feature.id] : feature.counterValue}
                      </div>
                      {feature.counterLabel && (
                        <div style={{
                          fontSize: '10px',
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontWeight: TYPOGRAPHY.weights.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          marginTop: '3px'
                        }}>
                          {feature.counterLabel}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Title */}
                  <h3 style={{
                    fontSize: TYPOGRAPHY.sizes.sm,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    color: '#2d3748',
                    marginBottom: '8px',
                    lineHeight: '1.3'
                  }}>
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p style={{
                    fontSize: TYPOGRAPHY.sizes.xs,
                    fontWeight: TYPOGRAPHY.weights.regular,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    lineHeight: '1.5',
                    color: 'rgba(0, 0, 0, 0.6)',
                    margin: 0
                  }}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* DESKTOP: Grid Layout with Centering */
          <div style={{
            maxWidth: getContainerMaxWidth(),
            margin: '0 auto',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: getGridColumns(),
              gap: '25px',
              justifyContent: 'center',
              alignItems: 'stretch'
            }}>
              {whyChooseFeatures.map((feature, index) => (
                <div
                  key={feature.id}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.3) 100%)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '24px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    padding: '40px 30px',
                    textAlign: 'center',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    cursor: 'pointer',
                    animation: `fadeInUp 0.8s ease-out ${index * 0.1}s both`,
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-10px)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.3)';
                    e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.4) 100%)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.3) 100%)';
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    fontSize: '56px',
                    marginBottom: '20px',
                    transition: 'transform 0.3s ease',
                    lineHeight: '1'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                  }}>
                    {feature.icon}
                  </div>

                  {/* Counter (Desktop) */}
                  {feature.counterValue && (
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{
                        fontSize: '48px',
                        fontWeight: TYPOGRAPHY.weights.bold,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #6b7280 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        lineHeight: '1.2'
                      }}>
                        {counters[feature.id] !== undefined ? counters[feature.id] : feature.counterValue}
                      </div>
                      {feature.counterLabel && (
                        <div style={{
                          fontSize: TYPOGRAPHY.sizes.sm,
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontWeight: TYPOGRAPHY.weights.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          marginTop: '5px'
                        }}>
                          {feature.counterLabel}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Title */}
                  <h3 style={{
                    fontSize: TYPOGRAPHY.sizes.xl,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    color: '#2d3748',
                    marginBottom: '12px',
                    lineHeight: '1.3'
                  }}>
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p style={{
                    fontSize: TYPOGRAPHY.sizes.sm,
                    fontWeight: TYPOGRAPHY.weights.regular,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    lineHeight: '1.7',
                    color: 'rgba(0, 0, 0, 0.7)',
                    margin: 0
                  }}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Hide scrollbar completely for mobile */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media (max-width: 768px) {
            div::-webkit-scrollbar {
              display: none;
            }
          }
        `
      }} />
    </section>
  );
};







const OurSolutionsSection = ({ isMobile }) => {
  const solutions = [
    {
      id: 1,
      label: 'Serving Private Institutions & Companies',
      title: 'Serving Private Institutions & Companies',
      description: 'We collaborate with private institutions, educational organizations, and companies to deliver customized technological solutions. Whether it\'s equipping a technical lab, providing IT infrastructure, or developing custom engineering tools, we tailor our services to meet the specific needs and objectives of each client.',
      image: 'https://dynavationelectronics.com/storage/lab-set-up.jpeg?w=600&h=400&fit=crop',
      position: 'left-top'
    },
    {
      id: 2,
      label: 'Government Projects',
      title: 'Government Tender Projects',
      description: 'We actively participate in government tenders and public sector projects across various departments. Our team is equipped to handle end-to-end execution—from planning and design to manufacturing and delivery—ensuring compliance with government standards and timelines. Our expertise allows us to contribute to national development initiatives with a focus on innovation and reliability.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop',
      position: 'right-bottom'
    },
    {
      id: 3,
      label: 'Custom Product Development for Public and Private Clients',
      title: 'Custom Product Development for Public and Private Clients',
      description: 'We offer product design and manufacturing services tailored to the unique requirements of individuals, startups, and organizations—both public and private. From concept to prototype to production, we transform ideas into functional, user-friendly products. Our team ensures each solution is optimized for performance, cost-effectiveness, and usability.',
      image: 'https://dynavationelectronics.com/storage/istockphoto-856912154-612x612.jpg?w=600&h=400&fit=crop',
      position: 'left-bottom'
    },
    {
      id: 4,
      label: 'Manufacturing of Testing and Measuring Instruments',
      title: 'Manufacturing of Testing and Measuring Instruments',
      description: 'Manufacturing of Testing and Measuring Instruments',
      image: 'https://dynavationelectronics.com/storage/iotaut.jpg?w=600&h=400&fit=crop',
      position: 'right-top'
    }
  ];

  if (isMobile) {
    return (
      <section style={{
        padding: '60px 0',
        background: '#ffffff',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '100%',
          margin: '0 auto',
          padding: '0 16px'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '40px'
          }}>
            <h2 style={{
              fontSize: 'clamp(28px, 8vw, 36px)',
              fontWeight: TYPOGRAPHY.weights.bold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#1a1a1a',
              marginBottom: '16px'
            }}>
              Our Solutions
            </h2>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '30px'
          }}>
            {solutions.map((solution) => (
              <div
                key={solution.id}
                style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  height: '200px',
                  overflow: 'hidden'
                }}>
                  <img
                    src={solution.image}
                    alt={solution.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div style={{ padding: '20px 0' }}>
                  <div style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    background: 'rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(0, 0, 0, 0.18)',
                    borderRadius: '4px',
                    marginBottom: '12px'
                  }}>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: TYPOGRAPHY.weights.semibold,
                      color: '#1a1a1a',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {solution.label}
                    </span>
                  </div>
                  <h3 style={{
                    fontSize: TYPOGRAPHY.sizes.xl,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    color: '#1a1a1a',
                    marginBottom: '12px',
                    lineHeight: '1.3'
                  }}>
                    {solution.title}
                  </h3>
                  <p style={{
                    fontSize: TYPOGRAPHY.sizes.sm,
                    color: 'rgba(0, 0, 0, 0.8)',
                    lineHeight: '1.6'
                  }}>
                    {solution.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Desktop layout - two columns with alternating positions
  const leftSolutions = solutions.filter((_, idx) => idx % 2 === 0);
  const rightSolutions = solutions.filter((_, idx) => idx % 2 === 1);

  const SolutionCard = ({ solution, imagePosition }) => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '0'
    }}>
      {imagePosition === 'top' && (
        <div style={{
          height: '280px',
          overflow: 'hidden',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <img
            src={solution.image}
            alt={solution.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.5s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          />
        </div>
      )}
      
      <div>
        <div style={{
          display: 'inline-block',
          padding: '6px 14px',
          background: 'rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(0, 0, 0, 0.18)',
          borderRadius: '4px',
          marginBottom: '16px'
        }}>
          <span style={{
            fontSize: '11px',
            fontWeight: TYPOGRAPHY.weights.semibold,
            color: '#1a1a1a',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {solution.label}
          </span>
        </div>
        
        <h3 style={{
          fontSize: 'clamp(24px, 2.5vw, 32px)',
          fontWeight: TYPOGRAPHY.weights.bold,
          color: '#1a1a1a',
          marginBottom: '16px',
          lineHeight: '1.2'
        }}>
          {solution.title}
        </h3>
        
        <p style={{
          fontSize: TYPOGRAPHY.sizes.base,
          color: 'rgba(0, 0, 0, 0.7)',
          lineHeight: '1.7'
        }}>
          {solution.description}
        </p>
      </div>

      {imagePosition === 'bottom' && (
        <div style={{
          height: '280px',
          overflow: 'hidden',
          borderRadius: '8px',
          marginTop: '20px'
        }}>
          <img
            src={solution.image}
            alt={solution.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.5s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          />
        </div>
      )}
    </div>
  );

return (
    <section style={{
      padding: '120px 0',
      background: '#ffffff',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 60px'
      }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '80px'
        }}>
          <div style={{
            display: 'inline-block',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '8px 24px',
              background: 'rgba(0, 0, 0, 0.08)',
              borderRadius: '25px',
              border: '1px solid rgba(0, 0, 0, 0.18)'
            }}>
              <span style={{
                fontSize: TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.bold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                textTransform: 'uppercase',
                letterSpacing: '0.15em'
              }}>
                What We Offer
              </span>
            </div>
          </div>

          <h2 style={{
            fontSize: 'clamp(48px, 6vw, 72px)',
            fontWeight: TYPOGRAPHY.weights.extrabold,
            fontFamily: TYPOGRAPHY.fontFamily,
            letterSpacing: '-0.02em',
            lineHeight: '1.1',
            color: '#1a1a1a'
          }}>
            Our Solutions
          </h2>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '80px'
        }}>
          {/* Left Column */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '80px'
          }}>
            {leftSolutions.map((solution, idx) => (
              <SolutionCard 
                key={solution.id} 
                solution={solution} 
                imagePosition={idx === 0 ? 'top' : 'bottom'} 
              />
            ))}
          </div>

          {/* Right Column */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '80px',
            paddingTop: '100px'
          }}>
            {rightSolutions.map((solution, idx) => (
              <SolutionCard 
                key={solution.id} 
                solution={solution} 
                imagePosition={idx === 0 ? 'bottom' : 'top'} 
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};






const TescaLandingPage = () => {


  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('home');
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const categoriesRef = useRef(null);
  const contactRef = useRef(null);
  const searchRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submittingContact, setSubmittingContact] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestFormData, setRequestFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestProduct, setRequestProduct] = useState(null);

  const [contactFormErrors, setContactFormErrors] = useState({});
  const [contactFormTouched, setContactFormTouched] = useState({});
  const [contactSubmitSuccess, setContactSubmitSuccess] = useState(false);


  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
const [news, setNews] = useState([]); // ADD THIS LINE


  // Add these new state variables at the beginning of TescaLandingPage component
  const [productEnquiryData, setProductEnquiryData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [productEnquiryErrors, setProductEnquiryErrors] = useState({});
  const [productEnquiryTouched, setProductEnquiryTouched] = useState({});
  const [submittingProductEnquiry, setSubmittingProductEnquiry] = useState(false);
  const [productEnquirySuccess, setProductEnquirySuccess] = useState(false);

  // Add this function to handle product enquiry submission
  const handleProductEnquirySubmit = async (e) => {
    e.preventDefault();

    // Check if user is logged in
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    // NEW: Add this null check for selectedProduct
    if (!selectedProduct) {
      setProductEnquiryErrors({
        submit: 'No product selected. Please go back and select a product.'
      });
      return;
    }

    // Validation
    const errors = {};
    if (!productEnquiryData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!productEnquiryData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(productEnquiryData.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!productEnquiryData.message.trim()) {
      errors.message = 'Message is required';
    }

    setProductEnquiryErrors(errors);

    if (Object.keys(errors).length > 0) {
      setProductEnquiryTouched({ name: true, email: true, message: true });
      return;
    }

    setSubmittingProductEnquiry(true);

    try {
      const enquiryData = {
        ...productEnquiryData,
        productName: selectedProduct.name,
        productId: selectedProduct.id,
        categoryName: selectedCategory?.name || 'Unknown', // Add null check here
        categoryId: selectedCategory?.id || null, // Add null check here
        timestamp: new Date().toISOString(),
        userId: user.uid,
        userEmail: user.email,
        userName: user.name
      };

      const response = await fetch(`${FIREBASE_CONFIG.databaseURL}/productEnquiries.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enquiryData)
      });

      if (response.ok) {
        setProductEnquirySuccess(true);
        setProductEnquiryData({ name: '', email: '', phone: '', message: '' });
        setProductEnquiryTouched({});

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setProductEnquirySuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      setProductEnquiryErrors({
        submit: 'Failed to submit enquiry. Please try again.'
      });
    } finally {
      setSubmittingProductEnquiry(false);
    }
  };

  // Add this validation function
  const validateProductEnquiryField = (name, value) => {
    let error = '';
    switch (name) {
      case 'name':
        if (!value.trim()) error = 'Name is required';
        break;
      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Invalid email format';
        }
        break;
      case 'message':
        if (!value.trim()) error = 'Message is required';
        break;
    }
    return error;
  };

  // Add this function to handle field changes
  const handleProductEnquiryFieldChange = (name, value) => {
    setProductEnquiryData(prev => ({ ...prev, [name]: value }));
    if (productEnquiryTouched[name]) {
      const error = validateProductEnquiryField(name, value);
      setProductEnquiryErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  // Add this function to handle field blur
  const handleProductEnquiryFieldBlur = (name) => {
    setProductEnquiryTouched(prev => ({ ...prev, [name]: true }));
    const error = validateProductEnquiryField(name, productEnquiryData[name]);
    setProductEnquiryErrors(prev => ({ ...prev, [name]: error }));
  };

  // Reset enquiry form when product changes
  useEffect(() => {
    if (selectedProduct) {
      setProductEnquiryData({
        name: user ? user.name : '',
        email: user ? user.email : '',
        phone: '',
        message: ''
      });
      setProductEnquiryErrors({});
      setProductEnquiryTouched({});
      setProductEnquirySuccess(false);
    }
  }, [selectedProduct, user]);


  // Add this useEffect right after state declarations to check for existing session
  useEffect(() => {
    const checkAuth = async () => {
      // Listen for auth state changes
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
          try {
            // Get user data from database
            const snapshot = await database.ref(`users/${user.uid}`).once('value');
            const userData = snapshot.val();
            const userName = userData?.name || user.displayName || user.email.split('@')[0];

            // Get fresh token
            const idToken = await user.getIdToken();

            setUser({
              email: user.email,
              uid: user.uid,
              token: idToken,
              name: userName,
              photoUrl: user.photoURL || ''
            });

            localStorage.setItem('userToken', idToken);
            localStorage.setItem('userId', user.uid);
            localStorage.setItem('userName', userName);
          } catch (error) {
            console.error('Error loading user data:', error);
          }
        } else {
          setUser(null);
          localStorage.removeItem('userToken');
          localStorage.removeItem('userId');
          localStorage.removeItem('userName');
        }

        setAuthChecking(false);
      });

      // Cleanup subscription
      return () => unsubscribe();
    };

    checkAuth();
  }, []);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setShowAuthModal(false);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('userToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };



  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('nav')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  const RequestInfoModal = ({ product, onClose }) => {
    const [formData, setFormData] = useState({
      name: user ? user.name : '',
      email: user ? user.email : '',
      phone: '',
      message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);

    // Check if user is logged in
    useEffect(() => {
      if (!user) {
        onClose();
        setShowAuthModal(true);
      }
    }, [user]);

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (isSubmittingRef.current) return;

      if (!formData.name || !formData.email) {
        alert('Please fill in all required fields');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        alert('Please enter a valid email address');
        return;
      }

      isSubmittingRef.current = true;
      setSubmitting(true);

      try {
        const productRequest = {
          ...formData,
          productName: product.name,
          productId: product.id,
          timestamp: new Date().toISOString(),
          // Store user details
          userId: user.uid,
          userEmail: user.email,
          userName: user.name
        };

        const response = await fetch(`${FIREBASE_CONFIG.databaseURL}/productRequests.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productRequest)
        });

        if (response.ok) {
          alert('Thank you! Your request has been submitted successfully. We will contact you soon with more information about ' + product.name);
          onClose();
        } else {
          throw new Error('Failed to submit request');
        }
      } catch (error) {
        console.error('Error submitting request:', error);
        alert('Sorry, there was an error submitting your request. Please try again or contact us directly.');
      } finally {
        setSubmitting(false);
        isSubmittingRef.current = false;
      }
    };

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: isMobile ? '16px' : '20px',
        animation: 'fadeIn 0.3s ease'
      }}
        onClick={onClose}>
        <div style={{
          background: 'linear-gradient(135deg, #1a0f0a 0%, #0a0a0a 100%)',
          border: '2px solid rgba(0, 0, 0, 0.18)',
          borderRadius: isMobile ? '16px' : '20px',
          padding: isMobile ? '30px 20px' : '40px',
          maxWidth: isMobile ? '100%' : '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
          onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onClose}
            type="button"
            style={{
              position: 'absolute',
              top: isMobile ? '15px' : '20px',
              right: isMobile ? '15px' : '20px',
              padding: '8px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '8px',
              color: '#2d3748',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>

          <div style={{
            marginBottom: isMobile ? '20px' : '30px'
          }}>
            <h2 style={{
              fontSize: isMobile ? TYPOGRAPHY.sizes.xxl : TYPOGRAPHY.sizes.display,
              fontWeight: TYPOGRAPHY.weights.bold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#2d3748',
              marginBottom: '10px'
            }}>
              Request Information
            </h2>
            <p style={{
              fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
              color: 'rgba(0, 0, 0, 0.7)',
              fontFamily: TYPOGRAPHY.fontFamily
            }}>
              Interested in <span style={{ color: '#1a1a1a', fontWeight: TYPOGRAPHY.weights.semibold }}>{product.name}</span>? Fill out the form below and we'll get back to you shortly.
            </p>
          </div>

          <div style={{
            background: 'rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.18)',
            borderRadius: '12px',
            padding: isMobile ? '12px' : '15px',
            marginBottom: isMobile ? '20px' : '30px',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '12px' : '15px'
          }}>
            <img
              src={product.image}
              alt={product.name}
              style={{
                width: isMobile ? '50px' : '60px',
                height: isMobile ? '50px' : '60px',
                borderRadius: '8px',
                objectFit: 'cover'
              }}
            />
            <div>
              <h3 style={{
                fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#2d3748',
                marginBottom: '5px'
              }}>
                {product.name}
              </h3>
              <p style={{
                fontSize: TYPOGRAPHY.sizes.xs,
                color: 'rgba(0, 0, 0, 0.6)',
                fontFamily: TYPOGRAPHY.fontFamily,
                margin: 0
              }}>
                {product.shortDescription}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: isMobile ? '15px' : '20px' }}>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                marginBottom: '8px'
              }}>Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                required
                disabled={submitting}
                style={{
                  width: isMobile ? '92%' : '95%',
                  padding: isMobile ? '10px' : '12px',
                  background: '#f8f9fa',
                  border: '2px solid rgba(0, 0, 0, 0.18)',
                  borderRadius: '10px',
                  color: '#2d3748',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: isMobile ? '15px' : '20px' }}>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                marginBottom: '8px'
              }}>Email Address *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                required
                disabled={submitting}
                style={{
                  width: isMobile ? '92%' : '95%',
                  padding: isMobile ? '10px' : '12px',
                  background: '#f8f9fa',
                  border: '2px solid rgba(0, 0, 0, 0.18)',
                  borderRadius: '10px',
                  color: '#2d3748',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: isMobile ? '15px' : '20px' }}>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                marginBottom: '8px'
              }}>Phone Number (Optional)</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
                disabled={submitting}
                style={{
                  width: isMobile ? '92%' : '95%',
                  padding: isMobile ? '10px' : '12px',
                  background: '#f8f9fa',
                  border: '2px solid rgba(0, 0, 0, 0.18)',
                  borderRadius: '10px',
                  color: '#2d3748',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.semibold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                marginBottom: '8px'
              }}>Additional Message (Optional)</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Any specific questions or requirements..."
                rows={4}
                disabled={submitting}
                style={{
                  width: isMobile ? '92%' : '95%',
                  padding: isMobile ? '10px' : '12px',
                  background: '#f8f9fa',
                  border: '2px solid rgba(0, 0, 0, 0.18)',
                  borderRadius: '10px',
                  color: '#2d3748',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '15px'
            }}>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: isMobile ? '12px' : '15px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(0, 0, 0, 0.15)',
                  borderRadius: '12px',
                  color: '#2d3748',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontWeight: TYPOGRAPHY.weights.semibold,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: isMobile ? '12px' : '15px',
                  background: submitting
                    ? 'rgba(0, 0, 0, 0.3)'
                    : 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontWeight: TYPOGRAPHY.weights.bold,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!submitting && !isMobile) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.25)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {submitting ? (
                  <>
                    <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </form>

          <div style={{
            marginTop: isMobile ? '15px' : '20px',
            padding: isMobile ? '12px' : '15px',
            background: 'rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: '10px'
          }}>
            <p style={{
              fontSize: TYPOGRAPHY.sizes.xs,
              color: 'rgba(0, 0, 0, 0.6)',
              fontFamily: TYPOGRAPHY.fontFamily,
              margin: 0,
              lineHeight: '1.6'
            }}>
              🔒 Your information is secure and will only be used to respond to your inquiry about this product.
              We respect your privacy and will never share your data with third parties.
            </p>
          </div>
        </div>
      </div>
    );
  };

useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch categories
      const categoriesResponse = await fetch(`${FIREBASE_CONFIG.databaseURL}/categories.json`);
      const categoriesData = await categoriesResponse.json();

      if (categoriesData) {
        const categoriesArray = Object.keys(categoriesData)
          .map(key => ({
            id: key,
            ...categoriesData[key]
          }))
          .filter(category =>
            category.name &&
            category.description &&
            category.image
          );
        setCategories(categoriesArray);
      } else {
        setCategories([]);
      }

      // Fetch news
      try {
        const newsResponse = await fetch(`${FIREBASE_CONFIG.databaseURL}/news.json`);
        const newsData = await newsResponse.json();

        if (newsData) {
          const newsArray = Object.keys(newsData)
            .map(key => ({
              id: key,
              ...newsData[key]
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
          
          console.log('Fetched news:', newsArray); // Debug log
          setNews(newsArray);
        } else {
          console.log('No news data found'); // Debug log
          setNews([]);
        }
      } catch (newsError) {
        console.error('Error fetching news:', newsError);
        setNews([]);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  fetchData();

  // Set up polling with smart updates
  const pollInterval = setInterval(() => {
    // Poll categories
    fetch(`${FIREBASE_CONFIG.databaseURL}/categories.json`)
      .then(response => response.json())
      .then(data => {
        if (data) {
          const categoriesArray = Object.keys(data)
            .map(key => ({
              id: key,
              ...data[key]
            }))
            .filter(category =>
              category.name &&
              category.description &&
              category.image
            );

          setCategories(prevCategories => {
            const hasChanged = JSON.stringify(prevCategories) !== JSON.stringify(categoriesArray);
            return hasChanged ? categoriesArray : prevCategories;
          });
        } else {
          setCategories(prevCategories => {
            return prevCategories.length > 0 ? [] : prevCategories;
          });
        }
      })
      .catch(error => {
        console.error('Error polling categories:', error);
      });

    // Poll news
    fetch(`${FIREBASE_CONFIG.databaseURL}/news.json`)
      .then(response => response.json())
      .then(data => {
        if (data) {
          const newsArray = Object.keys(data)
            .map(key => ({
              id: key,
              ...data[key]
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date));

          setNews(prevNews => {
            const hasChanged = JSON.stringify(prevNews) !== JSON.stringify(newsArray);
            return hasChanged ? newsArray : prevNews;
          });
        } else {
          setNews(prevNews => {
            return prevNews.length > 0 ? [] : prevNews;
          });
        }
      })
      .catch(error => {
        console.error('Error polling news:', error);
      });
  }, 3000);

  return () => {
    clearInterval(pollInterval);
  };
}, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    // Check if user is logged in
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    // Validation
    const errors = {};
    if (!contactFormData.name.trim()) {
      errors.name = 'Name is required';
    } else if (contactFormData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    if (!contactFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactFormData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!contactFormData.message.trim()) {
      errors.message = 'Message is required';
    } else if (contactFormData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }

    setContactFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setContactFormTouched({ name: true, email: true, message: true });
      return;
    }

    setSubmittingContact(true);

    try {
      const contactMessage = {
        name: contactFormData.name.trim(),
        email: contactFormData.email.trim(),
        message: contactFormData.message.trim(),
        timestamp: new Date().toISOString(),
        status: 'pending',
        // Store user details
        userId: user.uid,
        userEmail: user.email,
        userName: user.name
      };

      const response = await fetch(`${FIREBASE_CONFIG.databaseURL}/contactMessages.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactMessage)
      });

      if (response.ok) {
        setContactSubmitSuccess(true);
        setContactFormData({ name: '', email: '', message: '' });
        setContactFormTouched({});
        setContactFormErrors({});

        setTimeout(() => {
          contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);

        setTimeout(() => {
          setContactSubmitSuccess(false);
        }, 8000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setContactFormErrors({
        submit: 'Sorry, there was an error sending your message. Please try again or contact us directly at info@dynavation.com'
      });
    } finally {
      setSubmittingContact(false);
    }
  };

  // Add these helper functions for field validation:
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = 'Name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        }
        break;
      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'message':
        if (!value.trim()) {
          error = 'Message is required';
        } else if (value.trim().length < 10) {
          error = 'Message must be at least 10 characters';
        }
        break;
    }

    return error;
  };

  const handleContactFieldChange = (name, value) => {
    setContactFormData(prev => ({ ...prev, [name]: value }));

    if (contactFormTouched[name]) {
      const error = validateField(name, value);
      setContactFormErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleContactFieldBlur = (name) => {
    setContactFormTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, contactFormData[name]);
    setContactFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleProductRequest = (product) => {
    setRequestProduct(product);
    setShowRequestModal(true);
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setCurrentView('products');
  };

  const handleProductClick = (product) => {
    // If product has a category reference, set it
    if (product.category) {
      setSelectedCategory(product.category);
    } else {
      // Find the category that contains this product
      const category = categories.find(cat =>
        cat.products?.some(p => p.id === product.id)
      );
      if (category) {
        setSelectedCategory(category);
      }
    }

    setSelectedProduct(product);
    setCurrentView('productDetail');
  };

  const handleBackClick = () => {
    if (currentView === 'productDetail') {
      setCurrentView('products');
      setSelectedProduct(null);
    } else if (currentView === 'products') {
      setCurrentView('home');
      setSelectedCategory(null);
    }
  };

  const scrollToCategories = () => {
    setCurrentView('home');
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      categoriesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const scrollToContact = () => {
    setCurrentView('home');
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setGlobalSearchQuery(query);

    if (query.trim().length > 0) {
      const results = [];
      categories.forEach(category => {
        category.products?.forEach(product => {
          if (
            product.name.toLowerCase().includes(query.toLowerCase()) ||
            product.shortDescription.toLowerCase().includes(query.toLowerCase())
          ) {
            results.push({ ...product, categoryName: category.name, category });
          }
        });
      });
      setSearchSuggestions(results.slice(0, 5));
      setShowSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleProductSelect = (product, category) => {
    setSelectedCategory(category);
    setSelectedProduct(product);
    setCurrentView('productDetail');
    setGlobalSearchQuery('');
    setShowSuggestions(false);
    setIsMobileMenuOpen(false);

    setTimeout(() => {
      const heroHeight = window.innerHeight;
      window.scrollTo({
        top: heroHeight,
        behavior: 'smooth'
      });
    }, 100);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // if (loading) {
  //   return (
  //     <div style={{
  //       minHeight: '100vh',
  //       display: 'flex',
  //       alignItems: 'center',
  //       justifyContent: 'center',
  //       background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 50%, #f3f4f6 100%)'
  //     }}>
  //       <div style={{ textAlign: 'center', color: '#2d3748' }}>
  //         <div style={{
  //           width: isMobile ? '50px' : '60px',
  //           height: isMobile ? '50px' : '60px',
  //           border: '4px solid rgba(0, 0, 0, 0.12)',
  //           borderTop: '4px solid #1a1a1a',
  //           borderRadius: '50%',
  //           animation: 'spin 1s linear infinite',
  //           margin: '0 auto 20px'
  //         }} />
  //         <p style={{
  //           fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
  //           fontFamily: TYPOGRAPHY.fontFamily,
  //           fontWeight: TYPOGRAPHY.weights.medium
  //         }}>
  //           Loading amazing products...
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }



  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 50%, #f3f4f6 100%)',
      color: '#2d3748',
      overflowX: 'hidden'
    }}>
      {/* Navigation */}
      {/* Navigation */}
<nav style={{
  position: 'fixed',
  top: 0,
  width: '100%',
  zIndex: 1000,
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(20px)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
}}>
  <div style={{
    maxWidth: '1400px',  // Changed from 1280px to 1400px for more space
    margin: '0 auto',
    padding: isMobile ? '12px 16px' : '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '20px'  // Added gap between main sections
  }}>
    <img
      src="/oakmond logo.png"
      alt="Dynavation Logo"
      style={{
        height: isMobile ? '55px' : '70px',
        width: 'auto',
        cursor: 'pointer',
        transition: 'all 0.3s ease-out',
        flexShrink: 0  // Prevent logo from shrinking
      }}
      onClick={() => {
        setCurrentView('home');
        setSelectedCategory(null);
        setSelectedProduct(null);
        setGlobalSearchQuery('');
        setShowSuggestions(false);
        setIsMobileMenuOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
    />

 {/* Desktop Navigation */}
{!isMobile && (
  <div style={{
    display: 'flex',
    gap: '18px',  // Reduced gap slightly to accommodate larger text
    alignItems: 'center',
    flex: '0 1 auto'
  }}>
    {[
      { name: 'Categories', action: scrollToCategories },
      { name: 'Gallery', action: () => { setCurrentView('gallery'); setIsMobileMenuOpen(false); } },
      { name: 'News', action: () => { setCurrentView('news'); setIsMobileMenuOpen(false); } },
      { name: 'About', action: () => { setCurrentView('about'); setIsMobileMenuOpen(false); } },
      { name: 'Contact', action: scrollToContact }
    ].map((item, index) => (
      <a
        key={index}
        href="#"
        onClick={(e) => {
          e.preventDefault();
          item.action();
        }}
        style={{
          color: '#2d3748',
          fontSize: TYPOGRAPHY.sizes.base,  // Changed back to base size
          fontWeight: TYPOGRAPHY.weights.medium,
          fontFamily: TYPOGRAPHY.fontFamily,
          textDecoration: 'none',
          transition: 'color 0.3s ease-out',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => e.currentTarget.style.color = '#1a1a1a'}
        onMouseLeave={(e) => e.currentTarget.style.color = '#2d3748'}
      >
        {item.name}
      </a>
    ))}
  </div>
)}

    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexShrink: 0  // Prevent this section from shrinking
    }}>
            {!isMobile && (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '25px',
                  padding: '10px 20px',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  position: 'relative'
                }} ref={searchRef}>
                  <Search size={18} style={{ marginRight: '10px', color: '#1a1a1a' }} />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={globalSearchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => globalSearchQuery && setShowSuggestions(true)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      fontSize: TYPOGRAPHY.sizes.sm,
                      fontWeight: TYPOGRAPHY.weights.regular,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      color: '#2d3748',
                      width: '200px'
                    }}
                  />

                  {showSuggestions && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '10px',
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '15px',
                      border: '1px solid rgba(0, 0, 0, 0.18)',
                      overflow: 'hidden',
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                      zIndex: 2000,
                      maxWidth: '300px'
                    }}>
                      {searchSuggestions.length > 0 ? (
                        searchSuggestions.map((product, index) => (
                          <div
                            key={product.id}
                            onClick={() => handleProductSelect(product, product.category)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: index < searchSuggestions.length - 1 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
                              transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{
                              fontSize: TYPOGRAPHY.sizes.sm,
                              fontWeight: TYPOGRAPHY.weights.semibold,
                              color: '#2d3748',
                              marginBottom: '4px'
                            }}>
                              {product.name}
                            </div>
                            <div style={{
                              fontSize: TYPOGRAPHY.sizes.xs,
                              color: '#1a1a1a'
                            }}>
                              in {product.categoryName}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{
                          padding: '20px 16px',
                          textAlign: 'center'
                        }}>
                          <Search size={24} style={{ color: 'rgba(0, 0, 0, 0.3)', marginBottom: '8px' }} />
                          <div style={{
                            fontSize: TYPOGRAPHY.sizes.sm,
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontFamily: TYPOGRAPHY.fontFamily
                          }}>
                            No products found
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Desktop Login/Logout */}
                {user ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      padding: '8px 16px',
                      background: 'rgba(0, 0, 0, 0.08)',
                      borderRadius: '20px',
                      border: '1px solid rgba(0, 0, 0, 0.18)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Users size={16} style={{ color: '#1a1a1a' }} />
                      <span style={{
                        fontSize: TYPOGRAPHY.sizes.sm,
                        color: '#2d3748',
                        fontWeight: TYPOGRAPHY.weights.medium,
                        fontFamily: TYPOGRAPHY.fontFamily
                      }}>
                        {user.name}
                      </span>
                    </div>
                    <button
                      onClick={handleLogout}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '20px',
                        color: '#ef4444',
                        fontSize: TYPOGRAPHY.sizes.sm,
                        fontWeight: TYPOGRAPHY.weights.semibold,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                      }}
                    >
                      <LogOut size={14} />
                      Logout
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    style={{
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                      border: 'none',
                      borderRadius: '20px',
                      color: '#ffffff',
                      fontSize: TYPOGRAPHY.sizes.sm,
                      fontWeight: TYPOGRAPHY.weights.semibold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.18)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.18)';
                    }}
                  >
                    <Users size={16} />
                    Login
                  </button>
                )}
              </>
            )}

            {isMobile && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                style={{
                  background: 'rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.18)',
                  borderRadius: '12px',
                  padding: '10px',
                  color: '#1a1a1a',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobile && isMobileMenuOpen && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            padding: '20px',
            animation: 'slideDown 0.3s ease-out'
          }}>
            {/* Mobile Search */}
            <div style={{
              marginBottom: '20px',
              position: 'relative'
            }} ref={searchRef}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '25px',
                padding: '12px 16px',
                border: '1px solid rgba(0, 0, 0, 0.12)'
              }}>
                <Search size={18} style={{ marginRight: '10px', color: '#1a1a1a' }} />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={globalSearchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => globalSearchQuery && setShowSuggestions(true)}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    fontSize: TYPOGRAPHY.sizes.sm,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    color: '#2d3748'
                  }}
                />
              </div>

              {showSuggestions && (
                <div style={{
                  marginTop: '10px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '15px',
                  border: '1px solid rgba(0, 0, 0, 0.18)',
                  overflow: 'hidden'
                }}>
                  {searchSuggestions.length > 0 ? (
                    searchSuggestions.map((product, index) => (
                      <div
                        key={product.id}
                        onClick={() => {
                          handleProductSelect(product, product.category);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{
                          padding: '12px 16px',
                          borderBottom: index < searchSuggestions.length - 1 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'
                        }}
                      >
                        <div style={{
                          fontSize: TYPOGRAPHY.sizes.sm,
                          fontWeight: TYPOGRAPHY.weights.semibold,
                          color: '#2d3748',
                          marginBottom: '4px'
                        }}>
                          {product.name}
                        </div>
                        <div style={{
                          fontSize: TYPOGRAPHY.sizes.xs,
                          color: '#1a1a1a'
                        }}>
                          in {product.categoryName}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '20px 16px',
                      textAlign: 'center'
                    }}>
                      <Search size={24} style={{ color: 'rgba(0, 0, 0, 0.3)', marginBottom: '8px' }} />
                      <div style={{
                        fontSize: TYPOGRAPHY.sizes.sm,
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontFamily: TYPOGRAPHY.fontFamily
                      }}>
                        No products found
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Menu Items */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              marginBottom: '20px'
            }}>
              {[
  { name: 'Categories', action: scrollToCategories },
  { name: 'Gallery', action: () => { setCurrentView('gallery'); setIsMobileMenuOpen(false); } },
  { name: 'News', action: () => { setCurrentView('news'); setIsMobileMenuOpen(false); } },
  { name: 'About', action: () => { setCurrentView('about'); setIsMobileMenuOpen(false); } },
  { name: 'Contact', action: scrollToContact }
].map((item, index) => (
                <a
                  key={index}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    item.action();
                  }}
                  style={{
                    color: '#2d3748',
                    fontSize: TYPOGRAPHY.sizes.lg,
                    fontWeight: TYPOGRAPHY.weights.medium,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    textDecoration: 'none',
                    padding: '12px 16px',
                    background: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    display: 'block',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {item.name}
                </a>
              ))}
            </div>

            {/* Mobile Login/Logout Section */}
            <div style={{
              paddingTop: '20px',
              borderTop: '1px solid rgba(0, 0, 0, 0.12)'
            }}>
              {user ? (
                <>
                  <div style={{
                    padding: '12px 16px',
                    background: 'rgba(0, 0, 0, 0.08)',
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.18)',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <Users size={18} style={{ color: '#1a1a1a' }} />
                    <span style={{
                      fontSize: TYPOGRAPHY.sizes.base,
                      color: '#2d3748',
                      fontWeight: TYPOGRAPHY.weights.medium,
                      fontFamily: TYPOGRAPHY.fontFamily
                    }}>
                      {user.name}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.4)',
                      borderRadius: '12px',
                      color: '#ef4444',
                      fontSize: TYPOGRAPHY.sizes.base,
                      fontWeight: TYPOGRAPHY.weights.semibold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setShowAuthModal(true);
                    setIsMobileMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: TYPOGRAPHY.sizes.base,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.25)',
                    cursor: 'pointer'
                  }}
                >
                  <Users size={18} />
                  Login / Sign Up
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      {currentView !== 'about' && (
        <section style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          paddingTop: isMobile ? '60px' : '80px',
          padding: isMobile ? '60px 20px 40px' : '80px 24px'
        }}>
          {/* <video
            autoPlay
            loop
            muted
            playsInline
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              opacity: isMobile ? 0.2 : 0.3
            }}
          > */}
          <video
  autoPlay
  loop
  muted
  playsInline
  style={{
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 0,
    opacity: 1,
    backgroundColor: '#000',
    filter: isMobile
      ? 'contrast(1) brightness(1) saturate(1)'
      : 'contrast(1.05) brightness(0.98) saturate(1.05)',
    transform: 'translateZ(0)',
    willChange: 'transform'
  }}
>

            <source src="/refenerance.mp4" type="video/mp4" />
          </video>

          {/* <div style={{
            position: 'absolute',
            inset: 0,
            background: isMobile
              ? 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)'
              : 'radial-gradient(ellipse at center, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1
          }} /> */}

          {/* <div style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1
          }}>
            <div style={{
              position: 'absolute',
              top: '10%',
              left: '10%',
              width: isMobile ? '200px' : '300px',
              height: isMobile ? '200px' : '300px',
              background: 'rgba(0, 0, 0, 0.08)',
              borderRadius: '50%',
              filter: isMobile ? 'blur(40px)' : 'blur(60px)',
              animation: 'float 6s ease-in-out infinite'
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '10%',
              right: '10%',
              width: isMobile ? '250px' : '400px',
              height: isMobile ? '250px' : '400px',
              background: 'rgba(0, 0, 0, 0.06)',
              borderRadius: '50%',
              filter: isMobile ? 'blur(50px)' : 'blur(80px)',
              animation: 'float 8s ease-in-out infinite reverse'
            }}></div>
          </div> */}

          <div style={{
            position: 'relative',
            zIndex: 10,
            maxWidth: '1200px',
            margin: '0 auto',
            padding: isMobile ? '0 16px' : '0 24px',
            textAlign: 'center',
            marginBottom: isMobile ? '40px' : '80px'
          }}>
            <h1 style={{
              fontSize: isMobile ? 'clamp(48px, 12vw, 80px)' : TYPOGRAPHY.sizes.hero,
              fontWeight: TYPOGRAPHY.weights.extrabold,
              fontFamily: TYPOGRAPHY.fontFamily,
              letterSpacing: TYPOGRAPHY.letterSpacing.tight,
              lineHeight: TYPOGRAPHY.lineHeights.tight,
              marginBottom: isMobile ? '20px' : '30px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: isMobile ? '12px' : '20px'
              }}>
                Build To
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 50%, #111111 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Last
              </div>
            </h1>

            {/* <p style={{
              fontSize: isMobile ? 'clamp(16px, 4vw, 20px)' : 'clamp(20px, 2.5vw, 28px)',
              fontWeight: TYPOGRAPHY.weights.regular,
              fontFamily: TYPOGRAPHY.fontFamily,
              lineHeight: TYPOGRAPHY.lineHeights.normal,
              color: 'rgba(0, 0, 0, 0.8)',
              marginBottom: isMobile ? '30px' : '50px',
              maxWidth: isMobile ? '100%' : '800px',
              margin: isMobile ? '0 auto 30px' : '0 auto 50px auto',
              padding: isMobile ? '0 10px' : '0'
            }}>
              Discover premium technology solutions designed for the future.
              Where innovation meets excellence.
            </p> */}
          </div>

         <div style={{
  position: 'absolute',
  bottom: isMobile ? 20 : 40,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10,
  animation: 'bounce 2s infinite'
}}>
  {isMobile ? (
    // Mobile version - Chevron icon
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px'
    }}>
      <div style={{
        color: '#1a1a1a',
        fontSize: '12px',
        fontFamily: TYPOGRAPHY.fontFamily,
        fontWeight: TYPOGRAPHY.weights.medium,
        opacity: 0.8,
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        Scroll Down
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px'
      }}>
        <ChevronRight 
          size={20} 
          style={{
            color: '#1a1a1a',
            transform: 'rotate(90deg)',
            opacity: 0.6,
            animation: 'fadeInDown 1s infinite'
          }}
        />
        <ChevronRight 
          size={20} 
          style={{
            color: '#1a1a1a',
            transform: 'rotate(90deg)',
            opacity: 0.8,
            animation: 'fadeInDown 1s infinite 0.2s'
          }}
        />
      </div>
    </div>
  ) : (
    // Desktop version - Mouse icon
    <div style={{
      width: 30,
      height: 50,
      border: '2px solid rgba(0, 0, 0, 0.3)',
      borderRadius: 20,
      display: 'flex',
      justifyContent: 'center',
      paddingTop: 8,
      background: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(5px)'
    }}>
      <div style={{
        width: 6,
        height: 6,
        background: '#1a1a1a',
        borderRadius: '50%',
        animation: 'scroll 1.5s infinite'
      }} />
    </div>
  )}
</div>
        </section>
      )}




      {/* Main Content */}
      <div style={{ position: 'relative', zIndex: 10, width: '100%', margin: '0 auto' }}>
        {currentView === 'about' && <AboutPage />}
        {currentView === 'our-story' && <OurStoryPage />}
        {currentView === 'press' && <PressPage />}
        {currentView === 'blog' && <BlogPage />}
      {currentView === 'gallery' && <GalleryPage categories={categories} handleProductClick={handleProductClick} />}
{currentView === 'news' && <NewsPage news={news} />}



{/* Our Solutions Section - Add this after the hero section and before categories */}
{currentView === 'home' && (
  <section style={{
    padding: isMobile ? '60px 0' : '100px 0',
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
  }}>
    {/* Animated Background */}
    <div style={{
      position: 'absolute',
      top: '50%',
      right: '5%',
      width: isMobile ? '200px' : '400px',
      height: isMobile ? '200px' : '400px',
      background: 'radial-gradient(circle, rgba(0, 0, 0, 0.06) 0%, transparent 70%)',
      borderRadius: '50%',
      filter: 'blur(60px)',
      animation: 'float 8s ease-in-out infinite',
      pointerEvents: 'none'
    }} />

    <div style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isMobile ? '0 16px' : '0 40px',
      position: 'relative',
      zIndex: 1
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '40px' : '80px',
        alignItems: 'center'
      }}>
        {/* Left Content */}
        <div style={{
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          <div style={{
            display: 'inline-block',
            marginBottom: isMobile ? '16px' : '24px'
          }}>
            <div style={{
              padding: '8px 20px',
              background: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '25px',
              border: '1px solid rgba(0, 0, 0, 0.18)'
            }}>
              <span style={{
                fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.bold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                textTransform: 'uppercase',
                letterSpacing: '0.15em'
              }}>
                What We Offer
              </span>
            </div>
          </div>

          <h2 style={{
            fontSize: isMobile ? 'clamp(32px, 8vw, 44px)' : 'clamp(44px, 5vw, 64px)',
            fontWeight: TYPOGRAPHY.weights.extrabold,
            fontFamily: TYPOGRAPHY.fontFamily,
            color: '#1a1a1a',
            marginBottom: isMobile ? '16px' : '24px',
            lineHeight: '1.1',
            letterSpacing: '-0.02em'
          }}>
            Our Solutions
          </h2>

          <p style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.xl,
            fontWeight: TYPOGRAPHY.weights.regular,
            fontFamily: TYPOGRAPHY.fontFamily,
            lineHeight: TYPOGRAPHY.lineHeights.relaxed,
            color: 'rgba(0, 0, 0, 0.7)',
            marginBottom: isMobile ? '30px' : '40px'
          }}>
            Join the innovation revolution with Dynavation Electronics, where technology meets efficiency!. We support you at every step, ensuring seamless and successful tests. With our expertise and focus on quality, we handle the complexities so you can deliver exceptional results with confidence.
          </p>

          {/* Solutions List */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '16px' : '20px'
          }}>
            {[
              'Government Projects',
              'Serving Private Institutions and Companies',
              'Manufacturing of Testing and Measuring Instruments',
              'Custom Product Development for Public and Private Clients'
            ].map((solution, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: isMobile ? '12px' : '16px',
                  background: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  animation: `fadeInUp 0.8s ease-out ${0.2 + index * 0.1}s both`
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateX(10px)';
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.borderColor = '#1a1a1a';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{
                  width: isMobile ? '20px' : '24px',
                  height: isMobile ? '20px' : '24px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1a1a1a, #111111)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '2px'
                }}>
                  <svg width={isMobile ? '10' : '12'} height={isMobile ? '10' : '12'} viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span style={{
                  fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                  fontWeight: TYPOGRAPHY.weights.medium,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  color: '#2d3748',
                  lineHeight: '1.5'
                }}>
                  {solution}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Image */}
        <div style={{
          position: 'relative',
          animation: 'fadeInUp 1s ease-out 0.4s both',
          display: isMobile ? 'none' : 'block'
        }}>
          <div style={{
            position: 'relative',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Glowing Effect */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80%',
              height: '60px',
              background: 'radial-gradient(ellipse, rgba(0, 0, 0, 0.4) 0%, transparent 70%)',
              filter: 'blur(30px)',
              zIndex: 1
            }} />
            
            {/* Product Image - Replace with your actual image */}
            <img
              src="/image.webp"
              alt="Our Solutions"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                position: 'relative',
                zIndex: 2
              }}
            />
            
            {/* Decorative Elements */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'radial-gradient(circle, rgba(0, 0, 0, 0.12) 0%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(40px)',
              pointerEvents: 'none'
            }} />
          </div>

          {/* Floating Badge */}
          {/* <div style={{
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            padding: '20px 30px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
            borderRadius: '16px',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
            animation: 'float 3s ease-in-out infinite'
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.sizes.xxxl,
              fontWeight: TYPOGRAPHY.weights.extrabold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#ffffff',
              lineHeight: '1'
            }}>
              2+
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.sizes.xs,
              fontWeight: TYPOGRAPHY.weights.semibold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: 'rgba(255, 255, 255, 0.9)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginTop: '4px'
            }}>
              Years
            </div>
          </div> */}
        </div>
      </div>
    </div>
  </section>
)}





        {currentView === 'home' && (
          <div ref={categoriesRef} id="categories-section">
            {categories.length > 0 ? (
              <CategoriesCarousel
                categories={categories}
                onSelect={handleCategoryClick}
              />
            ) : (
              <div style={{
                padding: isMobile ? '60px 20px' : '100px 24px',
                textAlign: 'center',
                maxWidth: '1200px',
                margin: '0 auto'
              }}>
                <h2 style={{
                  fontSize: isMobile ? 'clamp(32px, 8vw, 44px)' : 'clamp(36px, 6vw, 56px)',
                  fontWeight: TYPOGRAPHY.weights.bold,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  letterSpacing: TYPOGRAPHY.letterSpacing.tight,
                  lineHeight: TYPOGRAPHY.lineHeights.tight,
                  marginBottom: isMobile ? '12px' : '16px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  No Categories Yet
                </h2>
                <p style={{
                  fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.xl,
                  fontWeight: TYPOGRAPHY.weights.regular,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  lineHeight: TYPOGRAPHY.lineHeights.relaxed,
                  color: 'rgba(255,255,255,0.7)',
                  marginBottom: isMobile ? '20px' : '30px'
                }}>
                  Categories will appear here once added
                </p>
                <div style={{
                  maxWidth: '600px',
                  margin: '0 auto',
                  padding: isMobile ? '20px' : '30px',
                  background: 'rgba(0, 0, 0, 0.08)',
                  borderRadius: '20px',
                  border: '1px solid rgba(0, 0, 0, 0.18)'
                }}>
                  <p style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                    color: 'rgba(0, 0, 0, 0.8)',
                    lineHeight: '1.6',
                    fontFamily: TYPOGRAPHY.fontFamily
                  }}>
                    Use the admin panel to add your first category and products to see them displayed here!
                  </p>
                </div>
              </div>
            )}
          </div>
        )}




{currentView === 'home' && <OurSolutionsSection isMobile={isMobile} />}


{currentView === 'home' && categories.length > 0 && (() => {
  // Get featured products from all categories
  const featuredProducts = categories
    .flatMap(cat => (cat.products || []).map(p => ({ ...p, categoryName: cat.name, category: cat })))
    .filter(p => p.rating >= 4.5)
    .slice(0, 6); // Changed to 6 for alternating layout

  if (featuredProducts.length === 0) return null;

  // Split products into pairs for alternating layout
  const productPairs = [];
  for (let i = 0; i < Math.min(featuredProducts.length, 4); i += 2) {
    productPairs.push([featuredProducts[i], featuredProducts[i + 1]]);
  }

  return (
    <section style={{
      padding: isMobile ? '60px 0 80px 0' : '120px 0 140px 0',
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)'
    }}>
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '-5%',
        width: isMobile ? '300px' : '600px',
        height: isMobile ? '300px' : '600px',
        background: 'radial-gradient(circle, rgba(0, 0, 0, 0.04) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        animation: 'float 15s ease-in-out infinite',
        pointerEvents: 'none'
      }} />

      <div style={{
        position: 'absolute',
        bottom: '20%',
        right: '-5%',
        width: isMobile ? '250px' : '500px',
        height: isMobile ? '250px' : '500px',
        background: 'radial-gradient(circle, rgba(0, 0, 0, 0.04) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        animation: 'float 18s ease-in-out infinite reverse',
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0 40px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: isMobile ? '50px' : '80px',
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          <div style={{
            display: 'inline-block',
            marginBottom: isMobile ? '16px' : '24px'
          }}>
            <div style={{
              padding: '8px 24px',
              background: 'rgba(0, 0, 0, 0.08)',
              borderRadius: '25px',
              border: '1px solid rgba(0, 0, 0, 0.18)'
            }}>
              <span style={{
                fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
                fontWeight: TYPOGRAPHY.weights.bold,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: '#1a1a1a',
                textTransform: 'uppercase',
                letterSpacing: '0.15em'
              }}>
                Featured Products
              </span>
            </div>
          </div>

          <h2 style={{
            fontSize: isMobile ? 'clamp(32px, 9vw, 48px)' : 'clamp(48px, 6vw, 72px)',
            fontWeight: TYPOGRAPHY.weights.extrabold,
            fontFamily: TYPOGRAPHY.fontFamily,
            letterSpacing: '-0.02em',
            lineHeight: '1.1',
            marginBottom: isMobile ? '16px' : '24px',
            background: 'linear-gradient(135deg, #ffffff 0%, #1a1a1a 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Our Products
          </h2>

          <p style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.xl,
            color: 'rgba(0, 0, 0, 0.7)',
            fontFamily: TYPOGRAPHY.fontFamily,
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            Innovative solutions designed to transform your industry
          </p>
        </div>

        {/* Alternating Product Layout */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '60px' : '100px'
        }}>
          {productPairs.map((pair, pairIndex) => (
            <div key={pairIndex}>
              {pair.map((product, productIndex) => {
                if (!product) return null;
                
                const isEven = (pairIndex * 2 + productIndex) % 2 === 0;
                const delay = (pairIndex * 2 + productIndex) * 0.2;

                return (
                  <div
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: isMobile ? '30px' : '60px',
                      alignItems: 'center',
                      marginBottom: productIndex === 0 && pair[1] ? (isMobile ? '60px' : '100px') : '0',
                      cursor: 'pointer',
                      animation: `fadeInUp 0.8s ease-out ${delay}s both`
                    }}
                  >
                    {/* Image Section */}
                    <div
                      style={{
                        order: isMobile ? 1 : (isEven ? 1 : 2),
                        position: 'relative',
                        borderRadius: isMobile ? '20px' : '24px',
                        overflow: 'hidden',
                        height: isMobile ? '280px' : '500px',
                        background: 'rgba(0, 0, 0, 0.2)',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile) {
                          e.currentTarget.style.transform = 'scale(1.02)';
                          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.25)';
                          e.currentTarget.style.boxShadow = '0 30px 80px rgba(0, 0, 0, 0.12)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <img
                        src={product.image}
                        alt={product.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.6s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile) e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      />

                      {/* Category Badge */}
                      <div style={{
                        position: 'absolute',
                        top: isMobile ? '16px' : '24px',
                        left: isMobile ? '16px' : '24px',
                        padding: isMobile ? '6px 14px' : '8px 18px',
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '8px',
                        border: '1px solid rgba(0, 0, 0, 0.18)'
                      }}>
                        <span style={{
                          fontSize: isMobile ? '10px' : TYPOGRAPHY.sizes.xs,
                          fontWeight: TYPOGRAPHY.weights.bold,
                          fontFamily: TYPOGRAPHY.fontFamily,
                          color: '#1a1a1a',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em'
                        }}>
                          {product.categoryName}
                        </span>
                      </div>

                      {/* Rating Badge */}
                      <div style={{
                        position: 'absolute',
                        top: isMobile ? '16px' : '24px',
                        right: isMobile ? '16px' : '24px',
                        padding: isMobile ? '6px 12px' : '8px 16px',
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: '1px solid rgba(0, 0, 0, 0.18)'
                      }}>
                        <Star size={isMobile ? 12 : 14} style={{ color: '#f59e0b', fill: '#f59e0b' }} />
                        <span style={{
                          fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
                          fontWeight: TYPOGRAPHY.weights.bold,
                          color: '#2d3748'
                        }}>
                          {product.rating}
                        </span>
                      </div>

                      {/* Gradient Overlay */}
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.7) 100%)',
                        pointerEvents: 'none'
                      }} />
                    </div>

                    {/* Content Section */}
                    <div
                      style={{
                        order: isMobile ? 2 : (isEven ? 2 : 1),
                        padding: isMobile ? '0' : '20px'
                      }}
                    >
                      <h3 style={{
                        fontSize: isMobile ? 'clamp(24px, 6vw, 32px)' : 'clamp(32px, 4vw, 48px)',
                        fontWeight: TYPOGRAPHY.weights.extrabold,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        color: '#1a1a1a',
                        marginBottom: isMobile ? '12px' : '20px',
                        lineHeight: '1.2',
                        letterSpacing: '-0.01em'
                      }}>
                        {product.name}
                      </h3>

                      <p style={{
                        fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.lg,
                        fontWeight: TYPOGRAPHY.weights.regular,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        lineHeight: '1.7',
                        color: 'rgba(0, 0, 0, 0.8)',
                        marginBottom: isMobile ? '20px' : '30px'
                      }}>
                        {product.fullDescription || product.shortDescription}
                      </p>

                      {/* Features List */}
                      {product.features && product.features.length > 0 && (
                        <div style={{
                          marginBottom: isMobile ? '20px' : '30px'
                        }}>
                          <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: isMobile ? '10px' : '12px'
                          }}>
                            {product.features.slice(0, 3).map((feature, idx) => (
                              <li
                                key={idx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '12px'
                                }}
                              >
                                <div style={{
                                  width: isMobile ? '18px' : '20px',
                                  height: isMobile ? '18px' : '20px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #1a1a1a, #111111)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  marginTop: '2px'
                                }}>
                                  <svg width={isMobile ? '8' : '10'} height={isMobile ? '8' : '10'} viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                                <span style={{
                                  fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.base,
                                  fontWeight: TYPOGRAPHY.weights.regular,
                                  fontFamily: TYPOGRAPHY.fontFamily,
                                  color: 'rgba(0, 0, 0, 0.7)',
                                  lineHeight: '1.6'
                                }}>
                                  {feature}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* CTA Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product);
                        }}
                        style={{
                          padding: isMobile ? '14px 28px' : '16px 36px',
                          background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                          border: 'none',
                          borderRadius: '12px',
                          color: '#ffffff',
                          fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
                          fontWeight: TYPOGRAPHY.weights.bold,
                          fontFamily: TYPOGRAPHY.fontFamily,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '10px',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.18)'
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile) {
                            e.currentTarget.style.transform = 'translateY(-3px)';
                            e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 0, 0, 0.3)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.18)';
                        }}
                      >
                        View Details
                        <ArrowRight size={isMobile ? 18 : 20} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: isMobile ? '60px' : '80px',
          animation: 'fadeInUp 1.2s ease-out 0.8s both'
        }}>
          <button
            onClick={() => {
              setTimeout(() => {
                const categoriesSection = document.getElementById('categories-section');
                if (categoriesSection) {
                  categoriesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            style={{
              padding: isMobile ? '16px 40px' : '20px 50px',
              background: 'rgba(0, 0, 0, 0.08)',
              border: '2px solid rgba(0, 0, 0, 0.25)',
              borderRadius: '14px',
              color: '#2d3748',
              fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
              fontWeight: TYPOGRAPHY.weights.bold,
              fontFamily: TYPOGRAPHY.fontFamily,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)';
                e.currentTarget.style.borderColor = '#111111';
                e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
                e.currentTarget.style.boxShadow = '0 15px 50px rgba(0, 0, 0, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.25)';
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span>Explore All Products</span>
            <ArrowRight size={isMobile ? 18 : 22} />
          </button>
        </div>
      </div>
    </section>
  );
})()}


        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px' }}>
          {(currentView === 'products' || currentView === 'productDetail') && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: isMobile ? '30px' : '40px',
              fontSize: TYPOGRAPHY.sizes.sm,
              fontWeight: TYPOGRAPHY.weights.regular,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#9ca3af',
              background: '#f8f9fa',
              padding: isMobile ? '10px 16px' : '12px 20px',
              borderRadius: '25px',
              backdropFilter: 'blur(10px)',
              width: 'fit-content',
              marginTop: isMobile ? '30px' : '40px'
            }}>
              <button
                onClick={handleBackClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  fontSize: TYPOGRAPHY.sizes.sm,
                  fontWeight: TYPOGRAPHY.weights.medium,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  cursor: 'pointer'
                }}
              >
                <ChevronLeft size={16} style={{ marginRight: '4px' }} />
                Back
              </button>
              <span style={{ margin: '0 12px', color: '#4b5563' }}>•</span>
              <span style={{ color: '#e5e7eb' }}>{selectedCategory?.name}</span>
              {currentView === 'productDetail' && (
                <>
                  <span style={{ margin: '0 12px', color: '#4b5563' }}>•</span>
                  <span style={{ color: '#1a1a1a' }}>{selectedProduct?.name}</span>
                </>
              )}
            </div>
          )}

          {currentView === 'products' && selectedCategory && (
            <ProductsScrollView
              category={selectedCategory}
              onProductClick={handleProductClick}
            />
          )}

          {currentView === 'productDetail' && selectedProduct && (
            <div style={{ marginBottom: isMobile ? '60px' : '80px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : window.innerWidth < 1024 ? '1fr' : '1fr 1fr',
                gap: isMobile ? '40px' : '60px',
                marginBottom: isMobile ? '40px' : '60px'
              }}>
                <div>
                  <div style={{
                    background: '#f8fafc',
                    borderRadius: isMobile ? '16px' : '20px',
                    padding: isMobile ? '30px' : '40px',
                    marginBottom: isMobile ? '15px' : '20px',
                    border: '1px solid rgba(0, 0, 0, 0.12)'
                  }}>
                    <img
                      src={selectedProduct.gallery?.[0] || selectedProduct.image}
                      alt={selectedProduct.name}
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '12px'
                      }}
                    />
                  </div>

                  {selectedProduct.gallery && selectedProduct.gallery.length > 1 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                      gap: isMobile ? '10px' : '15px'
                    }}>
                      {selectedProduct.gallery.slice(1).map((img, index) => (
                        <div
                          key={index}
                          style={{
                            background: '#f8fafc',
                            borderRadius: '12px',
                            padding: isMobile ? '12px' : '15px',
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isMobile) {
                              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.3)';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <img
                            src={img}
                            alt={`${selectedProduct.name} ${index + 2}`}
                            style={{
                              width: '100%',
                              height: 'auto',
                              borderRadius: '8px'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h1 style={{
                    fontSize: isMobile ? 'clamp(28px, 7vw, 36px)' : TYPOGRAPHY.sizes.display,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    letterSpacing: TYPOGRAPHY.letterSpacing.tight,
                    lineHeight: TYPOGRAPHY.lineHeights.tight,
                    marginBottom: isMobile ? '15px' : '20px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    {selectedProduct.name}
                  </h1>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: isMobile ? '20px' : '30px'
                  }}>
                    <div style={{ display: 'flex', gap: 5, marginRight: 10 }}>
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={isMobile ? 16 : 20}
                          style={{
                            color: i < Math.floor(selectedProduct.rating) ? '#f59e0b' : '#d1d5db',
                            fill: i < Math.floor(selectedProduct.rating) ? '#f59e0b' : 'none'
                          }}
                        />
                      ))}
                    </div>
                    <span style={{
                      color: '#1a1a1a',
                      fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
                      fontWeight: TYPOGRAPHY.weights.bold,
                      fontFamily: TYPOGRAPHY.fontFamily
                    }}>
                      {selectedProduct.rating}
                    </span>
                  </div>

                  <p style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
                    fontWeight: TYPOGRAPHY.weights.regular,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    lineHeight: TYPOGRAPHY.lineHeights.loose,
                    color: 'rgba(0, 0, 0, 0.8)',
                    marginBottom: isMobile ? '30px' : '40px'
                  }}>
                    {selectedProduct.fullDescription}
                  </p>

                  <button
                    onClick={() => handleProductRequest(selectedProduct)}
                    style={{
                      width: '100%',
                      padding: isMobile ? '16px' : '20px',
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                      border: 'none',
                      borderRadius: isMobile ? '12px' : '15px',
                      color: '#ffffff',
                      fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
                      fontWeight: TYPOGRAPHY.weights.bold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      marginBottom: isMobile ? '25px' : '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}
                    onMouseEnter={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.25)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <Mail size={isMobile ? 18 : 20} />
                    Enquire now
                  </button>

                  <div style={{
                    background: 'rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: '15px',
                    padding: isMobile ? '20px' : '25px'
                  }}>
                    <h3 style={{
                      fontSize: isMobile ? TYPOGRAPHY.sizes.lg : TYPOGRAPHY.sizes.xl,
                      fontWeight: TYPOGRAPHY.weights.bold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      marginBottom: '15px',
                      color: '#1a1a1a'
                    }}>
                      Key Features
                    </h3>
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0
                    }}>
                      {selectedProduct.features?.map((feature, index) => (
                        <li
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            marginBottom: '12px',
                            color: 'rgba(0, 0, 0, 0.8)',
                            fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                            fontWeight: TYPOGRAPHY.weights.regular,
                            fontFamily: TYPOGRAPHY.fontFamily,
                            lineHeight: TYPOGRAPHY.lineHeights.relaxed
                          }}
                        >
                          <ChevronRight size={isMobile ? 16 : 18} style={{ color: '#1a1a1a', marginRight: '10px', marginTop: '2px', flexShrink: 0 }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>


              {selectedProduct.specifications && (
                <div style={{
                  background: '#f8fafc',
                  backdropFilter: 'blur(20px)',
                  borderRadius: isMobile ? '16px' : '20px',
                  padding: isMobile ? '30px 20px' : '40px',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  marginBottom: isMobile ? '30px' : '40px'
                }}>
                  <h2 style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.xxl : TYPOGRAPHY.sizes.xxxl,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    marginBottom: isMobile ? '25px' : '30px',
                    color: '#2d3748'
                  }}>
                    Technical Specifications
                  </h2>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    gap: isMobile ? '15px' : '20px'
                  }}>
                    {Object.entries(selectedProduct.specifications).map(([key, value], index) => (
                      <div
                        key={index}
                        style={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          padding: isMobile ? '16px' : '20px',
                          borderRadius: '12px',
                          border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                      >
                        <div style={{
                          fontSize: TYPOGRAPHY.sizes.xs,
                          fontWeight: TYPOGRAPHY.weights.semibold,
                          fontFamily: TYPOGRAPHY.fontFamily,
                          color: '#1a1a1a',
                          marginBottom: '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {key}
                        </div>
                        <div style={{
                          fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                          fontWeight: TYPOGRAPHY.weights.regular,
                          fontFamily: TYPOGRAPHY.fontFamily,
                          lineHeight: TYPOGRAPHY.lineHeights.normal,
                          color: 'rgba(0, 0, 0, 0.9)'
                        }}>
                          {value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

{/* Document/PDF Section */}
{selectedProduct.documentUrl && (
  <div style={{
    marginTop: isMobile ? '30px' : '40px',
    padding: isMobile ? '20px' : '30px',
    background: '#f9fafb',
    borderRadius: '16px',
    border: '2px solid rgba(0, 0, 0, 0.18)'
  }}>
    <h3 style={{
      fontSize: isMobile ? TYPOGRAPHY.sizes.lg : TYPOGRAPHY.sizes.xl,
      fontWeight: TYPOGRAPHY.weights.bold,
      color: '#2d3748',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    }}>
      <FileText size={isMobile ? 20 : 24} style={{ color: '#1a1a1a' }} />
      Product Documentation
    </h3>

    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      gap: '20px',
      padding: '20px',
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '12px',
      border: '1px solid rgba(0, 0, 0, 0.12)'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '15px',
        flex: 1
      }}>
        {/* Document Icon */}
        <div style={{
          width: isMobile ? '50px' : '60px',
          height: isMobile ? '50px' : '60px',
          borderRadius: '12px',
          background: selectedProduct.documentType === 'pdf' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 
                      selectedProduct.documentType === 'doc' ? 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)' : 
                      selectedProduct.documentType === 'xlsx' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 
                      'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)'
        }}>
          <span style={{
            color: '#2d3748',
            fontWeight: TYPOGRAPHY.weights.bold,
            fontSize: isMobile ? '11px' : '13px',
            textTransform: 'uppercase'
          }}>
            {selectedProduct.documentType || 'PDF'}
          </span>
        </div>

        {/* Document Info */}
        <div>
          <h4 style={{
            fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
            fontWeight: TYPOGRAPHY.weights.semibold,
            color: '#2d3748',
            marginBottom: '4px'
          }}>
            {selectedProduct.documentTitle || 'Product Datasheet'}
          </h4>
          <p style={{
            fontSize: TYPOGRAPHY.sizes.sm,
            color: 'rgba(0, 0, 0, 0.6)',
            margin: 0
          }}>
            {selectedProduct.documentType === 'pdf' ? 'PDF Document' :
             selectedProduct.documentType === 'doc' ? 'Word Document' :
             selectedProduct.documentType === 'xlsx' ? 'Excel Spreadsheet' : 'External Link'}
          </p>
        </div>
      </div>

      {/* View/Download Button */}
      <a
        href={selectedProduct.documentUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          padding: isMobile ? '14px 24px' : '14px 32px',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)',
          border: 'none',
          borderRadius: '12px',
          color: '#ffffff',
          fontSize: TYPOGRAPHY.sizes.base,
          fontWeight: TYPOGRAPHY.weights.semibold,
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.25)',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.25)';
        }}
      >
        <ExternalLink size={18} />
        View Document
      </a>
    </div>

    <p style={{
      fontSize: TYPOGRAPHY.sizes.xs,
      color: 'rgba(0, 0, 0, 0.5)',
      marginTop: '12px',
      textAlign: 'center'
    }}>
      Click to view or download the product documentation
    </p>
  </div>
)}


              {/* Product Enquiry Section */}
              {currentView === 'productDetail' && selectedProduct && (
                <div style={{
                  background: '#f9fafb',
                  backdropFilter: 'blur(20px)',
                  borderRadius: isMobile ? '20px' : '28px',
                  padding: isMobile ? '30px 20px' : '50px 40px',
                  border: '2px solid rgba(0, 0, 0, 0.15)',
                  marginTop: isMobile ? '40px' : '60px',
                  marginBottom: isMobile ? '40px' : '60px'
                }}>
                  <div style={{
                    textAlign: 'center',
                    marginBottom: isMobile ? '30px' : '40px'
                  }}>
                    <h2 style={{
                      fontSize: isMobile ? TYPOGRAPHY.sizes.xxl : 'clamp(32px, 4vw, 40px)',
                      fontWeight: TYPOGRAPHY.weights.bold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      color: '#2d3748',
                      marginBottom: '12px'
                    }}>
                      Enquire About This Product
                    </h2>
                    <p style={{
                      fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                      color: 'rgba(0, 0, 0, 0.7)',
                      fontFamily: TYPOGRAPHY.fontFamily
                    }}>
                      Have questions about {selectedProduct.name}? We're here to help!
                    </p>
                  </div>

                  {/* Success Message */}
                  {productEnquirySuccess && (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.1) 100%)',
                      border: '2px solid rgba(34, 197, 94, 0.4)',
                      borderRadius: '16px',
                      padding: isMobile ? '16px' : '20px',
                      marginBottom: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      animation: 'fadeInUp 0.5s ease-out'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: 'rgba(34, 197, 94, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <span style={{ fontSize: '20px', color: '#22c55e' }}>✓</span>
                      </div>
                      <div>
                        <h4 style={{
                          fontSize: TYPOGRAPHY.sizes.base,
                          fontWeight: TYPOGRAPHY.weights.semibold,
                          color: '#22c55e',
                          marginBottom: '4px'
                        }}>
                          Enquiry Sent Successfully!
                        </h4>
                        <p style={{
                          fontSize: TYPOGRAPHY.sizes.sm,
                          color: 'rgba(0, 0, 0, 0.8)',
                          margin: 0
                        }}>
                          We'll get back to you within 24 hours with more information.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {productEnquiryErrors.submit && (
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '2px solid rgba(239, 68, 68, 0.4)',
                      borderRadius: '16px',
                      padding: isMobile ? '16px' : '20px',
                      marginBottom: '30px',
                      color: '#ef4444',
                      fontSize: TYPOGRAPHY.sizes.sm,
                      fontFamily: TYPOGRAPHY.fontFamily
                    }}>
                      {productEnquiryErrors.submit}
                    </div>
                  )}

                  <form onSubmit={handleProductEnquirySubmit}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: isMobile ? '20px' : '25px',
                      marginBottom: '25px'
                    }}>
                      {/* Name Field */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: TYPOGRAPHY.sizes.sm,
                          fontWeight: TYPOGRAPHY.weights.semibold,
                          color: '#1a1a1a',
                          marginBottom: '8px'
                        }}>
                          Your Name *
                        </label>
                        <input
                          type="text"
                          value={productEnquiryData.name}
                          onChange={(e) => handleProductEnquiryFieldChange('name', e.target.value)}
                          onBlur={() => handleProductEnquiryFieldBlur('name')}
                          placeholder="John Doe"
                          disabled={submittingProductEnquiry}
                          style={{
                            width: isMobile ? '92%' : '95%',
                            padding: isMobile ? '12px 16px' : '14px 18px',
                            background: 'rgba(0, 0, 0, 0.2)',
                            border: `2px solid ${productEnquiryTouched.name && productEnquiryErrors.name ? '#ef4444' : 'rgba(0, 0, 0, 0.18)'}`,
                            borderRadius: '12px',
                            color: '#2d3748',
                            fontSize: TYPOGRAPHY.sizes.base,
                            fontFamily: TYPOGRAPHY.fontFamily,
                            outline: 'none',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => {
                            if (!productEnquiryErrors.name) {
                              e.target.style.borderColor = '#1a1a1a';
                              e.target.style.background = 'rgba(0, 0, 0, 0.06)';
                            }
                          }}
                        />
                        {productEnquiryTouched.name && productEnquiryErrors.name && (
                          <div style={{
                            fontSize: TYPOGRAPHY.sizes.xs,
                            color: '#ef4444',
                            marginTop: '6px'
                          }}>
                            {productEnquiryErrors.name}
                          </div>
                        )}
                      </div>

                      {/* Email Field */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: TYPOGRAPHY.sizes.sm,
                          fontWeight: TYPOGRAPHY.weights.semibold,
                          color: '#1a1a1a',
                          marginBottom: '8px'
                        }}>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          value={productEnquiryData.email}
                          onChange={(e) => handleProductEnquiryFieldChange('email', e.target.value)}
                          onBlur={() => handleProductEnquiryFieldBlur('email')}
                          placeholder="john@example.com"
                          disabled={submittingProductEnquiry}
                          style={{
                            width: isMobile ? '92%' : '95%',
                            padding: isMobile ? '12px 16px' : '14px 18px',
                            background: 'rgba(0, 0, 0, 0.2)',
                            border: `2px solid ${productEnquiryTouched.email && productEnquiryErrors.email ? '#ef4444' : 'rgba(0, 0, 0, 0.18)'}`,
                            borderRadius: '12px',
                            color: '#2d3748',
                            fontSize: TYPOGRAPHY.sizes.base,
                            fontFamily: TYPOGRAPHY.fontFamily,
                            outline: 'none',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => {
                            if (!productEnquiryErrors.email) {
                              e.target.style.borderColor = '#1a1a1a';
                              e.target.style.background = 'rgba(0, 0, 0, 0.06)';
                            }
                          }}
                        />
                        {productEnquiryTouched.email && productEnquiryErrors.email && (
                          <div style={{
                            fontSize: TYPOGRAPHY.sizes.xs,
                            color: '#ef4444',
                            marginTop: '6px'
                          }}>
                            {productEnquiryErrors.email}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Phone Field */}
                    <div style={{ marginBottom: '25px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.sizes.sm,
                        fontWeight: TYPOGRAPHY.weights.semibold,
                        color: '#1a1a1a',
                        marginBottom: '8px'
                      }}>
                        Phone Number (Optional)
                      </label>
                      <input
                        type="tel"
                        value={productEnquiryData.phone}
                        onChange={(e) => setProductEnquiryData({ ...productEnquiryData, phone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                        disabled={submittingProductEnquiry}
                        style={{
                          width: isMobile ? '92%' : '97%',
                          padding: isMobile ? '12px 16px' : '14px 18px',
                          background: 'rgba(0, 0, 0, 0.2)',
                          border: '2px solid rgba(0, 0, 0, 0.18)',
                          borderRadius: '12px',
                          color: '#2d3748',
                          fontSize: TYPOGRAPHY.sizes.base,
                          fontFamily: TYPOGRAPHY.fontFamily,
                          outline: 'none',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </div>

                    {/* Message Field */}
                    <div style={{ marginBottom: '30px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.sizes.sm,
                        fontWeight: TYPOGRAPHY.weights.semibold,
                        color: '#1a1a1a',
                        marginBottom: '8px'
                      }}>
                        Your Message *
                      </label>
                      <textarea
                        value={productEnquiryData.message}
                        onChange={(e) => handleProductEnquiryFieldChange('message', e.target.value)}
                        onBlur={() => handleProductEnquiryFieldBlur('message')}
                        placeholder="Tell us what you'd like to know about this product..."
                        rows={5}
                        disabled={submittingProductEnquiry}
                        style={{
                          width: isMobile ? '92%' : '97%',
                          padding: isMobile ? '12px 16px' : '14px 18px',
                          background: 'rgba(0, 0, 0, 0.2)',
                          border: `2px solid ${productEnquiryTouched.message && productEnquiryErrors.message ? '#ef4444' : 'rgba(0, 0, 0, 0.18)'}`,
                          borderRadius: '12px',
                          color: '#2d3748',
                          fontSize: TYPOGRAPHY.sizes.base,
                          fontFamily: TYPOGRAPHY.fontFamily,
                          outline: 'none',
                          resize: 'vertical',
                          minHeight: '120px'
                        }}
                      />
                      {productEnquiryTouched.message && productEnquiryErrors.message && (
                        <div style={{
                          fontSize: TYPOGRAPHY.sizes.xs,
                          color: '#ef4444',
                          marginTop: '6px'
                        }}>
                          {productEnquiryErrors.message}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={submittingProductEnquiry}
                      style={{
                        width: '100%',
                        padding: isMobile ? '16px' : '20px',
                        background: submittingProductEnquiry
                          ? 'rgba(0, 0, 0, 0.3)'
                          : 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                        border: 'none',
                        borderRadius: '14px',
                        color: '#ffffff',
                        fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.lg,
                        fontWeight: TYPOGRAPHY.weights.bold,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        cursor: submittingProductEnquiry ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        transition: 'all 0.3s ease',
                        boxShadow: submittingProductEnquiry ? 'none' : '0 10px 30px rgba(0, 0, 0, 0.18)'
                      }}
                      onMouseEnter={(e) => {
                        if (!submittingProductEnquiry && !isMobile) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 0, 0, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.18)';
                      }}
                    >
                      {submittingProductEnquiry ? (
                        <>
                          <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                          Sending Enquiry...
                        </>
                      ) : (
                        <>
                          <Mail size={20} />
                          Send Enquiry
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}

            </div>
          )}
        </div>
      </div>

      {currentView === 'home' && <TestimonialsSection isMobile={isMobile} />}






{/* Enhanced Professional News Section */}
{/* Enhanced Professional News Section */}
{currentView === 'home' && news.length > 0 && (() => {
  // Move hooks to component level by creating a separate component
  const MobileNewsScroll = () => {
    const [activeCardIndex, setActiveCardIndex] = useState(0);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const handleScroll = () => {
        const scrollLeft = container.scrollLeft;
        const cardWidth = 296; // 280px width + 16px gap
        const index = Math.round(scrollLeft / cardWidth);
        setActiveCardIndex(index);
      };

      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    return (
      <div style={{
        position: 'relative',
        paddingBottom: '20px'
      }}>
        <div 
          ref={scrollContainerRef}
          style={{
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            padding: '0 16px 10px 16px',
            scrollPaddingLeft: '16px'
          }}
          className="hide-scrollbar">
          {news.slice(0, 5).map((article, index) => (
            <div
              key={article.id}
              onClick={() => {
                setCurrentView('news');
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
              }}
              style={{
                minWidth: '280px',
                maxWidth: '280px',
                scrollSnapAlign: 'center',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
            >
              {/* Image */}
              {article.image && (
                <div style={{
                  height: '180px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <img
                    src={article.image}
                    alt={article.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.7) 100%)'
                  }} />
                  
                  {/* Badge */}
                  {index === 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      padding: '4px 10px',
                      background: '#1a1a1a',
                      borderRadius: '6px',
                      fontSize: '10px',
                      fontWeight: TYPOGRAPHY.weights.bold,
                      color: '#ffffff',
                      textTransform: 'uppercase'
                    }}>
                      Latest
                    </div>
                  )}
                  
                  {/* Date */}
                  <div style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '12px',
                    fontSize: '11px',
                    color: 'rgba(0, 0, 0, 0.9)',
                    fontWeight: TYPOGRAPHY.weights.medium
                  }}>
                    {new Date(article.date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric'
                    })}
                  </div>
                </div>
              )}
              
              {/* Content */}
              <div style={{
                padding: '16px'
              }}>
                <h3 style={{
                  fontSize: TYPOGRAPHY.sizes.base,
                  fontWeight: TYPOGRAPHY.weights.bold,
                  color: '#2d3748',
                  marginBottom: '8px',
                  lineHeight: '1.3',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  minHeight: '38px'
                }}>
                  {article.title}
                </h3>
                
                <p style={{
                  fontSize: TYPOGRAPHY.sizes.xs,
                  color: 'rgba(0, 0, 0, 0.6)',
                  lineHeight: '1.4',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  marginBottom: '12px'
                }}>
                  {article.excerpt || article.content}
                </p>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#1a1a1a',
                  fontSize: '12px',
                  fontWeight: TYPOGRAPHY.weights.semibold,
                  gap: '4px'
                }}>
                  Read More <ArrowRight size={12} />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Working Scroll Indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          marginTop: '16px'
        }}>
          {news.slice(0, 5).map((_, index) => (
            <div
              key={index}
              style={{
                width: activeCardIndex === index ? '24px' : '6px',
                height: '6px',
                borderRadius: '3px',
                background: activeCardIndex === index 
                  ? '#1a1a1a' 
                  : 'rgba(0, 0, 0, 0.18)',
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <section style={{
      padding: isMobile ? '60px 0' : '120px 0',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle Background */}
      <div style={{
        position: 'absolute',
        top: '20%',
        right: '-5%',
        width: isMobile ? '300px' : '500px',
        height: isMobile ? '300px' : '500px',
        background: 'radial-gradient(circle, rgba(0, 0, 0, 0.04) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '0' : '0 40px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? '30px' : '60px',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '20px' : '0',
          padding: isMobile ? '0 16px' : '0'
        }}>
          <div style={{ width: isMobile ? '100%' : 'auto' }}>
            <div style={{
              fontSize: isMobile ? TYPOGRAPHY.sizes.xs : TYPOGRAPHY.sizes.base,
              fontWeight: TYPOGRAPHY.weights.semibold,
              color: '#1a1a1a',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>
              Latest Updates
            </div>
            <h2 style={{
              fontSize: isMobile ? 'clamp(28px, 8vw, 36px)' : 'clamp(40px, 5vw, 56px)',
              fontWeight: TYPOGRAPHY.weights.bold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#2d3748',
              margin: 0,
              letterSpacing: '-0.02em'
            }}>
              News & Insights
            </h2>
          </div>
          
          <button
            onClick={() => {
              setCurrentView('news');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            style={{
              padding: isMobile ? '10px 24px' : '14px 32px',
              background: 'transparent',
              border: '2px solid rgba(0, 0, 0, 0.25)',
              borderRadius: '8px',
              color: '#2d3748',
              fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
              fontWeight: TYPOGRAPHY.weights.semibold,
              fontFamily: TYPOGRAPHY.fontFamily,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              alignSelf: isMobile ? 'flex-start' : 'auto'
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.borderColor = '#1a1a1a';
                e.currentTarget.style.transform = 'translateX(5px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.25)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            View All <ArrowRight size={16} />
          </button>
        </div>

        {isMobile ? (
          <MobileNewsScroll />
        ) : (
          /* DESKTOP: Grid Layout */
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: '32px'
          }}>
            {/* Featured Article (Latest) */}
            {news[0] && (
              <div
                onClick={() => {
                  setCurrentView('news');
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }}
                style={{
                  gridRow: 'span 2',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  transition: 'all 0.4s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.18)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {news[0].image && (
                  <div style={{
                    height: '400px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img
                      src={news[0].image}
                      alt={news[0].title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.8) 100%)'
                    }} />
                    
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      padding: '6px 14px',
                      background: 'rgba(0, 0, 0, 0.8)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '6px',
                      fontSize: TYPOGRAPHY.sizes.xs,
                      fontWeight: TYPOGRAPHY.weights.bold,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Latest
                    </div>
                  </div>
                )}
                
                <div style={{
                  padding: '32px'
                }}>
                  <div style={{
                    fontSize: TYPOGRAPHY.sizes.xs,
                    color: 'rgba(0, 0, 0, 0.5)',
                    marginBottom: '12px',
                    fontWeight: TYPOGRAPHY.weights.medium,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {new Date(news[0].date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  
                  <h3 style={{
                    fontSize: TYPOGRAPHY.sizes.xxxl,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    color: '#2d3748',
                    marginBottom: '16px',
                    lineHeight: '1.3',
                    letterSpacing: '-0.01em'
                  }}>
                    {news[0].title}
                  </h3>
                  
                  <p style={{
                    fontSize: TYPOGRAPHY.sizes.base,
                    color: 'rgba(0, 0, 0, 0.7)',
                    lineHeight: '1.6',
                    marginBottom: '20px',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {news[0].excerpt || news[0].content}
                  </p>
                  
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: '#1a1a1a',
                    fontSize: TYPOGRAPHY.sizes.sm,
                    fontWeight: TYPOGRAPHY.weights.semibold,
                    gap: '6px',
                    transition: 'gap 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.gap = '10px'}
                  onMouseLeave={(e) => e.currentTarget.style.gap = '6px'}>
                    Read Article <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            )}

            {/* Recent Articles */}
            {news.slice(1, 3).map((article, index) => (
              <div
                key={article.id}
                onClick={() => {
                  setCurrentView('news');
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  transition: 'all 0.4s ease',
                  display: 'flex',
                  flexDirection: 'row',
                  height: '240px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(8px)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.18)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)';
                }}
              >
                {article.image && (
                  <div style={{
                    width: '200px',
                    height: '100%',
                    flexShrink: 0,
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img
                      src={article.image}
                      alt={article.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  </div>
                )}
                
                <div style={{
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  flex: 1
                }}>
                  <div style={{
                    fontSize: TYPOGRAPHY.sizes.xs,
                    color: 'rgba(0, 0, 0, 0.5)',
                    marginBottom: '10px',
                    fontWeight: TYPOGRAPHY.weights.medium,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {new Date(article.date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                  
                  <h4 style={{
                    fontSize: TYPOGRAPHY.sizes.lg,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    color: '#2d3748',
                    marginBottom: '12px',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {article.title}
                  </h4>
                  
                  <p style={{
                    fontSize: TYPOGRAPHY.sizes.sm,
                    color: 'rgba(0, 0, 0, 0.6)',
                    lineHeight: '1.5',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    marginBottom: '12px'
                  }}>
                    {article.excerpt || article.content}
                  </p>
                  
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: '#1a1a1a',
                    fontSize: TYPOGRAPHY.sizes.xs,
                    fontWeight: TYPOGRAPHY.weights.semibold,
                    gap: '4px'
                  }}>
                    Read More <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
})()}





     {/* Why Choose Us Section */}

{currentView === 'home' && <WhyChooseUsSection isMobile={isMobile} />}

      {currentView === 'home' && (
        <section ref={contactRef} style={{
          padding: isMobile ? '80px 0' : '120px 0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Animated background elements */}
          <div style={{
            position: 'absolute',
            top: '20%',
            left: '5%',
            width: isMobile ? '150px' : '250px',
            height: isMobile ? '150px' : '250px',
            background: 'radial-gradient(circle, rgba(0, 0, 0, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(40px)',
            animation: 'float 8s ease-in-out infinite'
          }} />

          <div style={{
            position: 'absolute',
            bottom: '10%',
            right: '10%',
            width: isMobile ? '200px' : '300px',
            height: isMobile ? '200px' : '300px',
            background: 'radial-gradient(circle, rgba(0, 0, 0, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
            animation: 'float 10s ease-in-out infinite reverse'
          }} />

          <div style={{ maxWidth: '900px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px', position: 'relative', zIndex: 10 }}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '50px' : '70px' }}>
              <div style={{
                display: 'inline-block',
                background: 'rgba(0, 0, 0, 0.08)',
                padding: '8px 20px',
                borderRadius: '20px',
                border: '1px solid rgba(0, 0, 0, 0.18)',
                marginBottom: '20px',
                animation: 'fadeInUp 0.6s ease-out'
              }}>
                <span style={{
                  fontSize: TYPOGRAPHY.sizes.sm,
                  fontWeight: TYPOGRAPHY.weights.semibold,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  color: '#1a1a1a',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}>
                  Let's Connect
                </span>
              </div>

              <h2 style={{
                fontSize: isMobile ? 'clamp(32px, 8vw, 48px)' : 'clamp(48px, 6vw, 64px)',
                fontWeight: TYPOGRAPHY.weights.bold,
                fontFamily: TYPOGRAPHY.fontFamily,
                letterSpacing: TYPOGRAPHY.letterSpacing.tight,
                lineHeight: TYPOGRAPHY.lineHeights.tight,
                marginBottom: '20px',
                background: 'linear-gradient(135deg, #1a1a1a 0%, #374151 50%, #6b7280 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'fadeInUp 0.8s ease-out 0.2s both'
              }}>
                Get In Touch
              </h2>

              <p style={{
                fontSize: isMobile ? TYPOGRAPHY.sizes.base : TYPOGRAPHY.sizes.xl,
                fontWeight: TYPOGRAPHY.weights.regular,
                fontFamily: TYPOGRAPHY.fontFamily,
                lineHeight: TYPOGRAPHY.lineHeights.relaxed,
                color: 'rgba(0, 0, 0, 0.8)',
                maxWidth: '600px',
                margin: '0 auto',
                animation: 'fadeInUp 1s ease-out 0.4s both'
              }}>
                We support you at every step, ensuring seamless and successful tests. With our expertise and focus on quality, we handle the complexities so you can deliver exceptional results with confidence.
              </p>
            </div>

            {/* Success Message */}
            {contactSubmitSuccess && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.1) 100%)',
                border: '2px solid rgba(34, 197, 94, 0.4)',
                borderRadius: isMobile ? '20px' : '24px',
                padding: isMobile ? '20px' : '25px',
                marginBottom: isMobile ? '30px' : '40px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                animation: 'fadeInUp 0.5s ease-out',
                boxShadow: '0 10px 30px rgba(34, 197, 94, 0.2)'
              }}>
                <div style={{
                  width: isMobile ? '40px' : '50px',
                  height: isMobile ? '40px' : '50px',
                  background: 'rgba(34, 197, 94, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: isMobile ? '20px' : '24px', color: '#22c55e' }}>✓</span>
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.lg : TYPOGRAPHY.sizes.xl,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    color: '#22c55e',
                    marginBottom: '5px'
                  }}>
                    Message Sent Successfully!
                  </h3>
                  <p style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                    color: 'rgba(0, 0, 0, 0.9)',
                    fontFamily: TYPOGRAPHY.fontFamily,
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    Thank you for reaching out! We've received your message and will get back to you within 24 hours.
                  </p>
                </div>
                <button
                  onClick={() => setContactSubmitSuccess(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#22c55e',
                    cursor: 'pointer',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
            )}

            {/* Error Message (for submit errors) */}
            {contactFormErrors.submit && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.1) 100%)',
                border: '2px solid rgba(239, 68, 68, 0.4)',
                borderRadius: isMobile ? '20px' : '24px',
                padding: isMobile ? '20px' : '25px',
                marginBottom: isMobile ? '30px' : '40px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                animation: 'fadeInUp 0.5s ease-out'
              }}>
                <div style={{
                  width: isMobile ? '40px' : '50px',
                  height: isMobile ? '40px' : '50px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: isMobile ? '20px' : '24px', color: '#ef4444' }}>⚠</span>
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.lg : TYPOGRAPHY.sizes.xl,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    color: '#ef4444',
                    marginBottom: '5px'
                  }}>
                    Error Sending Message
                  </h3>
                  <p style={{
                    fontSize: isMobile ? TYPOGRAPHY.sizes.sm : TYPOGRAPHY.sizes.base,
                    color: 'rgba(0, 0, 0, 0.9)',
                    fontFamily: TYPOGRAPHY.fontFamily,
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    {contactFormErrors.submit}
                  </p>
                </div>
                <button
                  onClick={() => setContactFormErrors(prev => ({ ...prev, submit: undefined }))}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
            )}

            <div style={{
              background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
              backdropFilter: 'blur(30px)',
              borderRadius: isMobile ? '24px' : '32px',
              padding: isMobile ? '35px 25px' : '60px 50px',
              border: '2px solid rgba(0, 0, 0, 0.18)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              animation: 'fadeInUp 1.2s ease-out 0.6s both'
            }}>
              {/* Glowing corner accents */}
              <div style={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(0, 0, 0, 0.18) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(30px)',
                pointerEvents: 'none'
              }} />

              <div style={{
                position: 'absolute',
                bottom: -50,
                left: -50,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(2, 132, 199, 0.3) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(30px)',
                pointerEvents: 'none'
              }} />

              <form onSubmit={handleContactSubmit} style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: isMobile ? '25px' : '30px',
                  marginBottom: isMobile ? '25px' : '30px'
                }}>
                  {/* Name Field */}
                  <div style={{ position: 'relative' }}>
                    <label style={{
                      display: 'block',
                      fontSize: TYPOGRAPHY.sizes.sm,
                      fontWeight: TYPOGRAPHY.weights.semibold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      color: '#1a1a1a',
                      marginBottom: '12px'
                    }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      value={contactFormData.name}
                      onChange={(e) => handleContactFieldChange('name', e.target.value)}
                      onBlur={() => handleContactFieldBlur('name')}
                      disabled={submittingContact}
                      style={{
                        width: isMobile ? '90%' : '93%',
                        padding: isMobile ? '14px 18px' : '16px 20px',
                        background: 'rgba(255, 255, 255, 0.4)',
                        border: `2px solid ${contactFormTouched.name && contactFormErrors.name ? '#ef4444' : 'rgba(0, 0, 0, 0.18)'}`,
                        borderRadius: '14px',
                        color: '#2d3748',
                        fontSize: TYPOGRAPHY.sizes.base,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        if (!contactFormErrors.name) {
                          e.target.style.borderColor = '#1a1a1a';
                          e.target.style.boxShadow = '0 0 0 4px rgba(0, 0, 0, 0.08)';
                        }
                        e.target.style.background = 'rgba(0, 0, 0, 0.06)';
                      }}
                    />
                    {contactFormTouched.name && contactFormErrors.name && (
                      <div style={{
                        fontSize: TYPOGRAPHY.sizes.xs,
                        color: '#ef4444',
                        marginTop: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontFamily: TYPOGRAPHY.fontFamily
                      }}>
                        <span>⚠</span>
                        {contactFormErrors.name}
                      </div>
                    )}
                  </div>

                  {/* Email Field */}
                  <div style={{ position: 'relative' }}>
                    <label style={{
                      display: 'block',
                      fontSize: TYPOGRAPHY.sizes.sm,
                      fontWeight: TYPOGRAPHY.weights.semibold,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      color: '#1a1a1a',
                      marginBottom: '12px'
                    }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      placeholder="john@example.com"
                      value={contactFormData.email}
                      onChange={(e) => handleContactFieldChange('email', e.target.value)}
                      onBlur={() => handleContactFieldBlur('email')}
                      disabled={submittingContact}
                      style={{
                        width: isMobile ? '90%' : '93%',
                        padding: isMobile ? '14px 18px' : '16px 20px',
                        background: 'rgba(255, 255, 255, 0.4)',
                        border: `2px solid ${contactFormTouched.email && contactFormErrors.email ? '#ef4444' : 'rgba(0, 0, 0, 0.18)'}`,
                        borderRadius: '14px',
                        color: '#2d3748',
                        fontSize: TYPOGRAPHY.sizes.base,
                        fontFamily: TYPOGRAPHY.fontFamily,
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        if (!contactFormErrors.email) {
                          e.target.style.borderColor = '#1a1a1a';
                          e.target.style.boxShadow = '0 0 0 4px rgba(0, 0, 0, 0.08)';
                        }
                        e.target.style.background = 'rgba(0, 0, 0, 0.06)';
                      }}
                    />
                    {contactFormTouched.email && contactFormErrors.email && (
                      <div style={{
                        fontSize: TYPOGRAPHY.sizes.xs,
                        color: '#ef4444',
                        marginTop: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontFamily: TYPOGRAPHY.fontFamily
                      }}>
                        <span>⚠</span>
                        {contactFormErrors.email}
                      </div>
                    )}
                  </div>
                </div>

                {/* Message Field */}
                <div style={{ marginBottom: isMobile ? '30px' : '35px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: TYPOGRAPHY.sizes.sm,
                    fontWeight: TYPOGRAPHY.weights.semibold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    color: '#1a1a1a',
                    marginBottom: '12px'
                  }}>
                    <span>Your Message *</span>
                    <span style={{
                      fontSize: TYPOGRAPHY.sizes.xs,
                      color: 'rgba(0, 0, 0, 0.5)',
                      fontWeight: TYPOGRAPHY.weights.regular
                    }}>
                      {contactFormData.message.length}/500
                    </span>
                  </label>
                  <textarea
                    rows={isMobile ? 5 : 6}
                    maxLength={500}
                    placeholder="Tell us what you're looking for..."
                    value={contactFormData.message}
                    onChange={(e) => handleContactFieldChange('message', e.target.value)}
                    onBlur={() => handleContactFieldBlur('message')}
                    disabled={submittingContact}
                    style={{
                      width: isMobile ? '90%' : '96%',
                      padding: isMobile ? '14px 18px' : '16px 20px',
                      background: 'rgba(255, 255, 255, 0.4)',
                      border: `2px solid ${contactFormTouched.message && contactFormErrors.message ? '#ef4444' : 'rgba(0, 0, 0, 0.18)'}`,
                      borderRadius: '14px',
                      color: '#2d3748',
                      fontSize: TYPOGRAPHY.sizes.base,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      outline: 'none',
                      resize: 'vertical',
                      minHeight: '120px',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      if (!contactFormErrors.message) {
                        e.target.style.borderColor = '#1a1a1a';
                        e.target.style.boxShadow = '0 0 0 4px rgba(0, 0, 0, 0.08)';
                      }
                      e.target.style.background = 'rgba(0, 0, 0, 0.06)';
                    }}
                  />
                  {contactFormTouched.message && contactFormErrors.message && (
                    <div style={{
                      fontSize: TYPOGRAPHY.sizes.xs,
                      color: '#ef4444',
                      marginTop: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontFamily: TYPOGRAPHY.fontFamily
                    }}>
                      <span>⚠</span>
                      {contactFormErrors.message}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submittingContact}
                  style={{
                    width: '100%',
                    padding: isMobile ? '16px' : '20px',
                    background: submittingContact
                      ? 'rgba(0, 0, 0, 0.3)'
                      : 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                    border: 'none',
                    borderRadius: '16px',
                    color: '#ffffff',
                    fontSize: isMobile ? TYPOGRAPHY.sizes.lg : TYPOGRAPHY.sizes.xl,
                    fontWeight: TYPOGRAPHY.weights.bold,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    cursor: submittingContact ? 'not-allowed' : 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    boxShadow: submittingContact ? 'none' : '0 10px 30px rgba(0, 0, 0, 0.18)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    if (!submittingContact && !isMobile) {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 0, 0, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.18)';
                  }}
                >
                  {submittingContact ? (
                    <>
                      <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
                      Sending Your Message...
                    </>
                  ) : (
                    <>
                      <Mail size={24} />
                      Send Message
                      <ArrowRight size={20} style={{ transition: 'transform 0.3s ease' }} />
                    </>
                  )}
                </button>
              </form>

              <div style={{
                marginTop: isMobile ? '25px' : '30px',
                padding: isMobile ? '16px' : '20px',
                background: 'rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(0, 0, 0, 0.12)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Shield size={20} style={{ color: '#1a1a1a' }} />
                </div>
                <p style={{
                  fontSize: TYPOGRAPHY.sizes.sm,
                  color: 'rgba(0, 0, 0, 0.7)',
                  fontFamily: TYPOGRAPHY.fontFamily,
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  Your privacy matters. We'll never share your information and will respond within 24 hours.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      {/* Footer */}
   {/* Footer */}
<footer style={{
  background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
  backdropFilter: 'blur(20px)',
  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
  padding: isMobile ? '30px 0 20px' : '80px 0 40px',
  marginTop: '100px'
}}>
  <div style={{
    maxWidth: '1280px',
    margin: '0 auto',
    padding: isMobile ? '0 16px' : '0 24px'
  }}>
    {isMobile ? (
      <>
        {/* Mobile: Compact Layout */}
        <div style={{
          textAlign: 'center',
          marginBottom: '25px'
        }}>
          <img
            src="/oakmond logo.png"
            alt="Dynavation Logo"
            style={{
              height: '60px',
              width: 'auto',
              marginBottom: '10px'
            }}
          />
          <p style={{
            fontSize: '12px',
            fontWeight: TYPOGRAPHY.weights.regular,
            fontFamily: TYPOGRAPHY.fontFamily,
            lineHeight: TYPOGRAPHY.lineHeights.normal,
            color: 'rgba(0, 0, 0, 0.7)',
            marginBottom: '15px'
          }}>
            Pioneering technology solutions
          </p>

          {/* Social Icons - Mobile - Only Instagram and LinkedIn */}
          <div style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'center'
          }}>
            <a
              href="https://www.instagram.com/dynavation/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: '36px',
                height: '36px',
                background: 'rgba(225, 48, 108, 0.1)',
                border: '1px solid rgba(225, 48, 108, 0.2)',
                borderRadius: '8px',
                color: '#e1306c',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease-out',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.background = '#e1306c';
                e.currentTarget.style.color = '#2d3748';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.background = 'rgba(225, 48, 108, 0.1)';
                e.currentTarget.style.color = '#e1306c';
              }}
            >
              <Instagram size={16} />
            </a>
            
            <a
              href="https://in.linkedin.com/company/dynavation-electronics-pvt-ltd"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: '36px',
                height: '36px',
                background: 'rgba(10, 102, 194, 0.1)',
                border: '1px solid rgba(10, 102, 194, 0.2)',
                borderRadius: '8px',
                color: '#0a66c2',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease-out',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.background = '#0a66c2';
                e.currentTarget.style.color = '#2d3748';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.background = 'rgba(10, 102, 194, 0.1)';
                e.currentTarget.style.color = '#0a66c2';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Horizontal Links and Contact Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
          marginBottom: '20px'
        }}>
          {/* Quick Links */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            padding: '15px'
          }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: TYPOGRAPHY.weights.bold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#2d3748',
              marginBottom: '10px'
            }}>
              Quick Links
            </h4>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}>
              {[
                { name: 'About', action: () => { setCurrentView('about'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
                { name: 'Story', action: () => { setCurrentView('our-story'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
                { name: 'Gallery', action: () => { setCurrentView('gallery'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
                { name: 'News', action: () => { setCurrentView('news'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
                { name: 'Contact', action: () => { setCurrentView('home'); setTimeout(() => contactRef.current?.scrollIntoView({ behavior: 'smooth' }), 100); } }
              ].map((link, index) => (
                <li key={index}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      link.action();
                    }}
                    style={{
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: TYPOGRAPHY.weights.regular,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      color: 'rgba(0, 0, 0, 0.7)',
                      textDecoration: 'none',
                      transition: 'color 0.3s ease-out'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#1a1a1a';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = 'rgba(0, 0, 0, 0.7)';
                    }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Get in Touch */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            padding: '15px'
          }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: TYPOGRAPHY.weights.bold,
              fontFamily: TYPOGRAPHY.fontFamily,
              color: '#2d3748',
              marginBottom: '10px'
            }}>
              Get in Touch
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center'
              }}>
                <Mail size={14} style={{ color: '#1a1a1a', marginRight: '6px', flexShrink: 0 }} />
                <span style={{
                  fontSize: '11px',
                  fontWeight: TYPOGRAPHY.weights.regular,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  color: 'rgba(0, 0, 0, 0.7)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  oakmondengieers@gmail.com
                </span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center'
              }}>
                <Phone size={14} style={{ color: '#1a1a1a', marginRight: '6px', flexShrink: 0 }} />
                <span style={{
                  fontSize: '11px',
                  fontWeight: TYPOGRAPHY.weights.regular,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  color: 'rgba(0, 0, 0, 0.7)'
                }}>
                  +91 7348955507
                </span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center'
              }}>
                <MapPin size={14} style={{ color: '#1a1a1a', marginRight: '6px', flexShrink: 0, marginTop: '2px' }} />
                <span style={{
                  fontSize: '11px',
                  fontWeight: TYPOGRAPHY.weights.regular,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  lineHeight: '1.3',
                  color: 'rgba(0, 0, 0, 0.7)'
                }}>
                  India
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Badges - Horizontal */}
        <div style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'center',
          marginTop: '15px'
        }}>
          {[
            { icon: Award, label: 'Award Winning' },
            { icon: Users, label: '1000+ Users' }
          ].map((badge, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                background: 'rgba(0, 0, 0, 0.1)',
                padding: '5px 8px',
                borderRadius: '16px',
                border: '1px solid rgba(0, 0, 0, 0.15)'
              }}
            >
              <badge.icon size={12} style={{ color: '#1a1a1a', marginRight: '4px' }} />
              <span style={{
                fontSize: '10px',
                fontWeight: TYPOGRAPHY.weights.medium,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: 'rgba(0, 0, 0, 0.8)'
              }}>
                {badge.label}
              </span>
            </div>
          ))}
        </div>
      </>
    ) : (
      // Desktop Layout - Keep Original 4-Column Grid
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '50px',
        marginBottom: '60px'
      }}>
        <div>
          <img
            src="/oakmond logo.png"
            alt="Dynavation Logo"
            style={{
              height: '90px',
              width: 'auto',
              marginBottom: '20px'
            }}
          />
          <p style={{
            fontSize: TYPOGRAPHY.sizes.base,
            fontWeight: TYPOGRAPHY.weights.regular,
            fontFamily: TYPOGRAPHY.fontFamily,
            lineHeight: TYPOGRAPHY.lineHeights.relaxed,
            color: 'rgba(0, 0, 0, 0.7)',
            marginBottom: '30px'
          }}>
            Pioneering the future of technology with innovative solutions for modern needs.
          </p>

          {/* Desktop Social Icons - Only Instagram and LinkedIn */}
          <div style={{
            display: 'flex',
            gap: '15px'
          }}>
            <a
              href="https://www.instagram.com/dynavation/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: '45px',
                height: '45px',
                background: 'rgba(225, 48, 108, 0.1)',
                border: '1px solid rgba(225, 48, 108, 0.2)',
                borderRadius: '12px',
                color: '#e1306c',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease-out',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.background = '#e1306c';
                e.currentTarget.style.color = '#2d3748';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.background = 'rgba(225, 48, 108, 0.1)';
                e.currentTarget.style.color = '#e1306c';
              }}
            >
              <Instagram size={20} />
            </a>
            
            <a
              href="https://in.linkedin.com/company/dynavation-electronics-pvt-ltd"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: '45px',
                height: '45px',
                background: 'rgba(10, 102, 194, 0.1)',
                border: '1px solid rgba(10, 102, 194, 0.2)',
                borderRadius: '12px',
                color: '#0a66c2',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease-out',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.background = '#0a66c2';
                e.currentTarget.style.color = '#2d3748';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.background = 'rgba(10, 102, 194, 0.1)';
                e.currentTarget.style.color = '#0a66c2';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>

        <div>
          <h4 style={{
            fontSize: TYPOGRAPHY.sizes.lg,
            fontWeight: TYPOGRAPHY.weights.bold,
            fontFamily: TYPOGRAPHY.fontFamily,
            color: '#2d3748',
            marginBottom: '25px'
          }}>
            Quick Links
          </h4>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0
          }}>
            {[
              { name: 'About Us', action: () => { setCurrentView('about'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
              { name: 'Our Story', action: () => { setCurrentView('our-story'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
              { name: 'Gallery', action: () => { setCurrentView('gallery'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
              { name: 'News', action: () => { setCurrentView('news'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
              { name: 'Contact', action: () => { setCurrentView('home'); setTimeout(() => contactRef.current?.scrollIntoView({ behavior: 'smooth' }), 100); } }
            ].map((link, index) => (
              <li key={index} style={{ marginBottom: '15px' }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    link.action();
                  }}
                  style={{
                    cursor: 'pointer',
                    fontSize: TYPOGRAPHY.sizes.base,
                    fontWeight: TYPOGRAPHY.weights.regular,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    color: 'rgba(0, 0, 0, 0.7)',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease-out'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#1a1a1a';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = 'rgba(0, 0, 0, 0.7)';
                  }}
                >
                  {link.name}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 style={{
            fontSize: TYPOGRAPHY.sizes.lg,
            fontWeight: TYPOGRAPHY.weights.bold,
            fontFamily: TYPOGRAPHY.fontFamily,
            color: '#2d3748',
            marginBottom: '25px'
          }}>
            Get in Touch
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Mail size={18} style={{ color: '#1a1a1a', marginRight: '12px' }} />
              <span style={{
                fontSize: TYPOGRAPHY.sizes.base,
                fontWeight: TYPOGRAPHY.weights.regular,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: 'rgba(0, 0, 0, 0.7)'
              }}>
                oakmondengieers@gmail.com
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Phone size={18} style={{ color: '#1a1a1a', marginRight: '12px' }} />
              <span style={{
                fontSize: TYPOGRAPHY.sizes.base,
                fontWeight: TYPOGRAPHY.weights.regular,
                fontFamily: TYPOGRAPHY.fontFamily,
                color: 'rgba(0, 0, 0, 0.7)'
              }}>
                +91 7348955507
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <MapPin size={18} style={{ color: '#1a1a1a', marginRight: '12px', marginTop: '3px' }} />
              <span style={{
                fontSize: TYPOGRAPHY.sizes.base,
                fontWeight: TYPOGRAPHY.weights.regular,
                fontFamily: TYPOGRAPHY.fontFamily,
                lineHeight: TYPOGRAPHY.lineHeights.normal,
                color: 'rgba(0, 0, 0, 0.7)'
              }}>
                India
              </span>
            </div>
          </div>
        </div>

<div>
  <h4 style={{
    fontSize: TYPOGRAPHY.sizes.lg,
    fontWeight: TYPOGRAPHY.weights.bold,
    fontFamily: TYPOGRAPHY.fontFamily,
    color: '#2d3748',
    marginBottom: '25px'
  }}>
    Certification
  </h4>

  <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
    {[
      { icon: Award, label: 'ISO 9001:2015', desc: 'Quality Management System' },
      { icon: CheckCircle, label: 'CE', desc: 'European Conformity Certified' },
      { icon: ShieldCheck, label: 'DPIIT', desc: 'Govt. of India Recognition' },
      { icon: Briefcase, label: 'MSME', desc: 'Registered Micro Enterprise' }
    ].map((cert, index) => (
      <div
        key={index}
        style={{
          display: 'flex',
          alignItems: 'center',
          background: 'rgba(0, 0, 0, 0.08)',
          padding: '12px 15px',
          borderRadius: '12px',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#1a1a1a';
          e.currentTarget.style.transform = 'translateX(5px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.12)';
          e.currentTarget.style.transform = 'translateX(0)';
        }}
      >
        <cert.icon size={22} style={{ color: '#1a1a1a', marginRight: '12px' }} />
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.sizes.base,
            fontWeight: TYPOGRAPHY.weights.semibold,
            fontFamily: TYPOGRAPHY.fontFamily,
            color: '#2d3748'
          }}>
            {cert.label}
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.sizes.sm,
            fontWeight: TYPOGRAPHY.weights.regular,
            fontFamily: TYPOGRAPHY.fontFamily,
            color: 'rgba(0, 0, 0, 0.6)'
          }}>
            {cert.desc}
          </div>
        </div>
      </div>
    ))}
  </div>
</div>

      </div>
    )}

    {/* Bottom Bar */}
    <div style={{
      borderTop: '1px solid rgba(0, 0, 0, 0.08)',
      paddingTop: isMobile ? '20px' : '30px',
      marginTop: isMobile ? '25px' : '40px',
      textAlign: 'center'
    }}>
      <p style={{
        fontSize: isMobile ? '11px' : TYPOGRAPHY.sizes.sm,
        fontWeight: TYPOGRAPHY.weights.regular,
        fontFamily: TYPOGRAPHY.fontFamily,
        color: 'rgba(0, 0, 0, 0.5)',
        margin: 0
      }}>
        © 2024 Dynavation Electronics Pvt. Ltd. All rights reserved. | Privacy Policy | Terms of Service
      </p>
    </div>
  </div>
</footer>

      {showRequestModal && requestProduct && (
        <RequestInfoModal
          key={requestProduct.id} // Add this key
          product={requestProduct}
          onClose={() => {
            setShowRequestModal(false);
            setRequestProduct(null);
          }}
        />
      )}


      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={handleAuthSuccess}
        />
      )}

      {/* CSS Animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
    /* ===== ANIMATIONS ===== */
    
    @keyframes float {
      0%, 100% { 
        transform: translateY(0px) rotate(0deg); 
      }
      33% { 
        transform: translateY(-15px) rotate(1deg); 
      }
      66% { 
        transform: translateY(5px) rotate(-1deg); 
      }
    }
    
@keyframes bounce {
  0%, 100% { 
    transform: translateX(-50%) translateY(0); 
  }
  50% { 
    transform: translateX(-50%) translateY(${isMobile ? -5 : -10}px); 
  }
}
    
    @keyframes scroll {
      0% { 
        opacity: 0; 
        transform: translateY(0); 
      }
      50% { 
        opacity: 1; 
      }
      100% { 
        opacity: 0; 
        transform: translateY(15px); 
      }
    }
    
    @keyframes spin {
      from { 
        transform: rotate(0deg); 
      }
      to { 
        transform: rotate(360deg); 
      }
    }
    
    @keyframes rotate {
      from { 
        transform: rotate(0deg); 
      }
      to { 
        transform: rotate(360deg); 
      }
    }
    
    @keyframes fadeIn {
      from { 
        opacity: 0; 
      }
      to { 
        opacity: 1; 
      }
    }
    
    @keyframes fadeInDown {
  0% {
    opacity: 0;
    transform: rotate(90deg) translateY(-10px);
  }
  50% {
    opacity: 1;
    transform: rotate(90deg) translateY(0);
  }
  100% {
    opacity: 0;
    transform: rotate(90deg) translateY(10px);
  }
}

    @keyframes fadeInUp {
      from { 
        opacity: 0;
        transform: translateY(30px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideUpFade {
      from { 
        opacity: 0;
        transform: translateY(40px) scale(0.95);
      }
      to { 
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    
    @keyframes pulse {
      0%, 100% { 
        opacity: 1;
        transform: scale(1);
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
      }
      50% { 
        opacity: 0.9;
        transform: scale(1.02);
        box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4);
      }
    }

    @keyframes slideDown {
      from { 
        opacity: 0;
        transform: translateY(-10px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes swipeHint {
      0%, 100% {
        transform: translateX(-50%) translateX(0);
      }
      25% {
        transform: translateX(-50%) translateX(-10px);
      }
      75% {
        transform: translateX(-50%) translateX(10px);
      }
    }
    
    @keyframes shake {
      0%, 100% { 
        transform: translateX(0); 
      }
      10%, 30%, 50%, 70%, 90% { 
        transform: translateX(-5px); 
      }
      20%, 40%, 60%, 80% { 
        transform: translateX(5px); 
      }
    }
    /* Hide scrollbar in auth modal */
.auth-modal::-webkit-scrollbar {
  display: none;
}
    @keyframes shimmer {
      0% { 
        left: -100%; 
      }
      100% { 
        left: 200%; 
      }
    }
    
    @keyframes shine {
      0% { 
        left: -100%; 
      }
      100% { 
        left: 200%; 
      }
    }
    
    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scale(0.9);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    @keyframes ripple {
      0% {
        transform: scale(1);
        opacity: 1;
      }
      100% {
        transform: scale(1.5);
        opacity: 0;
      }
    }
    
    @keyframes glow {
      0%, 100% {
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.18);
      }
      50% {
        box-shadow: 0 0 40px rgba(0, 0, 0, 0.4);
      }
    }
    
    @keyframes gradient {
      0% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }
    
    @keyframes wiggle {
      0%, 100% {
        transform: rotate(0deg);
      }
      25% {
        transform: rotate(-3deg);
      }
      75% {
        transform: rotate(3deg);
      }
    }
    
    @keyframes heartbeat {
      0%, 100% {
        transform: scale(1);
      }
      10%, 30% {
        transform: scale(0.95);
      }
      20%, 40% {
        transform: scale(1.05);
      }
    }
    
    @keyframes wave {
      0% {
        transform: rotate(0deg);
      }
      10% {
        transform: rotate(14deg);
      }
      20% {
        transform: rotate(-8deg);
      }
      30% {
        transform: rotate(14deg);
      }
      40% {
        transform: rotate(-4deg);
      }
      50% {
        transform: rotate(10deg);
      }
      60% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(0deg);
      }
    }
    
    @keyframes blink {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
    
    /* ===== SCROLL BEHAVIOR ===== */
    
    html { 
      scroll-behavior: smooth; 
      overflow-x: hidden;
    }
    
    body {
      margin: 0;
      padding: 0;
      overflow-x: hidden;
    }
    
    /* ===== SCROLLBAR STYLING ===== */
    
    ::-webkit-scrollbar { 
      width: 10px;
      height: 10px;
    }
    
    ::-webkit-scrollbar-track { 
      background: rgba(0, 0, 0, 0.3);
      border-radius: 5px;
    }
    
    ::-webkit-scrollbar-thumb { 
      background: linear-gradient(135deg, #4b5563, #374151);
      border-radius: 5px;
      transition: background 0.3s ease;
    }
    
    ::-webkit-scrollbar-thumb:hover { 
      background: linear-gradient(135deg, #374151, #1f2937);
    }
    
    ::-webkit-scrollbar-corner {
      background: rgba(0, 0, 0, 0.3);
    }
    
    /* Firefox scrollbar */
    * {
      scrollbar-width: thin;
      scrollbar-color: #4b5563 rgba(0, 0, 0, 0.1);
    }
    
    /* ===== INPUT & FORM STYLING ===== */
    
    input::placeholder, 
    textarea::placeholder {
      color: rgba(0, 0, 0, 0.4);
      transition: color 0.3s ease;
    }
    
    input:focus::placeholder,
    textarea:focus::placeholder {
      color: rgba(0, 0, 0, 0.4);
    }
    
    input:focus, 
    textarea:focus {
      border-color: rgba(0, 0, 0, 0.4) !important;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08) !important;
      outline: none;
    }
    
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px rgba(15, 15, 15, 0.98) inset !important;
      -webkit-text-fill-color: white !important;
      transition: background-color 5000s ease-in-out 0s;
    }
    
    /* ===== BUTTON STYLING ===== */
    
    button {
      -webkit-tap-highlight-color: transparent;
      -webkit-appearance: none;
      appearance: none;
      user-select: none;
      touch-action: manipulation;
    }
    
    button:active {
      transform: scale(0.98);
    }
    
    button:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
    
    button:not(:disabled):hover {
      transform: translateY(-2px);
    }
    
    button:not(:disabled):active {
      transform: translateY(0) scale(0.98);
    }
    
    /* ===== LINK STYLING ===== */
    
    a {
      -webkit-tap-highlight-color: transparent;
      text-decoration: none;
      transition: all 0.3s ease;
    }
    
    a:hover {
      text-decoration: none;
    }
    
    /* ===== SELECTION STYLING ===== */
    
    ::selection {
      background: rgba(0, 0, 0, 0.18);
      color: white;
    }
    
    ::-moz-selection {
      background: rgba(0, 0, 0, 0.18);
      color: white;
    }
    
    /* ===== TEXT RENDERING ===== */
    
    * {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }
    
    /* ===== IMAGE OPTIMIZATION ===== */
    
    img {
      max-width: 100%;
      height: auto;
      display: block;
      image-rendering: -webkit-optimize-contrast;
      image-rendering: crisp-edges;
    }
    
    /* ===== FOCUS VISIBLE ===== */
    
    *:focus-visible {
      outline: 2px solid #1a1a1a;
      outline-offset: 2px;
    }
    
    /* ===== LOADING STATES ===== */
    
    .loading {
      pointer-events: none;
      opacity: 0.7;
    }
    
    /* ===== GLASSMORPHISM EFFECTS ===== */
    
    .glass {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .glass-dark {
      background: rgba(0, 0, 0, 0.3);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    /* ===== GRADIENT TEXT ===== */
    
    .gradient-text {
      background: linear-gradient(135deg, #1a1a1a 0%, #374151 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    /* ===== CARD HOVER EFFECTS ===== */
    
    .card-hover {
      transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    
    .card-hover:hover {
      transform: translateY(-10px) scale(1.02);
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
    }
    
    /* ===== SHIMMER EFFECT ===== */
    
    .shimmer {
      position: relative;
      overflow: hidden;
    }
    
    .shimmer::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      animation: shimmer 2s infinite;
    }
    
    /* ===== MOBILE OPTIMIZATIONS ===== */
    
    @media (max-width: 768px) {
      * {
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
      }
      
      input, 
      textarea,
      [contenteditable] {
        -webkit-user-select: text;
        user-select: text;
        -webkit-touch-callout: default;
      }
      
      button,
      a {
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.08);
      }
      
      /* Disable hover effects on mobile */
      .no-mobile-hover:hover {
        transform: none !important;
      }
      
      /* Optimize scrolling performance */
      * {
        -webkit-overflow-scrolling: touch;
      }
    }
    
    /* ===== TABLET OPTIMIZATIONS ===== */
    
    @media (min-width: 769px) and (max-width: 1024px) {
      ::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
    }
    
    /* ===== DESKTOP OPTIMIZATIONS ===== */
    
    @media (min-width: 1025px) {
      /* Custom hover effects for desktop */
      .desktop-hover:hover {
        transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
    }
    
    /* ===== REDUCED MOTION ===== */
    
    @media (prefers-reduced-motion: reduce) {
      *,
      *::before,
      *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
      
      html {
        scroll-behavior: auto;
      }
    }
    
    /* ===== DARK MODE SUPPORT ===== */
    
    @media (prefers-color-scheme: dark) {
      /* Already dark by default */
    }
    
    /* ===== HIGH CONTRAST MODE ===== */
    
    @media (prefers-contrast: high) {
      * {
        border-width: 2px !important;
      }
      
      button,
      a {
        text-decoration: underline;
      }
    }
    
    /* ===== PRINT STYLES ===== */
    
    @media print {
      * {
        background: white !important;
        color: black !important;
        box-shadow: none !important;
        text-shadow: none !important;
      }
      
      a,
      a:visited {
        text-decoration: underline;
      }
      
      img {
        max-width: 100% !important;
        page-break-inside: avoid;
      }
      
      h1, h2, h3, h4, h5, h6 {
        page-break-after: avoid;
      }
    }
    
    /* ===== UTILITY CLASSES ===== */
    
    .no-select {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
    
    .hide-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
    
    .truncate {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .line-clamp-2 {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    
    .line-clamp-3 {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    
    /* ===== ACCESSIBILITY ===== */
    
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
    }
    
    .skip-link {
      position: absolute;
      top: -40px;
      left: 0;
      background: #1a1a1a;
      color: white;
      padding: 8px;
      text-decoration: none;
      z-index: 9999;
    }
    
    .skip-link:focus {
      top: 0;
    }
    
    /* ===== MODAL BACKDROP ===== */
    
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 1000;
    }
    
    /* ===== LOADER ANIMATIONS ===== */
    
    .dot-pulse {
      position: relative;
      left: -9999px;
      width: 10px;
      height: 10px;
      border-radius: 5px;
      background-color: #1a1a1a;
      color: #1a1a1a;
      box-shadow: 9999px 0 0 -5px #1a1a1a;
      animation: dotPulse 1.5s infinite linear;
      animation-delay: 0.25s;
    }
    
    .dot-pulse::before,
    .dot-pulse::after {
      content: '';
      display: inline-block;
      position: absolute;
      top: 0;
      width: 10px;
      height: 10px;
      border-radius: 5px;
      background-color: #1a1a1a;
      color: #1a1a1a;
    }
    
    .dot-pulse::before {
      box-shadow: 9984px 0 0 -5px #1a1a1a;
      animation: dotPulseBefore 1.5s infinite linear;
      animation-delay: 0s;
    }
    
    .dot-pulse::after {
      box-shadow: 10014px 0 0 -5px #1a1a1a;
      animation: dotPulseAfter 1.5s infinite linear;
      animation-delay: 0.5s;
    }
    
    @keyframes dotPulseBefore {
      0% {
        box-shadow: 9984px 0 0 -5px #1a1a1a;
      }
      30% {
        box-shadow: 9984px 0 0 2px #1a1a1a;
      }
      60%,
      100% {
        box-shadow: 9984px 0 0 -5px #1a1a1a;
      }
    }
    
    @keyframes dotPulse {
      0% {
        box-shadow: 9999px 0 0 -5px #1a1a1a;
      }
      30% {
        box-shadow: 9999px 0 0 2px #1a1a1a;
      }
      60%,
      100% {
        box-shadow: 9999px 0 0 -5px #1a1a1a;
      }
    }
    
    @keyframes dotPulseAfter {
      0% {
        box-shadow: 10014px 0 0 -5px #1a1a1a;
      }
      30% {
        box-shadow: 10014px 0 0 2px #1a1a1a;
      }
      60%,
      100% {
        box-shadow: 10014px 0 0 -5px #1a1a1a;
      }
    }
    
    /* ===== PERFORMANCE OPTIMIZATIONS ===== */
    
    .will-change-transform {
      will-change: transform;
    }
    
    .will-change-opacity {
      will-change: opacity;
    }
    
    .gpu-accelerated {
      transform: translateZ(0);
      -webkit-transform: translateZ(0);
    }
    
    /* ===== SAFARI SPECIFIC FIXES ===== */
    
    @supports (-webkit-appearance: none) {
      input[type="search"] {
        -webkit-appearance: textfield;
      }
      
      input[type="search"]::-webkit-search-cancel-button,
      input[type="search"]::-webkit-search-decoration {
        -webkit-appearance: none;
      }
    }
    
    /* ===== FIREFOX SPECIFIC FIXES ===== */
    
    @-moz-document url-prefix() {
      input[type="number"] {
        -moz-appearance: textfield;
      }
    }
    
    /* ===== EDGE SPECIFIC FIXES ===== */
    
    @supports (-ms-ime-align: auto) {
      input {
        border: 2px solid rgba(0, 0, 0, 0.18);
      }
    }
  `
      }} />
    </div>
  );
};

export default TescaLandingPage;